// :)
/**
 * TypeScript hook scripts for Bun runtime — written to ~/.synkro/hooks/ during `synkro install`.
 *
 * Each export is the full source code of a TypeScript file that runs via `#!/usr/bin/env bun`.
 * All grading, classification, CVE scanning, and persistence lives server-side.
 * Local mode: grade via local-cc channel; rules + embeddings from local PGLite only.
 */

export const SYNKRO_COMMON_TS = `
// Shared Synkro hook utilities — imported by all hook scripts.
import { readFileSync, writeFileSync, appendFileSync, mkdirSync, existsSync, renameSync, openSync, closeSync, readSync, unlinkSync, readdirSync, statSync, createReadStream } from 'node:fs';
import { createInterface } from 'node:readline';
import { join, dirname, basename, extname, resolve as resolvePath } from 'node:path';
import { homedir } from 'node:os';
import { execSync } from 'node:child_process';
import { constants as FS_CONSTANTS } from 'node:fs';

// ─── Config ───

const HOME = homedir();
const CONFIG_PATH = join(HOME, '.synkro', 'config.env');

// Load config.env into process.env
if (existsSync(CONFIG_PATH)) {
  try {
    const lines = readFileSync(CONFIG_PATH, 'utf-8').split('\\n');
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) continue;
      const eqIdx = trimmed.indexOf('=');
      if (eqIdx < 1) continue;
      const key = trimmed.slice(0, eqIdx).trim();
      let val = trimmed.slice(eqIdx + 1).trim();
      // Strip surrounding quotes
      if ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'"))) {
        val = val.slice(1, -1);
      }
      process.env[key] = val;
    }
  } catch {}
}

const ALLOWED_GATEWAY_HOSTS = new Set(['api.synkro.sh', 'localhost', '127.0.0.1']);
function validateGatewayUrl(raw: string): string {
  try {
    const u = new URL(raw);
    if (!ALLOWED_GATEWAY_HOSTS.has(u.hostname)) return 'https://api.synkro.sh';
    return raw.replace(/\\/+$/, '');
  } catch {
    return 'https://api.synkro.sh';
  }
}
export const GATEWAY_URL = validateGatewayUrl(process.env.SYNKRO_GATEWAY_URL || 'https://api.synkro.sh');
export const CREDS_PATH = process.env.SYNKRO_CREDENTIALS_PATH || join(HOME, '.synkro', 'credentials.json');
// The long-lived (1yr) Synkro-signed MCP token, minted + stored at install time.
// In cloud mode the hook authenticates grade/scan/telemetry calls with THIS,
// not the ~5-min WorkOS access token — so a long session never 401s the grader.
export const MCP_JWT_PATH = process.env.SYNKRO_MCP_JWT_PATH || join(HOME, '.synkro', '.mcp-jwt');
const LAST_PROMPT_FILE = join(HOME, '.synkro', '.last-prompt');

// ─── Path Validation ───

export function isPathUnder(filePath: string, cwd: string): boolean {
  if (!filePath || !cwd) return false;
  const resolved = resolvePath(filePath);
  const base = resolvePath(cwd);
  return resolved.startsWith(base + '/') || resolved === base;
}

const SHELL_CODE_FILE_EXT = /\.(ts|tsx|js|jsx|mjs|cjs|py|go|java|rb|php|rs|vue|svelte)$/i;

export interface ShellCodeWrite {
  filePath: string;
  content: string;
}

/** Detect shell commands that write/rewrite source files (closes Edit-tool CWE bypass). */
export function extractShellCodeWrites(command: string, cwd: string): ShellCodeWrite[] {
  if (!command.trim()) return [];
  const writes: ShellCodeWrite[] = [];
  const seen = new Set<string>();

  function add(rawPath: string, content: string) {
    const trimmed = rawPath.trim().replace(/^['"]|['"]$/g, '');
    if (!trimmed) return;
    const resolved = trimmed.startsWith('/') ? resolvePath(trimmed) : resolvePath(cwd || '.', trimmed);
    if (!SHELL_CODE_FILE_EXT.test(resolved)) return;
    const key = resolved;
    if (seen.has(key)) return;
    seen.add(key);
    writes.push({ filePath: resolved, content: content.slice(0, 6000) });
  }

  const heredocBodies: string[] = [];
  const heredocRe = /<<-?\\s*['"]?(\\w+)['"]?\\s*\\n([\\s\\S]*?)\\n\\1\\b/g;
  let hm: RegExpExecArray | null;
  while ((hm = heredocRe.exec(command)) !== null) {
    heredocBodies.push(hm[2]);
  }
  const body = heredocBodies.length > 0 ? heredocBodies.join('\\n\\n') : command;

  for (const m of command.matchAll(/Path\\s*\\(\\s*['"]([^'"]+)['"]\\s*\\)/g)) add(m[1], body);
  for (const m of command.matchAll(/writeFileSync\\s*\\(\\s*['"]([^'"]+)['"]/g)) add(m[1], body);
  for (const m of command.matchAll(/writeFile\\s*\\(\\s*['"]([^'"]+)['"]/g)) add(m[1], body);
  for (const m of command.matchAll(/(?:^|[\\s;|])(?:>>?)\\s*([^\\s;&|]+\\.(?:ts|tsx|js|jsx|mjs|cjs|py|go|java|rb|php|rs|vue|svelte))\\b/gim)) {
    add(m[1], body);
  }
  for (const m of command.matchAll(/\\btee(?:\\s+-a)?\\s+([^\\s;&|]+\\.(?:ts|tsx|js|jsx|mjs|cjs|py|go|java|rb|php|rs|vue|svelte))\\b/gi)) {
    add(m[1], body);
  }

  return writes;
}

// ─── Logging ───

// Hooks must keep stderr quiet for non-error paths. Claude Code's PreToolUse
// hook protocol surfaces any stderr from a non-blocking hook as a
// "PreToolUse:Bash hook error" toast — even though the hook returned a
// non-blocking status. That toast interleaves with the tool-call header in the
// terminal and mangles the rendered output. Route routine progress lines to a
// rolling file under ~/.synkro/ so we keep the diagnostic trail without
// polluting the agent UI. Stderr is reserved for hard failures (caught at the
// top-level catch in each hook).
const HOOK_LOG_PATH = join(HOME, '.synkro', '.hooks.log');
const HOOK_LOG_MAX_BYTES = 2 * 1024 * 1024;

export function log(msg: string): void {
  // \`SYNKRO_HOOK_DEBUG=1\` mirrors to stderr — useful when iterating on hook
  // logic, off by default so normal sessions don't get toasts.
  if (process.env.SYNKRO_HOOK_DEBUG === '1') {
    process.stderr.write('[synkro] ' + msg + '\\n');
  }
  try {
    if (existsSync(HOOK_LOG_PATH)) {
      const sz = statSync(HOOK_LOG_PATH).size;
      if (sz > HOOK_LOG_MAX_BYTES) {
        try { renameSync(HOOK_LOG_PATH, HOOK_LOG_PATH + '.1'); } catch {}
      }
    }
    appendFileSync(HOOK_LOG_PATH, new Date().toISOString() + ' [synkro] ' + msg + '\\n', 'utf-8');
  } catch {}
}

// ─── JWT Management ───

export function loadJwt(): string | null {
  // Prefer the long-lived (1yr) Synkro MCP token in BOTH cloud and local mode.
  // It's org+user scoped and accepted across the hook surface (grade/submit,
  // cwe-rules, hook/*, cli/*), so a single 'synkro login' keeps grading + CWE
  // detection alive for a year with NO refresh. The alternative — the ~5-min
  // WorkOS access_token — rides a rotating refresh chain that dies with the
  // WorkOS session (invalid_grant "session has already ended"), which silently
  // disabled CWE findings while editGuard/cveScan (which don't need this token)
  // kept working. jwtIsExpired sees a year of runway, so we never hit refreshJwt.
  try {
    const mcp = readFileSync(MCP_JWT_PATH, 'utf-8').trim();
    if (mcp) return mcp;
  } catch { /* no mcp token on disk — fall back to the login token below */ }
  try {
    if (!existsSync(CREDS_PATH)) return null;
    const creds = JSON.parse(readFileSync(CREDS_PATH, 'utf-8'));
    return creds.access_token || null;
  } catch {
    return null;
  }
}

function decodeJwtExp(jwt: string): number {
  try {
    const parts = jwt.split('.');
    if (parts.length < 2) return 0;
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (payload.length % 4) payload += '=';
    const decoded = Buffer.from(payload, 'base64').toString('utf-8');
    const obj = JSON.parse(decoded);
    return obj.exp || 0;
  } catch {
    return 0;
  }
}

export async function refreshJwt(jwt: string): Promise<string> {
  const creds = JSON.parse(readFileSync(CREDS_PATH, 'utf-8'));
  const rt = creds.refresh_token;
  if (!rt) return jwt;

  const resp = await fetch(GATEWAY_URL + '/api/auth/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: rt }),
    signal: AbortSignal.timeout(4000),
  });

  if (!resp.ok) {
    log('refresh failed: HTTP ' + resp.status);
    return jwt;
  }

  const data = await resp.json() as any;
  const newAt = data.access_token;
  if (!newAt) return jwt;

  const newRt = data.refresh_token || rt;
  const existing = (() => {
    try { return JSON.parse(readFileSync(CREDS_PATH, 'utf-8')); } catch { return {}; }
  })();
  const updated = { ...existing, access_token: newAt, refresh_token: newRt };
  const tmp = CREDS_PATH + '.synkro.tmp';
  writeFileSync(tmp, JSON.stringify(updated, null, 2));
  renameSync(tmp, CREDS_PATH);
  return newAt;
}

function jwtIsExpired(jwt: string): boolean {
  const exp = decodeJwtExp(jwt);
  return exp - Math.floor(Date.now() / 1000) < 60;
}

function lockIsStale(lockfile: string, maxAgeMs = 8000): boolean {
  try {
    const stat = require('node:fs').statSync(lockfile);
    return Date.now() - stat.mtimeMs > maxAgeMs;
  } catch {
    return false;
  }
}

export async function ensureFreshJwt(jwt: string): Promise<string> {
  if (!jwt) return jwt;
  if (!jwtIsExpired(jwt)) return jwt;

  const lockfile = CREDS_PATH + '.lock';

  // Clean stale lock left by a killed hook
  if (existsSync(lockfile) && lockIsStale(lockfile)) {
    try { unlinkSync(lockfile); } catch {}
  }

  let fd = -1;
  try {
    fd = openSync(lockfile, FS_CONSTANTS.O_WRONLY | FS_CONSTANTS.O_CREAT | FS_CONSTANTS.O_EXCL, 0o644);
  } catch {
    // Another process is refreshing — wait for it
    for (let i = 0; i < 8; i++) {
      await new Promise(r => setTimeout(r, 500));
      if (!existsSync(lockfile)) break;
    }
    // Stale lock after full wait — force-remove
    if (existsSync(lockfile) && lockIsStale(lockfile)) {
      try { unlinkSync(lockfile); } catch {}
    }
    // Re-read — the winner should have written a fresh JWT
    const fresh = loadJwt();
    if (fresh && !jwtIsExpired(fresh)) return fresh;
    // Winner's refresh failed — try to acquire lock ourselves
    try {
      fd = openSync(lockfile, FS_CONSTANTS.O_WRONLY | FS_CONSTANTS.O_CREAT | FS_CONSTANTS.O_EXCL, 0o644);
    } catch {
      return fresh || jwt;
    }
  }

  try {
    // Re-check — another hook may have written fresh creds while we waited for lock
    const freshJwt = loadJwt();
    if (freshJwt && !jwtIsExpired(freshJwt)) return freshJwt;
    return await refreshJwt(jwt);
  } catch {
    return jwt;
  } finally {
    try { closeSync(fd); } catch {}
    try { unlinkSync(lockfile); } catch {}
  }
}

// ─── Repo Detection ───

const REPO_CACHE_PATH = join(homedir(), '.synkro', '.last-repo');

export function readCachedRepo(): string {
  try { return readFileSync(REPO_CACHE_PATH, 'utf-8').trim(); } catch { return ''; }
}

export function writeCachedRepo(repo: string): void {
  if (!repo) return;
  try { writeFileSync(REPO_CACHE_PATH, repo, 'utf-8'); } catch {}
}

function repoFromGitDir(cwd: string): string {
  if (!cwd) return '';
  try {
    const url = execSync('git remote get-url origin 2>/dev/null', { cwd, timeout: 3000, encoding: 'utf-8', stdio: ['pipe', 'pipe', 'pipe'] }).trim();
    if (url) {
      return url
        .replace(/^git@[^:]+:/, '')
        .replace(/^https?:\\/\\/[^/]+\\//, '')
        .replace(/\\.git$/, '');
    }
  } catch {}
  try {
    const root = execSync('git rev-parse --show-toplevel 2>/dev/null', { cwd, timeout: 2000, encoding: 'utf-8', stdio: ['pipe', 'pipe', 'pipe'] }).trim();
    if (root) return root.split('/').pop() || '';
  } catch {}
  return '';
}

// Repo resolution order:
//   1. explicit cwd from the hook payload
//   2. workspace_roots (Cursor sends this; CC does not)
//   3. CC transcript-path slug (~/.claude/projects/-Users-m-foo/...)
//   4. hook process cwd
//   5. absolute paths parsed out of the command (cat /abs/path, edit /abs/file, etc.)
// Steps 1-4 try \`git remote get-url origin\` then \`git rev-parse --show-toplevel\`.
// Step 5 climbs up the parent dirs of each parsed path until one exists, then
// tries the same git lookups. Returns '' if nothing resolves — the server then
// stores 'local' which the UI renders as "—".
export function detectRepo(
  cwd: string,
  transcriptPath?: string,
  command?: string,
  workspaceRoots?: string[],
): string {
  const candidates: string[] = [];
  if (cwd) candidates.push(cwd);
  if (workspaceRoots && Array.isArray(workspaceRoots)) {
    for (const r of workspaceRoots) {
      if (typeof r === 'string' && r) candidates.push(r);
    }
  }
  if (transcriptPath) {
    const ccSlug = transcriptPath.match(/\\/projects\\/(-[^/]+)\\//);
    if (ccSlug) candidates.push('/' + ccSlug[1].slice(1).replace(/-/g, '/'));
  }
  candidates.push(process.cwd());

  const tried = new Set<string>();
  for (const c of candidates) {
    if (tried.has(c)) continue;
    tried.add(c);
    const repo = repoFromGitDir(c);
    if (repo) return repo;
  }

  if (command) {
    const seen = new Set<string>();
    const re = /(?:^|[\\s=:"'(\\[])((?:\\/|~\\/)[A-Za-z0-9_.\\-/]+)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(command)) !== null) {
      let p = m[1];
      if (p.startsWith('~/')) p = HOME + p.slice(1);
      if (seen.has(p)) continue;
      seen.add(p);
      let dir = p;
      for (let i = 0; i < 8; i++) {
        try {
          if (existsSync(dir) && statSync(dir).isDirectory()) break;
        } catch {}
        const idx = dir.lastIndexOf('/');
        if (idx <= 0) { dir = ''; break; }
        dir = dir.slice(0, idx);
      }
      if (!dir || tried.has(dir)) continue;
      tried.add(dir);
      const repo = repoFromGitDir(dir);
      if (repo) return repo;
    }
  }
  return '';
}

// ─── Git Root ───

export function findGitRoot(cwd: string): string {
  if (!cwd) return '';
  try {
    return execSync('git rev-parse --show-toplevel 2>/dev/null', { cwd, timeout: 2000, encoding: 'utf-8', stdio: ['pipe', 'pipe', 'pipe'] }).trim();
  } catch { return ''; }
}

// ─── synkro.toml file ───

export interface SynkroFileConfig {
  version: number;
  harness: ('claude-code' | 'cursor')[];
  grader: { pool: 'auto' | 'claude' | 'cursor'; mode?: string; combinedEditGrade?: boolean };
  workers: { claude?: number; cursor?: number };
  ruleset: string;
  skills: string[];
  scanning: { cwe: boolean; cve: boolean };
}

const SYNKRO_FILE_DEFAULTS: SynkroFileConfig = {
  version: 1,
  harness: ['claude-code', 'cursor'],
  grader: { pool: 'auto' },
  workers: {},
  ruleset: 'default',
  skills: [],
  scanning: { cwe: true, cve: true },
};

// Coerce a single TOML value token to JS.
function parseTomlValue(raw: string): any {
  const v = raw.trim();
  if (v.startsWith('[') && v.endsWith(']')) {
    const inner = v.slice(1, -1).trim();
    if (!inner) return [];
    return inner.split(',').map((s: string) => parseTomlValue(s)).filter((s: any) => s !== '');
  }
  if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) return v.slice(1, -1);
  if (v === 'true') return true;
  if (v === 'false') return false;
  if (/^-?\\d+$/.test(v)) return parseInt(v, 10);
  return v;
}

// Minimal TOML reader for synkro.toml's fixed schema (top-level scalars/arrays
// + [grader]/[scanning] tables). Dependency-free — this runs inside the
// materialized _synkro-common.ts which has no node_modules. Returns the same
// nested-object shape the old YAML parser did.
function parseSynkroToml(raw: string): Record<string, any> {
  const result: Record<string, any> = {};
  let section: string | null = null;
  for (const rawLine of raw.split('\\n')) {
    const line = rawLine.trim();
    if (!line || line.startsWith('#')) continue;
    const sec = line.match(/^\\[([^\\]]+)\\]$/);
    if (sec) {
      section = sec[1].trim();
      if (!result[section]) result[section] = {};
      continue;
    }
    const eq = line.indexOf('=');
    if (eq === -1) continue;
    const key = line.slice(0, eq).trim().replace(/^["']|["']$/g, '');
    let valRaw = line.slice(eq + 1).trim();
    if (!valRaw.startsWith('"') && !valRaw.startsWith("'") && !valRaw.startsWith('[')) {
      const h = valRaw.indexOf('#');
      if (h !== -1) valRaw = valRaw.slice(0, h).trim();
    }
    const value = parseTomlValue(valRaw);
    if (section) (result[section] as Record<string, any>)[key] = value;
    else result[key] = value;
  }
  return result;
}

let _synkroFileCache: SynkroFileConfig | undefined;

/** Map any provider alias to the internal pool token. */
function normalizePoolToken(p: unknown): 'auto' | 'claude' | 'cursor' {
  if (p === 'cursor') return 'cursor';
  if (p === 'claude' || p === 'claude-code' || p === 'cc' || p === 'claude_code') return 'claude';
  return 'auto';
}

/**
 * Pull an explicit worker count for a provider, accepting both the unified
 * grader-block form (grader: claude-code: N) and the legacy workers block
 * (workers: claude: N). Provider aliases (claude / claude-code / cc) all match.
 */
function pickWorkerCount(parsed: any, kind: 'claude_code' | 'cursor'): number | undefined {
  const keys = kind === 'cursor' ? ['cursor'] : ['claude-code', 'claude', 'cc', 'claude_code'];
  for (const src of [parsed?.grader, parsed?.workers]) {
    if (!src || typeof src !== 'object') continue;
    for (const k of keys) {
      if (typeof src[k] === 'number') return Math.max(0, Math.floor(src[k]));
    }
  }
  return undefined;
}

export function loadSynkroFile(cwd?: string): SynkroFileConfig {
  if (_synkroFileCache) return _synkroFileCache;
  const root = cwd ? findGitRoot(cwd) : '';
  if (!root) { _synkroFileCache = SYNKRO_FILE_DEFAULTS; return _synkroFileCache; }
  const fp = root + '/synkro.toml';
  try {
    if (!existsSync(fp)) { _synkroFileCache = SYNKRO_FILE_DEFAULTS; return _synkroFileCache; }
    const raw = readFileSync(fp, 'utf-8');
    const parsed = parseSynkroToml(raw);
    const validHarness = ['claude-code', 'cursor'] as const;
    const harness = Array.isArray(parsed.harness)
      ? parsed.harness.filter((h: string) => validHarness.includes(h as any))
      : ['claude-code', 'cursor'];
    _synkroFileCache = {
      version: parsed.version || 1,
      harness: harness.length > 0 ? harness : ['claude-code', 'cursor'],
      grader: {
        pool: normalizePoolToken(parsed.grader?.pool),
        mode: ['local', 'byok'].includes(parsed.grader?.mode) ? parsed.grader.mode : undefined,
        combinedEditGrade: parsed.grader?.combined_edit_grade === true,
      },
      workers: {
        // Counts may live under the grader block (unified form) or the legacy
        // workers block, keyed by claude / claude-code / cc / cursor.
        ...(pickWorkerCount(parsed, 'claude_code') != null ? { claude: pickWorkerCount(parsed, 'claude_code') } : {}),
        ...(pickWorkerCount(parsed, 'cursor') != null ? { cursor: pickWorkerCount(parsed, 'cursor') } : {}),
      },
      ruleset: typeof parsed.ruleset === 'string' ? parsed.ruleset : 'default',
      skills: Array.isArray(parsed.skills) ? parsed.skills.filter((s: unknown) => typeof s === 'string') : [],
      scanning: {
        cwe: parsed.scanning?.cwe !== false,
        cve: parsed.scanning?.cve !== false,
      },
    };
    return _synkroFileCache;
  } catch {
    _synkroFileCache = SYNKRO_FILE_DEFAULTS;
    return _synkroFileCache;
  }
}

export function effectiveGraderPool(synkroFile: SynkroFileConfig, hookAgentKind: AgentKind, config?: HookConfig): AgentKind {
  // Source of truth for the pool SPLIT depends on grading location:
  //   • cloud / byok → SERVER-managed (the user's dashboard choice, delivered as
  //     config.graderPool from /v1/hook/config). synkro.toml isn't even written
  //     for cloud installs, so it can't be the source there.
  //   • local        → synkro.toml [grader] pool (on-device, user-owned).
  // Falls back to synkro.toml whenever the server pool is absent, so a missing
  // value never breaks grading — it just reverts to today's behavior.
  const managed = process.env.SYNKRO_DEPLOY_LOCATION === 'cloud'
    || (process.env.SYNKRO_GRADING_MODE || config?.gradingMode || synkroFile.grader?.mode) === 'byok';
  const pool = (managed && config?.graderPool)
    ? normalizePoolToken(config.graderPool)
    : normalizePoolToken(synkroFile.grader.pool);
  if (pool === 'auto') return hookAgentKind;
  if (pool === 'claude') return 'claude_code';
  return 'cursor';
}

// ─── synkro.toml presence (per-repo onboarding) ───
// A repo is "onboarded" to Synkro when it has a synkro.toml file at its git
// root. The hooks are installed globally, so they fire in every repo —
// including ones the user never set up. Without a synkro.toml there, grading is
// SKIPPED gracefully rather than attempting a grade that surfaces a confusing
// "grader unavailable" error: the tool call passes through and we show a
// one-time hint pointing at "synkro install".
export function synkroFilePresent(cwd?: string): boolean {
  try {
    const root = cwd ? findGitRoot(cwd) : '';
    if (!root) return false;
    // ~/.synkro is Synkro's CONFIG DIRECTORY, not a repo marker. If the git root
    // resolves to HOME (dotfiles tracked in git, or working in a non-git folder
    // under ~), never treat it as onboarded — otherwise grading fires in every
    // directory under home.
    if (root === HOME) return false;
    // A repo is onboarded ONLY via a regular synkro.toml FILE at its root.
    // statSync + isFile guards against a directory of that name being mistaken
    // for config.
    return statSync(root + '/synkro.toml').isFile();
  } catch { return false; }
}

const NO_SYNKRO_HINT_DIR = join(HOME, '.synkro', '.no-synkro-hint');

// Returns the onboarding hint the FIRST time a session touches a repo with no
// synkro.toml file, then null on subsequent calls so it doesn't repeat on every
// tool call. Caller passes null straight through to outputEmpty().
export function noSynkroSkipMessage(sessionId: string): string | null {
  try {
    mkdirSync(NO_SYNKRO_HINT_DIR, { recursive: true });
    const key = (sessionId || 'nosession').replace(/[^a-zA-Z0-9_-]/g, '_');
    const marker = join(NO_SYNKRO_HINT_DIR, key);
    if (existsSync(marker)) return null;
    writeFileSync(marker, '', { flag: 'w' });
  } catch {
    // best-effort dedup — if the marker can't be written, still show the hint
  }
  return '[synkro] No synkro.toml config in this repo — grading skipped. Run \`synkro install\` here to enable Synkro.';
}

// ─── Channel Health ───

export async function channelUp(port = 18929): Promise<boolean> {
  return new Promise(resolve => {
    const sock = require('node:net').connect(port, '127.0.0.1');
    const done = (ok: boolean) => { try { sock.destroy(); } catch {} resolve(ok); };
    sock.once('connect', () => done(true));
    sock.once('error', () => done(false));
    sock.setTimeout(3000, () => done(false));
  });
}

export async function cweChannelUp(): Promise<boolean> {
  return channelUp(18930);
}

// ─── Rule-mining status (user-facing) ───
// Asks the local server for the current rule-mining status line. The server returns
// a non-empty systemMessage only while a job is active or just completed (once per
// session); empty when idle. Best-effort + tightly timed so it never delays a hook.
export async function fetchMiningStatusMessage(sessionId: string): Promise<string> {
  try {
    const port = process.env.SYNKRO_MCP_PORT || '18931';
    const u = 'http://127.0.0.1:' + port + '/api/local/rule-mining/status?session_id=' + encodeURIComponent(sessionId || '');
    const r = await fetch(u, { signal: AbortSignal.timeout(1500) });
    if (!r.ok) return '';
    const j = await r.json() as { systemMessage?: unknown };
    return typeof j.systemMessage === 'string' ? j.systemMessage : '';
  } catch {
    return '';
  }
}

// ─── Mode Normalization ───

export function normalizeMode(m?: string): 'ask' | 'fix' {
  if (m === 'blocking' || m === 'ask') return 'ask';
  if (m === 'audit' || m === 'fix') return 'fix';
  return 'ask';
}

// ─── Config Loading ───

export interface RuleExample {
  text: string;
  verdict: 'violation' | 'ok';
  note?: string;
}

export interface Rule {
  rule_id: string;
  text: string;
  severity: string;
  category: string;
  mode: string;
  examples?: RuleExample[];
}

export interface HookConfig {
  captureDepth: string;
  tier: string;
  silent: boolean;
  policyName: string;
  rules: Rule[];
  scanExemptions: Array<{ path: string; cwe_id: string }>;
  // User-owned data axes. gradingMode: 'local' (container worker pool) | 'byok'
  // (LLM API with the user's own key). storageMode: 'local' (PGLite) | 'cloud'
  // (Timescale). Sourced from config.env (the installed choice); the server
  // value from /v1/hook/config is only a fallback when the env var is unset.
  gradingMode: string;
  storageMode: string;
  // Server-managed pool SPLIT (auto | claude | cursor) + canonical grading
  // location, from /v1/hook/config. Authoritative ONLY for cloud/byok grading
  // (synkro.toml governs local). Undefined when the gateway didn't report one.
  graderPool?: string;
  inferenceMode?: string;
}

/** True when telemetry + rules must stay on-machine (PGLite). Default: local. */
export function isLocalStorageMode(): boolean {
  return (process.env.SYNKRO_STORAGE_MODE || 'local') === 'local';
}

function mapHookRules(raw: unknown[]): Rule[] {
  return raw.map((r: any) => ({
    rule_id: r.rule_id || '',
    text: r.text || '',
    severity: r.severity || '',
    category: r.category || '',
    mode: normalizeMode(r.mode),
    examples: Array.isArray(r.examples) ? r.examples : undefined,
  }));
}

export async function loadConfig(jwt: string, query?: string): Promise<HookConfig> {
  const config: HookConfig = {
    captureDepth: 'local_only',
    tier: 'standard',
    silent: false,
    policyName: '',
    rules: [],
    scanExemptions: [],
    gradingMode: process.env.SYNKRO_GRADING_MODE || 'local',
    storageMode: process.env.SYNKRO_STORAGE_MODE || 'local',
  };

  // Kick the telemetry spool drainer. Fire-and-forget: it runs concurrently
  // with the grade that follows this call, so it adds no latency to the hook.
  drainSpool().catch(() => {});

  // Local-first: fetch from the local MCP server (PGLite-backed) — zero network egress.
  const mcpPort = process.env.SYNKRO_MCP_PORT || '18931';
  try {
    const resp = await fetch('http://127.0.0.1:' + mcpPort + '/api/local/hook-config', {
      signal: AbortSignal.timeout(1500),
    });
    if (resp.ok) {
      const raw = await resp.json() as any;
      const policy = raw.policy || null;
      if (policy) {
        config.policyName = policy.name || '';
        config.rules = mapHookRules(policy.rules || []);
      }
      config.silent = raw.silent === true;
      if (Array.isArray(raw.scan_exemptions)) {
        config.scanExemptions = raw.scan_exemptions
          .filter((e: any) => e && typeof e.path === 'string')
          .map((e: any) => ({ path: e.path, cwe_id: e.cwe_id || '' }));
      }
      // Trust the local result as authoritative ONLY when it actually carried a
      // policy (or we're in local storage mode, where no cloud fallback exists).
      // A transient empty local read (policy:null) must NOT short-circuit the
      // gateway fallback — otherwise the grade runs ruleless and the tag flaps to
      // "[…:all:…] no org rules to evaluate" on a repo that DOES have rules.
      if (policy || isLocalStorageMode()) return config;
    }
  } catch {}

  if (isLocalStorageMode()) {
    log('hook-config: local PGLite unavailable — skipping cloud rules fallback');
    return config;
  }

  // Cloud storage mode only: bootstrap rules from the gateway.
  try {
    const url = GATEWAY_URL + '/api/v1/hook/config' + (query ? '?' + query : '');
    const resp = await fetch(url, {
      headers: { Authorization: 'Bearer ' + jwt },
      signal: AbortSignal.timeout(4000),
    });
    const data = await resp.json() as any;
    config.captureDepth = data.capture_depth || 'local_only';
    config.tier = data.tier || 'standard';
    config.silent = data.silent_mode === true || data.silent_mode === 'true';
    if (!process.env.SYNKRO_GRADING_MODE && data.grading_mode) config.gradingMode = data.grading_mode;
    if (!process.env.SYNKRO_STORAGE_MODE && data.storage_mode) config.storageMode = data.storage_mode;
    // Server-managed pool split + location (cloud/byok only — see effectiveGraderPool).
    if (data.grader_pool) config.graderPool = data.grader_pool;
    if (data.inference_mode) config.inferenceMode = data.inference_mode;
    config.policyName = data.active_policy_name || '';
    if (Array.isArray(data.scan_exemptions)) {
      config.scanExemptions = data.scan_exemptions
        .filter((e: any) => e && typeof e.path === 'string')
        .map((e: any) => ({ path: e.path, cwe_id: e.cwe_id || '' }));
    }
    if (Array.isArray(data.rules)) config.rules = mapHookRules(data.rules);
  } catch {}
  return config;
}

// ─── Routing ───

export async function route(config: HookConfig, synkroFile?: SynkroFileConfig): Promise<'local' | 'cloud'> {
  const gradingMode = process.env.SYNKRO_GRADING_MODE || config.gradingMode || synkroFile?.grader?.mode || 'local';
  if (gradingMode === 'byok') return 'cloud';
  // Cloud-container deploy ([grader] location = cloud): grade via localGrade(),
  // which routes to the hosted container (containers.synkro.sh) and emits the
  // same rich systemMessage. Return 'local' so the hook takes that branch — NOT
  // the legacy BYOK 'cloud' branch (which a non-BYOK org can't use, so it would
  // silently produce no verdict / no system message). channelUp() is false in
  // cloud (no local container), so without this it would wrongly fall to 'cloud'.
  if (process.env.SYNKRO_DEPLOY_LOCATION === 'cloud') return 'local';
  if (config.captureDepth === 'local_only') return 'local';
  if (await channelUp()) return 'local';
  // Local channel down. Do NOT silently fall back to cloud grading when in local
  // mode — the cloud judge endpoint (/api/v1/hook/judge) writes guard_checks /
  // guard_violations to Timescale, leaking local activity into the cloud
  // dashboard. Only an explicit cloud storage posture (which install couples to
  // grader location = cloud) may route to the cloud judge; otherwise stay local
  // (localGrade emits a grader-unavailable message if the container is truly down).
  return process.env.SYNKRO_STORAGE_MODE === 'cloud' ? 'cloud' : 'local';
}

export async function cweRoute(config: HookConfig, synkroFile?: SynkroFileConfig): Promise<'local' | 'byok' | 'skip'> {
  const gradingMode = process.env.SYNKRO_GRADING_MODE || config.gradingMode || synkroFile?.grader?.mode || 'local';
  if (gradingMode === 'byok') return 'byok';
  // Cloud-container: CWE scanning runs on the hosted container via localGradeCwe()
  // (which honors SYNKRO_DEPLOY_LOCATION). Without this, cweChannelUp() is false in
  // cloud and we'd fall to 'skip' — silently disabling CWE scanning in cloud mode.
  if (process.env.SYNKRO_DEPLOY_LOCATION === 'cloud') return 'local';
  if (await cweChannelUp()) return 'local';
  return 'skip';
}

// ─── Tag Building ───

export function tag(rt: string, config: HookConfig, grader?: string): string {
  if (config.silent) return '[synkro:silent]';
  const rs = config.policyName || 'all';
  const g = grader ? ':' + (grader === 'claude_code' ? 'claude' : grader) : '';
  // route() collapses cloud-container to 'local' for BRANCH selection (the local
  // path emits the rich systemMessage), but the grade actually ran on the hosted
  // container. Surface the real location so a cloud grade reads [synkro:cloud:…],
  // and 'local' is reserved for an actual on-device channel grade.
  const loc = (rt === 'local' && process.env.SYNKRO_DEPLOY_LOCATION === 'cloud') ? 'cloud' : rt;
  return '[synkro:' + loc + ':' + rs + g + ']';
}

// ─── Local Grading (direct channel call) ───

type GradeRole = 'grade-edit' | 'grade-bash' | 'grade-plan' | 'grade-cwe' | 'grade-edit-cwe';

// Which coding agent fired this grade. The dispatcher routes grades to a
// worker pool of the matching kind so a Cursor grade is judged by Cursor and
// a Claude grade by Claude. Defaults to 'claude_code' so existing cc-* hook
// call sites need no change; cursor-* hooks pass 'cursor' explicitly.
export type AgentKind = 'claude_code' | 'cursor';

const ROLE_MAP: Record<string, GradeRole> = {
  edit: 'grade-edit', bash: 'grade-bash', plan: 'grade-plan', cwe: 'grade-cwe',
  'edit-cwe': 'grade-edit-cwe',
};

// Combined edit-time grade (org rules + CWE in one inference). OFF by default;
// flipped on by env var or synkro.toml [grader] combined_edit_grade = true.
// When on: the edit-precheck hook runs ONE grade-edit-cwe call and the
// cwe-precheck hook no-ops (the combined call owns CWE) — halving inferences
// per edit. See GRADER_PRIMER_EDIT_CWE.
export function combinedEditGradeEnabled(synkroFile?: SynkroFileConfig): boolean {
  const env = process.env.SYNKRO_COMBINED_EDIT_GRADE;
  if (env === '1' || env === 'true') return true;
  if (env === '0' || env === 'false') return false;
  return synkroFile?.grader?.combinedEditGrade === true;
}

async function channelGrade(role: GradeRole, prompt: string, _jwt: string, port: number, timeoutMs = 30000, agentKind: AgentKind = 'claude_code'): Promise<string> {
  // Defense-in-depth: redact any literal secret from the grade payload so a raw
  // credential never reaches the model. The grader still sees the SHAPE
  // (<redacted:...>) so its policy reasoning is unaffected.
  const safe = scrubSecrets(prompt);
  const body = JSON.stringify({ role, payload: safe, content: safe, agent_kind: agentKind });

  const resp = await fetch('http://127.0.0.1:' + port + '/submit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    signal: AbortSignal.timeout(timeoutMs),
  });

  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error('channel ' + resp.status + ': ' + text.slice(0, 200));
  }

  const data = await resp.json() as { result?: string; error?: string };
  if (data.error) throw new Error(data.error);
  return String(data.result || '');
}

// BYOK grading — grade via the cloud /v1/grade endpoint, which runs the same
// grader prompt through an LLM API using the org's own provider key. Any
// non-2xx (incl. 422 when no key is configured) throws, so the caller's
// existing catch falls open — never a hard block on a grader error.
async function cloudGrade(surface: string, prompt: string, jwt: string, timeoutMs: number): Promise<string> {
  const resp = await fetch(GATEWAY_URL + '/api/v1/grade', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + jwt },
    body: JSON.stringify({ surface, grader_prompt: prompt }),
    signal: AbortSignal.timeout(timeoutMs),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error('cloud grade ' + resp.status + ': ' + text.slice(0, 200));
  }
  const data = await resp.json() as { verdict?: string };
  return String(data.verdict || '');
}

// Cloud-container grading — the org's grader runs on Cloudflare Containers. Same
// request/response shape as channelGrade (the host-host Worker forwards verbatim
// to the org's dispatcher /submit), but to the hosted ingress with the gateway
// JWT (the Worker derives the org from the token and routes to that org's
// container). Any non-2xx throws so the caller's catch fails open.
const CONTAINERS_URL = process.env.SYNKRO_CONTAINERS_URL || 'https://api.synkro.sh';
// Cloud grades cross the network and can hit a cold/queued worker, so they get a
// longer leash than on-device channel grades. The hook chain is sized around it:
// grade abort (45s) < watchdog (48s) < CC hook budget (50s) — the grade aborts
// FIRST so the caller's catch fails open cleanly before CC ever force-kills us.
const CLOUD_GRADE_TIMEOUT_MS = 45000;
async function hostedGrade(role: GradeRole, prompt: string, jwt: string, timeoutMs = CLOUD_GRADE_TIMEOUT_MS, agentKind: AgentKind = 'claude_code'): Promise<string> {
  // Defense-in-depth: redact any literal secret before the payload crosses the
  // network to the cloud grader — a raw credential never reaches the model.
  const safe = scrubSecrets(prompt);
  const resp = await fetch(CONTAINERS_URL + '/grade/submit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + jwt },
    body: JSON.stringify({ role, payload: safe, content: safe, agent_kind: agentKind }),
    signal: AbortSignal.timeout(timeoutMs),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error('hosted grade ' + resp.status + ': ' + text.slice(0, 200));
  }
  const data = await resp.json() as { result?: string; error?: string };
  if (data.error) throw new Error(data.error);
  return String(data.result || '');
}

// Cloud task-adherence: fire the task-only 'task-drift' surface on the org's
// container and return ONLY the progress systemMessage (informational — never
// blocks an edit). Fail-silent: any hiccup returns '' so task tracking can't
// break grading. The payload IS a ScanEnvelope; the container runs getActiveTask
// (resolved per (org,user,repo) via active_tasks) + taskDrift and returns the
// decision. Secrets are scrubbed before the envelope crosses the network.
// Cloud task-drift returns BOTH channels so the hook can STEER the agent, not just
// narrate to the user: systemMessage → user terminal; additionalContext → the agent;
// decision='deny' → a contradiction the agent must fix (hard block, matching local).
// The container ships formatDecision()'s shape ({ systemMessage, hookSpecificOutput:
// { additionalContext, permissionDecision } }); we surface all three.
export interface TaskDriftResult { systemMessage: string; additionalContext: string; decision: 'allow' | 'deny' }
const TASK_DRIFT_NONE: TaskDriftResult = { systemMessage: '', additionalContext: '', decision: 'allow' };

export async function cloudTaskDrift(envelope: Record<string, unknown>, jwt: string, timeoutMs = CLOUD_GRADE_TIMEOUT_MS): Promise<TaskDriftResult> {
  const sid = typeof envelope.sessionId === 'string' ? envelope.sessionId : undefined;
  const repo = typeof envelope.cwd === 'string' ? envelope.cwd : undefined;
  // What was being graded (the action), captured for the failure record so a dead call
  // still shows WHAT timed out. The fully-assembled grader prompt is captured separately
  // server-side (grader_failures.grade_input); this is the client's view.
  const gradeInput = scrubSecrets(JSON.stringify(envelope.payload ?? envelope)).slice(0, 6000);
  try {
    const body = JSON.stringify({ role: 'task-drift', payload: scrubSecrets(JSON.stringify(envelope)), content: '' });
    const resp = await fetch(CONTAINERS_URL + '/grade/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + jwt },
      body,
      signal: AbortSignal.timeout(timeoutMs),
    });
    if (!resp.ok) {
      // Non-2xx — record it (rule grades log the same way) so the task message
      // silently not firing is observable, not invisible. Fire-and-forget per
      // logGraderUnavailable's contract; still fail-OPEN.
      void logGraderUnavailable('taskGuard', 'task-drift', 'http ' + resp.status, gradeInput, { sessionId: sid, repo }).catch(() => {});
      return TASK_DRIFT_NONE;
    }
    const j = await resp.json() as { systemMessage?: unknown; hookSpecificOutput?: { additionalContext?: unknown; permissionDecision?: unknown } };
    const hso = j?.hookSpecificOutput || {};
    return {
      systemMessage: typeof j?.systemMessage === 'string' ? j.systemMessage : '',
      additionalContext: typeof hso.additionalContext === 'string' ? hso.additionalContext : '',
      decision: hso.permissionDecision === 'deny' ? 'deny' : 'allow',
    };
  } catch (e) {
    // Track the failure — ESP. the 45s timeout, the most likely reason a task message
    // doesn't fire. AbortSignal.timeout throws TimeoutError on expiry; everything else
    // (network, parse) carries its own message. Fire-and-forget (don't await — would
    // add ~3s on the timeout path and blow the watchdog); fail-open: never block.
    const err = e as { name?: string; message?: string };
    const reason = err?.name === 'TimeoutError'
      ? 'task-drift timed out after ' + timeoutMs + 'ms'
      : (err?.message || 'task-drift failed');
    void logGraderUnavailable('taskGuard', 'task-drift', reason, gradeInput, { sessionId: sid, repo }).catch(() => {});
    return TASK_DRIFT_NONE;
  }
}

// Cloud task COMPLETION sweep — the end-of-turn FINAL CHECK (fired from the Stop hook).
// Hits the org container's 'task-complete' surface: verify EVERY requirement against the
// actual written code (file snapshots + transcript), mark met/open, and retire if all
// met. Returns the result systemMessage ("complete ✓ N/N retired" or "N/M — missing: …").
// payload.stop_hook_active MUST be falsey or handleTaskComplete bails (stop-loop guard).
// Fail-silent: any hiccup returns '' so a flaky sweep never blocks the agent stopping.
export async function cloudTaskComplete(envelope: Record<string, unknown>, jwt: string, timeoutMs = CLOUD_GRADE_TIMEOUT_MS): Promise<string> {
  const sid = typeof envelope.sessionId === 'string' ? envelope.sessionId : undefined;
  const repo = typeof envelope.cwd === 'string' ? envelope.cwd : undefined;
  try {
    const body = JSON.stringify({ role: 'task-complete', payload: scrubSecrets(JSON.stringify(envelope)), content: '' });
    const resp = await fetch(CONTAINERS_URL + '/grade/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + jwt },
      body,
      signal: AbortSignal.timeout(timeoutMs),
    });
    if (!resp.ok) {
      void logGraderUnavailable('taskComplete', 'task-complete', 'http ' + resp.status, undefined, { sessionId: sid, repo }).catch(() => {});
      return '';
    }
    const j = await resp.json() as { systemMessage?: unknown };
    return typeof j?.systemMessage === 'string' ? j.systemMessage : '';
  } catch (e) {
    const err = e as { name?: string; message?: string };
    const reason = err?.name === 'TimeoutError'
      ? 'task-complete timed out after ' + timeoutMs + 'ms'
      : (err?.message || 'task-complete failed');
    void logGraderUnavailable('taskComplete', 'task-complete', reason, undefined, { sessionId: sid, repo }).catch(() => {});
    return '';
  }
}

// Option B: when a cloud grade fails (esp. a timeout), the REAL reason lives
// inside the container, not on the wire — the client only ever sees "timed out".
// Pull the org's per-worker claude/cursor sick reasons + log tails from the
// hosted /debug endpoint so the diagnostic records WHY the grade actually died
// (e.g. a claude 401 inside the worker). Best-effort + tightly timed; null if
// the container is itself unreachable.
async function fetchContainerSickReason(jwt: string, timeoutMs = 2500): Promise<string | null> {
  try {
    const r = await fetch(CONTAINERS_URL + '/debug', {
      headers: { Authorization: 'Bearer ' + jwt },
      signal: AbortSignal.timeout(timeoutMs),
    });
    if (!r.ok) return 'debug ' + r.status + ': ' + (await r.text().catch(() => '')).slice(0, 400);
    const data = await r.json().catch(() => null);
    return data ? JSON.stringify(data).slice(0, 8000) : null;
  } catch {
    return null;
  }
}

// Cloud transcript capture — the cloud mirror of the local /api/conversation-sync
// + /api/session-action telemetry. Posts to the API /api/v1/cli/sync-transcripts
// (the org is derived from the login JWT, same one cloud grading uses). Best-effort:
// never throws into the hook path. messages/actions must already be in the route
// shape: message {message_index,type,content,model?,timestamp?,tool_calls?};
// action {step,tool,summary,file,outcome}.
async function syncTranscriptCloud(
  sessionId: string,
  repo: string,
  messages: Array<Record<string, unknown>>,
  actions: Array<Record<string, unknown>>,
): Promise<boolean> {
  if (!sessionId || (messages.length === 0 && actions.length === 0)) return false;
  const jwt = loadJwt();
  if (!jwt) return false;
  try {
    const resp = await fetch(GATEWAY_URL + '/api/v1/cli/sync-transcripts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + jwt },
      body: JSON.stringify({ repo: repo || '', sessions: [{ cc_session_id: sessionId, messages, actions }] }),
      signal: AbortSignal.timeout(5000),
    });
    return resp.ok;
  } catch {
    return false;
  }
}

// Local-channel default 24s — MUST stay below the CC hook timeout (50s for
// edit/bash/cwe in ccHookConfig.ts) so the AbortSignal fires and the caller's
// catch fails open cleanly. A hung on-device worker should fail open fast; only
// cloud grades (network + cold workers) get the longer 45s leash below.
export async function localGrade(surface: string, prompt: string, timeoutMs = 24000, agentKind: AgentKind = 'claude_code'): Promise<string> {
  const jwt = loadJwt();
  if (!jwt) throw new Error('NO_JWT');
  // Cloud-container: the grader runs on Cloudflare Containers — route to the
  // hosted ingress instead of the local channel. Same prompt + parseVerdict.
  // Cloud grades get the full 45s (not the local 24s): the round-trip + a cold
  // worker routinely needs >24s, which is exactly what was timing out and
  // failing open on big bash prompts.
  if (process.env.SYNKRO_DEPLOY_LOCATION === 'cloud') {
    return hostedGrade(ROLE_MAP[surface] || 'grade-edit', prompt, jwt, CLOUD_GRADE_TIMEOUT_MS, agentKind);
  }
  // BYOK grading mode routes the grade through an LLM API instead of the
  // on-device channel worker pool. The grader prompt + parseVerdict are shared.
  if ((process.env.SYNKRO_GRADING_MODE || 'local') === 'byok') {
    return cloudGrade(surface, prompt, jwt, timeoutMs);
  }
  if (!(await channelUp())) throw new Error('SYNKRO_CHANNEL_DOWN');
  return channelGrade(ROLE_MAP[surface] || 'grade-edit', prompt, jwt, 18929, timeoutMs, agentKind);
}

// Local-channel default 24s. Two cwe chunks grade in parallel within the same CC
// budget (now 50s), so each must finish under it. Cloud cwe grades take the 45s
// hosted leash (parallel chunks → wall-clock ~45s, still inside the 50s budget).
export async function localGradeCwe(prompt: string, agentKind: AgentKind = 'claude_code', timeoutMs = 24000): Promise<string> {
  const jwt = loadJwt();
  if (!jwt) throw new Error('NO_JWT');
  if (process.env.SYNKRO_DEPLOY_LOCATION === 'cloud') {
    return hostedGrade('grade-cwe', prompt, jwt, CLOUD_GRADE_TIMEOUT_MS, agentKind);
  }
  return channelGrade('grade-cwe', prompt, jwt, 18930, timeoutMs, agentKind);
}

// ─── Fix-poll directive (ask-mode human-preference capture) ───
// Turns an ask-mode flag into a labeled DPO pair: candidate A is the grader's
// suggested fix; candidate B is a best-effort second grade nudged toward a
// different technique. Registers a pending fix_labels item and returns an agent
// directive to poll the user + call record_fix_choice. PURELY ADDITIVE — any
// failure returns '' so enforcement behaves exactly as before.
export async function buildFixPollDirective(o: { candA: string; code: string; ruleId: string; sessionId: string; filePath: string; model: string; graderPrompt: string; graderPool?: AgentKind }): Promise<string> {
  // Kill switch (container/agent env, NOT user-toggleable via MCP): headless/benchmark
  // runs (e.g. sec-code-bench) set SYNKRO_FIX_POLL=0 to suppress the AskUserQuestion poll
  // so grading runs non-interactively. The container endpoint enforces the same flag.
  if (process.env.SYNKRO_FIX_POLL === '0') return '';
  try {
    const candA = (o.candA || '').trim();
    if (!candA) return '';
    const cands: string[] = [candA];
    try {
      // Candidate B from the SAME grader that produced A (the edit/rule grader),
      // reusing its prompt + rules — nudged toward a different remediation. Using
      // grade-cwe here would mis-grade an org-rule violation ('no rules' / wrong
      // surface), so we re-grade the edit surface. Best-effort: 1 candidate is fine.
      const altPrompt = (o.graderPrompt || '') + '\\n\\n// SECURITY REVIEWER: also propose an ALTERNATIVE remediation that uses a DIFFERENT technique than the most obvious one.';
      const rawB = await localGrade('edit', altPrompt, 12000, o.graderPool);
      const candB = (parseVerdict(rawB).suggestedFix || '').trim();
      if (candB && candB !== candA) cands.push(candB);
    } catch (e) { /* best-effort: one candidate is fine */ }
    // Route by deploy location: cloud -> the gateway (Timescale, org-scoped, auth'd);
    // local -> the on-device server (PGLite). GATEWAY_URL is host-allowlisted; the
    // loopback port is range-validated (CWE-918) so neither is an arbitrary host.
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    let url: string;
    if (process.env.SYNKRO_DEPLOY_LOCATION === 'cloud') {
      const jwt = loadJwt();
      if (!jwt) return '';
      headers['Authorization'] = 'Bearer ' + jwt;
      url = GATEWAY_URL + '/api/v1/cli/fix-poll';
    } else {
      const rawPort = parseInt(process.env.SYNKRO_MCP_PORT || '18931', 10);
      const mcpPort = (rawPort > 0 && rawPort < 65536) ? rawPort : 18931;
      url = 'http://127.0.0.1:' + mcpPort + '/api/local/fix-poll';
    }
    const resp = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        session_id: o.sessionId, file_path: o.filePath, cwe: o.ruleId,
        vulnerable_code: (o.code || '').slice(0, 8000), candidates: cands, generator_model: o.model || '',
      }),
      signal: AbortSignal.timeout(5000),
    });
    if (!resp.ok) return '';
    const j = await resp.json() as { item_id?: string };
    if (!j || !j.item_id) return '';
    let optsStr = '';
    for (let i = 0; i < cands.length; i++) optsStr += '\\n  [' + i + '] ' + cands[i];
    return '\\n\\nSYNKRO FIX POLL (item_id=' + j.item_id + '): Before retrying, present these candidate fixes to the user with the AskUserQuestion tool (one option per candidate, plus a "None of the above" option), then call the record_fix_choice tool with item_id="' + j.item_id + '" and chosen_idx set to the 0-based option the user picked (-1 for none of the above). Candidates:' + optsStr;
  } catch (e) {
    return '';
  }
}

// ─── Rule Pre-Filter (embedding-based) ───

/** User message + action for embedding search — intent surfaces boundary/consent rules the command alone misses. */
export function ruleFilterText(action: string, userMessage?: string | null): string {
  const user = (userMessage || '').trim();
  const act = (action || '').trim();
  if (!user) return act.slice(0, 4000);
  if (!act) return user.slice(0, 4000);
  return (user + '\\n' + act).slice(0, 4000);
}

export async function filterRules(commandText: string, allRules: Rule[]): Promise<Rule[]> {
  if (allRules.length <= 3) return allRules;

  // Cloud: the embedding index lives server-side (the container grade path can't
  // reach 127.0.0.1), so ask the gateway for the top-5 relevant rules — same
  // CF/BYOK bge-base vectors, same behavior as local. Without this, cloud grades
  // dumped EVERY rule into the prompt (9KB+), the dominant cause of grade
  // timeouts. Any failure falls back to all rules (status quo), never blocks.
  if (!isLocalStorageMode()) {
    const jwt = loadJwt();
    if (!jwt) return allRules;
    try {
      const resp = await fetch(GATEWAY_URL + '/api/v1/hook/filter-rules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + jwt },
        body: JSON.stringify({ text: commandText, top_k: 5 }),
        signal: AbortSignal.timeout(2000),
      });
      if (!resp.ok) return allRules;
      const data = await resp.json() as { rules?: Array<Record<string, unknown>> };
      if (!data.rules || data.rules.length === 0) return allRules;
      const selectedIds = new Set(data.rules.map(r => String(r.rule_id || '')));
      return allRules.filter(r => selectedIds.has(r.rule_id));
    } catch {
      return allRules;
    }
  }

  // Local: the on-device PGLite embedding index owns the rule set.
  const mcpPort = process.env.SYNKRO_MCP_PORT || '18931';
  try {
    const resp = await fetch('http://127.0.0.1:' + mcpPort + '/api/local/filter-rules', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: commandText, top_k: 5 }),
      signal: AbortSignal.timeout(500),
    });
    if (!resp.ok) return allRules;
    const data = await resp.json() as { rules?: Array<Record<string, unknown>> };
    if (!data.rules || data.rules.length === 0) return allRules;
    return mapHookRules(data.rules);
  } catch {
    return allRules;
  }
}

// ─── Safe-read short-circuit (shared by cc-bash-judge + cursor-bash-judge) ───
// Read-only tool calls, and bash pipelines where every segment is a pure
// in-repo read, are allowed instantly without an LLM grade. Strict by design:
// any $ / backtick / redirect / shell metachar, any non-whitelisted verb, any
// .. traversal, or any absolute path outside repoRoot falls through to the judge.

const SAFE_READ_TOOLS = new Set([
  'Read', 'ReadFile', 'read_file', 'Grep', 'grep_search', 'codebase_search',
  'file_search', 'Glob', 'list_dir',
]);
const SAFE_SHELL_TOOLS = new Set([
  'Bash', 'Shell', 'terminal', 'run_terminal_cmd', 'execute_command',
]);

function isSafeBashSegment(seg: string, repoRoot: string): boolean {
  const UNSAFE_CHARS = ['>', ';', '&', '\`', '$'];
  for (const ch of UNSAFE_CHARS) { if (seg.indexOf(ch) !== -1) return false; }
  const padded = ' ' + seg + ' ';
  const UNSAFE_WORDS = [
    ' sudo ', ' su ', ' rm ', ' mv ', ' cp ', ' chmod ', ' chown ',
    ' tee ', ' kill ', ' sed -i', ' sed --in-place',
    ' sh -c', ' bash -c', ' zsh -c', ' eval ', ' exec ',
  ];
  for (const w of UNSAFE_WORDS) { if (padded.indexOf(w) !== -1) return false; }
  const SAFE_VERBS = new Set([
    'cat','head','tail','less','more','grep','egrep','fgrep','rg','ag',
    'find','fd','ls','wc','cmp','diff','file','stat','which','whereis','type',
    'pwd','whoami','id','date','echo','printf','true','false',
    'jq','yq','sort','uniq','cut','tr','xxd','hexdump','od','column',
    'node','npm','pnpm','yarn','bun','python','python3','ruby','go','rustc','cargo',
    'git',
    // Read-only system / inspection utilities (no mutation, no exec). Deliberately
    // EXCLUDES curl/wget (exfil + -o write), sed/awk (w/e write+exec), env (command
    // runner), tree (-o write) — those stay graded. Keep in sync with scanning/safeRead.ts.
    'lsof','ps','df','du','uname','hostname','uptime',
    'basename','dirname','realpath','readlink',
    'shasum','md5','md5sum','sha256sum','cksum',
    'docker',
  ]);
  const tokens = seg.trim().split(' ').filter(t => t.length > 0);
  const verb = tokens[0] || '';
  if (!SAFE_VERBS.has(verb)) return false;
  if (verb === 'find' || verb === 'fd') {
    const BAD = new Set([
      '-exec','-execdir','-ok','-okdir','-delete',
      '-fprint','-fprintf','-fprint0','-fls','--exec','--exec-batch',
    ]);
    for (const t of tokens) { if (BAD.has(t)) return false; }
  }
  if (verb === 'git') {
    const SAFE_GIT = new Set([
      'log','show','diff','blame','status','rev-parse',
      'ls-files','ls-tree','cat-file','shortlog','reflog',
      'describe','symbolic-ref','--version',
    ]);
    const sub = tokens[1] || '';
    if (!SAFE_GIT.has(sub)) return false;
  } else if (['npm','pnpm','yarn','bun','cargo','go'].includes(verb)) {
    const sub = tokens[1] || '';
    const SAFE_PKG = new Set([
      '--version','-v','version','list','ls','why','view','show','info','outdated',
      '-h','--help','help',
    ]);
    if (!SAFE_PKG.has(sub)) return false;
  } else if (['node','python','python3','ruby','rustc'].includes(verb)) {
    const sub = tokens[1] || '';
    if (sub !== '--version' && sub !== '-v' && sub !== '-V') return false;
  } else if (verb === 'docker') {
    // Read-only docker subcommands only — same gating pattern as git. Excludes
    // run/exec/rm/rmi/kill/stop/start/build/buildx/push/pull/cp/commit/save/load/
    // compose and every mutating verb; those fall through to the judge.
    const SAFE_DOCKER = new Set([
      'ps','logs','images','inspect','version','info','top','port','history','diff',
    ]);
    const sub = tokens[1] || '';
    if (!SAFE_DOCKER.has(sub)) return false;
  }
  if (!repoRoot) return false;
  for (let i = 1; i < tokens.length; i++) {
    const stripped = tokens[i].replace(/^['"]/, '').replace(/['"]$/, '');
    if (stripped.startsWith('~')) return false;
    // Reject any .. traversal segment — a relative path with .. can escape the
    // repo root just as easily as an absolute one.
    if (stripped.split('/').some(p => p === '..')) return false;
    if (stripped.startsWith('/') && !isPathUnder(stripped, repoRoot)) return false;
  }
  return true;
}

export function isSafeInRepoRead(toolName: string, command: string, repoRoot: string): boolean {
  // Read/Grep/Glob are synthesized into cat/grep/find commands — validate paths
  // the same way as bash reads instead of blanket-allowing every tool call.
  if (SAFE_READ_TOOLS.has(toolName)) {
    if (!command || !repoRoot) return false;
    return isSafeBashSegment(command.trim(), repoRoot);
  }
  if (!SAFE_SHELL_TOOLS.has(toolName)) return false;
  if (!command || !repoRoot) return false;
  const segments = command.split('|');
  for (const seg of segments) {
    const t = seg.trim();
    if (t.length === 0) return false;
    if (!isSafeBashSegment(t, repoRoot)) return false;
  }
  return true;
}

// ─── Install protection: server-side pkg-scan (shared by both bash judges) ───
// Parses an install command, scans the packages for CVEs / typosquats /
// malicious tarballs / low reputation, and returns a structured verdict the
// caller renders in its own (cc or cursor) output format.

export interface InstallScanResult {
  scanned: boolean;
  action: 'allow' | 'warn' | 'block';
  blockContext: string;
  summary: string;
  scannedLabel: string;
  findings: Array<{ advisoryId: string; name: string; version: string; severity: string; detail: string }>;
  violatedIds: string[];
}

export async function runInstallScan(command: string, jwt: string): Promise<InstallScanResult> {
  const empty: InstallScanResult = {
    scanned: false, action: 'allow', blockContext: '', summary: '',
    scannedLabel: '', findings: [], violatedIds: [],
  };
  // Stage 0 — recall-tuned pre-filter. Cheap, dependency-free, runs on every
  // Bash command. A false positive only costs a wasted server round-trip; a
  // false negative would let a vulnerable install through — so keep it loose.
  const lc = command.toLowerCase();
  const HINTS = [
    'npm', 'pnpm', 'yarn', 'bun', 'pip', 'cargo', 'gem', 'composer',
    'apt', 'apk', 'brew', 'dnf', 'yum', 'pacman', 'install', 'require',
    'eval', '$(', '| sh', '| bash', 'curl', 'wget', 'make ',
  ];
  if (!HINTS.some(h => lc.includes(h))) return empty;

  try {
    let clientPackages: Array<{ name: string; version: string; ecosystem: string }> = [];
    try {
      const mod = await import(new URL('./installExtractCore.ts', import.meta.url).href);
      clientPackages = mod.extractDeterministicPkgRequests(command);
    } catch (err) {
      log('installExtract client error: ' + String(err));
    }

    const scanResp = await fetch(GATEWAY_URL + '/api/v1/pkg-scan', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + jwt },
      body: JSON.stringify({ command, packages: clientPackages }),
      signal: AbortSignal.timeout(10000),
    }).then(r => r.json()) as any;
    const action = scanResp?.action || 'allow';
    const pkgResults = Array.isArray(scanResp?.packages) ? scanResp.packages : [];
    const summary = scanResp?.summary || '';
    const scannedLabel = pkgResults.map((p: any) => p.name + '@' + p.version).join(', ');
    if (action === 'block') {
      // Every critical/high signal (uncapped) + the true CVE total. The grader
      // only sees what we put in blockContext — so the real count must be
      // STATED here; a bare preview lets it under-report (the count is data,
      // not something the grader should infer from a truncated list).
      const highSignals = pkgResults
        .flatMap((p: any) => (p.signals || []).filter((s: any) => s.severity === 'critical' || s.severity === 'high'));
      const cveCount = pkgResults
        .flatMap((p: any) => (p.signals || []))
        .filter((s: any) => s.type === 'cve').length;
      const findings: InstallScanResult['findings'] = [];
      const seenFindingKeys = new Set<string>();
      for (const p of pkgResults) {
        for (const s of (p.signals || [])) {
          const advisoryMatch = (s.detail || '').match(/\\b(GHSA-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}|CVE-\\d{4}-\\d+)\\b/i);
          const advisoryId = advisoryMatch ? advisoryMatch[1] : (s.type === 'cve' ? 'pkg:' + p.name : (s.type || 'signal'));
          const key = advisoryId + ':' + p.name + '@' + p.version;
          if (seenFindingKeys.has(key)) continue;
          seenFindingKeys.add(key);
          findings.push({
            advisoryId,
            name: p.name,
            version: p.version,
            severity: s.severity || 'high',
            detail: s.detail || summary,
          });
        }
      }
      // Preview the top 5 detail lines; the headline carries the true total.
      const blockSignals = highSignals.slice(0, 5);
      const headline = cveCount > 0
        ? cveCount + ' known CVE' + (cveCount === 1 ? '' : 's') + ' found in ' + (scannedLabel || 'the requested install') + '.\\n'
        : '';
      const preview = blockSignals.map((s: any) => s.detail).join('\\n') || summary;
      const more = highSignals.length > blockSignals.length
        ? '\\n(+' + (highSignals.length - blockSignals.length) + ' more critical/high findings not shown)'
        : '';
      return {
        scanned: true, action: 'block',
        blockContext: headline + preview + more
          + '\\nReport the CVE count and fix version exactly as stated above — do not estimate.'
          + '\\nDo NOT install packages with security risks. Use a patched version or a different package.',
        summary, scannedLabel, findings,
        violatedIds: blockSignals.map((s: any) => s.type + ':' + (s.detail || '').slice(0, 40)),
      };
    }
    return {
      scanned: true, action: action === 'warn' ? 'warn' : 'allow',
      blockContext: '', summary, scannedLabel, findings: [], violatedIds: [],
    };
  } catch {
    // Fail closed — could not verify the install (timeout / server
    // unreachable). Blocking an unverified install beats letting a
    // vulnerable package through.
    return {
      scanned: true, action: 'block',
      blockContext: 'Synkro could not verify this install is safe — the package scanner timed out or was unreachable. This is a verification failure, not a confirmed vulnerability. Retry once the Synkro server is reachable.',
      summary: 'install scan unavailable', scannedLabel: '', findings: [], violatedIds: ['scan_unavailable'],
    };
  }
}

// ─── Session Action Log ───

const SESSIONS_DIR = join(HOME, '.synkro', 'sessions');
const SAFE_SESSION_ID = /^[\\w-]+$/;

interface SessionAction {
  ts: string;
  tool: string;
  summary: string;
  file?: string;
  outcome?: string;
}

// True when the grader runs in Synkro Cloud (CF Containers) vs the on-device
// container. Defined here in the FULL common module too — it's referenced by
// appendSessionAction/dispatch helpers below; previously it lived only in the
// stub common, so the real editGuard hook hit a ReferenceError in cloud mode.
function deployIsCloud(): boolean { return process.env.SYNKRO_DEPLOY_LOCATION === 'cloud'; }

function sessionLogPath(sessionId: string): string | null {
  if (!sessionId || !SAFE_SESSION_ID.test(sessionId)) return null;
  return join(SESSIONS_DIR, sessionId + '.jsonl');
}

export function appendSessionAction(sessionId: string, entry: SessionAction): void {
  // Redact any secret in the action fields before they are persisted (local log
  // OR streamed to Timescale) — session diagnostics must never store a raw
  // credential. Tool name is a fixed identifier and left as-is.
  entry = {
    ...entry,
    summary: entry.summary ? scrubSecrets(entry.summary) : entry.summary,
    file: entry.file ? scrubSecrets(entry.file) : entry.file,
    outcome: entry.outcome ? scrubSecrets(entry.outcome) : entry.outcome,
  };
  const logPath = sessionLogPath(sessionId);
  if (!logPath) return;
  let step = 0;
  try {
    mkdirSync(SESSIONS_DIR, { recursive: true });
    try { step = readFileSync(logPath, 'utf-8').split('\\n').filter(Boolean).length + 1; } catch { step = 1; }
    appendFileSync(logPath, JSON.stringify(entry) + '\\n', 'utf-8');
  } catch {}

  // Cloud: stream the action to the session_actions hypertable (salience tagged
  // server-side, indexed) so the grade path pulls a BOUNDED indexed slice instead
  // of re-reading a multi-GB log — what lets us scale to million-tool-call
  // sessions. Fire-and-forget, idempotent by (session, step).
  if (deployIsCloud()) {
    shipCloud(loadJwt(), '/api/v1/cli/session-action', { session_id: sessionId, step, tool: entry.tool, summary: entry.summary, file: entry.file, outcome: entry.outcome });
    return;
  }
  const mcpPort = process.env.SYNKRO_MCP_PORT || '18931';
  let mcpToken = '';
  try { mcpToken = readFileSync(join(HOME, '.synkro', '.mcp-jwt'), 'utf-8').trim(); } catch {}
  if (!mcpToken) return;
  fetch('http://127.0.0.1:' + mcpPort + '/api/session-action', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + mcpToken },
    body: JSON.stringify({ session_id: sessionId, step, tool: entry.tool, summary: entry.summary, file: entry.file, outcome: entry.outcome }),
    signal: AbortSignal.timeout(2000),
  }).catch(() => {});
}

export function readSessionLog(sessionId: string): SessionAction[] {
  const logPath = sessionLogPath(sessionId);
  if (!logPath) return [];
  try {
    if (!existsSync(logPath)) return [];
    const raw = readFileSync(logPath, 'utf-8');
    return raw.split('\\n').filter(Boolean).map(l => {
      try { return JSON.parse(l); } catch { return null; }
    }).filter((x): x is SessionAction => x !== null);
  } catch { return []; }
}

// ─── Secret/PII firewall ───
// Detect LITERAL credentials in a proposed tool call so the hook can (a) BLOCK it
// locally before the tool runs — the value never reaches a file, shell history,
// OR the LLM grader (no leak to Anthropic/Cursor) — and (b) REDACT any secret
// before it is persisted/logged. Patterns use character classes (not \\d / \\.)
// to stay readable inside this template literal. HIGH-CONFIDENCE formats only —
// a false BLOCK stops the user's work, so we never block on generic entropy.
interface SecretHit { type: string; value: string; }
const HARD_SECRET_PATTERNS: Array<{ type: string; re: RegExp }> = [
  { type: 'anthropic-key', re: /sk-ant-[A-Za-z0-9_-]{24,}/g },
  { type: 'openai-key', re: /sk-(?:proj-)?[A-Za-z0-9]{32,}/g },
  { type: 'aws-access-key', re: /AKIA[0-9A-Z]{16}/g },
  { type: 'github-token', re: /gh[posru]_[A-Za-z0-9]{36,}/g },
  { type: 'google-api-key', re: /AIza[0-9A-Za-z_-]{35}/g },
  { type: 'slack-token', re: /xox[baprs]-[0-9A-Za-z-]{10,}/g },
  { type: 'stripe-key', re: /[rs]k_(?:live|test)_[A-Za-z0-9]{20,}/g },
  { type: 'jwt', re: /eyJ[A-Za-z0-9_-]{10,}[.][A-Za-z0-9_-]{10,}[.][A-Za-z0-9_-]{10,}/g },
  { type: 'private-key', re: /-----BEGIN [A-Z ]*PRIVATE KEY-----/g },
  { type: 'bearer-token', re: /[Bb]earer[ ]+[A-Za-z0-9._-]{20,}/g },
];
// A match that is obviously a placeholder or env reference is NOT a real secret —
// never block on $VAR, <token>, {{ }}, or common dummy values.
function isPlaceholderSecret(v: string): boolean {
  if (/[$<>{}]/.test(v)) return true;
  const tail = v.replace(/^(sk-ant-|[rs]k_(?:live|test)_|sk-|AKIA|gh[posru]_|AIza|xox[baprs]-|Bearer[ ]+)/, '');
  return /^(x+|0+|a+|placeholder|example|changeme|your[-_a-z0-9]*|dummy|test|fake|redacted|sample)$/i.test(tail);
}
// ── Opaque-secret heuristic (catches the FORMAT-LESS tail the regex can't) ──
// A high-entropy value sitting in a SECRET CONTEXT — assigned to a secret-named
// variable, in an auth header, or as a connection-string password. Local +
// deterministic, no model. Char-class regexes only (template-literal backslash safe).
function shannonEntropy(s: string): number {
  const m: Record<string, number> = {};
  for (const ch of s) m[ch] = (m[ch] || 0) + 1;
  let h = 0;
  for (const k in m) { const p = m[k] / s.length; h -= p * Math.log2(p); }
  return h;
}
function looksOpaqueSecret(v: string): boolean {
  if (v.length < 16) return false;
  if (/[$<>{}]/.test(v)) return false;                                                            // env ref / placeholder
  if (/^[0-9a-f]{32,}$/i.test(v)) return false;                                                    // hex hash / sha
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)) return false;     // uuid
  if (!(/[a-zA-Z]/.test(v) && /[0-9]/.test(v))) return false;                                      // real tokens mix letters+digits
  return shannonEntropy(v) >= 3.2;
}
const OPAQUE_CTX_PATTERNS: RegExp[] = [
  /[A-Za-z_][A-Za-z0-9_-]*(?:secret|token|key|password|passwd|api[_-]?key|auth|credential)[A-Za-z0-9_-]*[ ]*[=:][ ]*["']?([^ "'&|;]+)/gi,
  /(?:authorization|x-api-key|x-auth-token|api-key)[ ]*:[ ]*(?:bearer[ ]+)?([^ "']+)/gi,
  /[a-z][a-z0-9+.-]*:[/][/][^:/@ ]+:([^@/ ]+)@/gi,
];
export function detectOpaqueSecrets(text: string): SecretHit[] {
  if (!text) return [];
  const hits: SecretHit[] = [];
  const seen = new Set<string>();
  for (const re of OPAQUE_CTX_PATTERNS) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const v = m[1];
      if (v && !seen.has(v) && looksOpaqueSecret(v)) { seen.add(v); hits.push({ type: 'opaque-secret', value: v }); }
    }
  }
  return hits;
}
export function detectHardSecrets(text: string): SecretHit[] {
  if (!text) return [];
  const hits: SecretHit[] = [];
  const seen = new Set<string>();
  for (const { type, re } of HARD_SECRET_PATTERNS) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const v = m[0];
      if (isPlaceholderSecret(v) || seen.has(v)) continue;
      seen.add(v);
      hits.push({ type, value: v });
    }
  }
  // Union the format-less opaque-secret tail (entropy + secret context).
  for (const h of detectOpaqueSecrets(text)) {
    if (!seen.has(h.value)) { seen.add(h.value); hits.push(h); }
  }
  return hits;
}
// Shape-preserving redaction for persist/log/grade boundaries: the reader still
// sees that a secret of a given KIND was present, never the value. Broader than
// the block set (over-redacting a log is harmless; missing one is not).
export function scrubSecrets(text: string): string {
  if (!text) return text;
  let out = text;
  for (const { type, re } of HARD_SECRET_PATTERNS) {
    out = out.replace(re, (m) => isPlaceholderSecret(m) ? m : '<redacted:' + type + '>');
  }
  out = out.replace(/((?:password|passwd|secret|api[_-]?key|access[_-]?token|auth[_-]?token|client[_-]?secret|private[_-]?key)["' ]*[=:][ ]*["']?)([^ "'\\n]{12,})/gi, (full, pfx, val) => {
    if (/[$<>{}]/.test(val)) return full;
    return pfx + '<redacted:credential>';
  });
  // Format-less opaque secrets (entropy + secret context) the patterns above miss.
  for (const h of detectOpaqueSecrets(out)) out = out.split(h.value).join('<redacted:opaque-secret>');
  return out;
}
function guessEnvName(type: string, i: number): string {
  const base = type === 'anthropic-key' ? 'ANTHROPIC_API_KEY'
    : type === 'openai-key' ? 'OPENAI_API_KEY'
    : type === 'aws-access-key' ? 'AWS_ACCESS_KEY_ID'
    : type === 'github-token' ? 'GITHUB_TOKEN'
    : type === 'google-api-key' ? 'GOOGLE_API_KEY'
    : type === 'slack-token' ? 'SLACK_TOKEN'
    : type === 'stripe-key' ? 'STRIPE_API_KEY'
    : (type === 'jwt' || type === 'bearer-token') ? 'AUTH_TOKEN'
    : 'SECRET';
  return i === 0 ? base : base + '_' + (i + 1);
}
// Agent-facing remediation: show the SAME command with each literal secret swapped
// for an env reference, so the agent can re-issue it securely without the value
// ever appearing in the command, logs, or model context.
export function secretRemediation(command: string, hits: SecretHit[]): string {
  let safe = command;
  const names: string[] = [];
  hits.forEach((h, i) => {
    const envName = guessEnvName(h.type, i);
    names.push(envName);
    safe = safe.split(h.value).join('$' + envName);
  });
  const typeList = hits.map(h => h.type).join(', ');
  return 'Synkro blocked this tool call: it contains a literal credential (' + typeList + '). '
    + 'A secret value must NEVER be inlined, printed, or echoed — that leaks it into shell history, logs, and the model context. '
    + 'Reference the credential from the environment so its value resolves at runtime and never appears in the command. '
    + 'Set it once out-of-band (export ' + names[0] + '=... in an untracked file, or load from your secret manager), then re-issue as:\\n  ' + safe + '\\n'
    + 'Do NOT print the value to verify it; check the variable is non-empty instead, e.g. test -n "$' + names[0] + '". Re-issue with the literal removed — do not ask the user to paste it.';
}
// The NEW text an edit introduces, so we flag a secret the agent is ADDING — never
// a pre-existing one already in the file (which would falsely block any edit to a
// file that contains a credential). Write = whole new content; Edit = new_string;
// MultiEdit = every edit's new_string.
export function editedAddedText(toolName: string, toolInput: any): string {
  const ti = toolInput || {};
  if (toolName === 'MultiEdit' && Array.isArray(ti.edits)) {
    return ti.edits.map((e: any) => (e && typeof e.new_string === 'string') ? e.new_string : '').join('\\n');
  }
  return String(ti.content ?? ti.contents ?? ti.new_string ?? ti.new_source ?? ti.code_edit ?? ti.patch ?? '');
}
// Deny payload when an edit HARDCODES a literal credential (else null). Code-edit
// remediation: load from env/secret manager at runtime, never inline the value.
export function editSecretDenial(toolName: string, toolInput: any, fileShort: string, tagStr: string): { systemMessage: string; hookSpecificOutput: any } | null {
  const hits = detectHardSecrets(editedAddedText(toolName, toolInput));
  if (!hits.length) return null;
  const types = hits.map(h => h.type).join(', ');
  const rem = 'Synkro blocked this edit: it hardcodes a literal credential (' + types + ') into ' + fileShort + '. '
    + 'NEVER commit a secret value to a file — it leaks into source control, logs, and the model context. '
    + 'Load it at runtime from the environment or a secret manager and reference it instead (e.g. process.env.SECRET_NAME, os.environ["SECRET_NAME"], or your config/secret provider). '
    + 'Re-do the edit with the literal removed; do not ask the user to paste it.';
  return {
    systemMessage: tagStr + ' editGuard ' + fileShort + ' → BLOCKED: hardcoded credential (' + types + '). Not sent to the grader; reference it via the environment.',
    hookSpecificOutput: { hookEventName: 'PreToolUse', permissionDecision: 'deny', permissionDecisionReason: rem, additionalContext: rem },
  };
}

const RETRIEVAL_RECENT_N = 40;
const PROCESS_KW = ['test', 'typecheck', 'tsc', 'lint', 'build', 'migrate', 'migration', 'deploy', 'publish', 'push', 'seed'];

// Is this session action security- or rule-relevant (i.e. can it participate in a
// violation)? Conservative net — uses substring checks (no regex, to stay safe
// inside the materialized-hook template). Plus rule-awareness: an action that
// performs a process keyword an active rule actually mentions (e.g. a "pnpm test"
// when a rule is about tests) is kept so precondition rules stay resolvable.
function actionSalience(a: SessionAction, ruleText: string): string | null {
  const x = ((a.tool || '') + ' ' + (a.file || '') + ' ' + (a.summary || '')).toLowerCase();
  const has = (arr: string[]): boolean => { for (const k of arr) if (x.indexOf(k) >= 0) return true; return false; };
  if (has(['.env', 'credential', 'secret', 'token', '.pem', '.key', 'password', 'api key', 'api_key', 'api-key', 'authorization', 'bearer', 'oauth', '.aws'])) return 'secret';
  if (has(['curl ', 'wget ', 'ssh ', 'scp ', 'netcat', 'http://', 'https://', 'fetch('])) return 'network';
  if (has(['rm -rf', 'rm -r ', 'drop table', 'drop database', 'truncate', 'chmod', 'chown', 'sudo', 'kill ', 'wrangler', '--force', 'deploy', 'publish', 'git push'])) return 'destructive';
  for (const kw of PROCESS_KW) if (ruleText.indexOf(kw) >= 0 && x.indexOf(kw) >= 0) return 'rule';
  return null;
}

// Cloud session log — retrieval style (validated 4/4 vs the last-200 dump in the
// shadow corpus). Keeps EVERY security-salient + rule-relevant action in order
// regardless of age (so cross-turn violations the dump's 200-window drops are
// still seen), a recent verbatim window, and a COMPLETENESS-asserting summary of
// the routine remainder (the assertion is what lets the grader trust the collapsed
// part is benign — safe only because the salience net above is conservative).
function compressSessionLogRetrieval(actions: SessionAction[], rules: Rule[]): string {
  if (actions.length === 0) return '';
  const ruleText = (rules || []).map(r => String((r as any).text || '').toLowerCase()).join(' ');
  const total = actions.length;
  const recentStart = Math.max(0, total - RETRIEVAL_RECENT_N);
  const salient: Array<{ a: SessionAction; s: string; i: number }> = [];
  const noise: Record<string, number> = {};
  for (let i = 0; i < recentStart; i++) {
    const a = actions[i];
    const s = actionSalience(a, ruleText);
    if (s) salient.push({ a, s, i });
    else noise[a.tool] = (noise[a.tool] || 0) + 1;
  }
  const lines: string[] = ['SESSION HISTORY (' + total + ' actions; security-salient + recent, in order):'];
  if (salient.length) {
    lines.push('  --- relevant (any point this session) ---');
    for (const r of salient) {
      const target = r.a.file || (r.a.summary || '').slice(0, 80);
      lines.push('  ' + (r.i + 1) + '. [' + r.s + '] ' + r.a.tool + ' ' + target + (r.a.outcome ? ' -> ' + r.a.outcome : ''));
    }
  }
  const noiseN = Object.values(noise).reduce((n, c) => n + c, 0);
  if (noiseN) {
    lines.push('  [' + noiseN + ' earlier routine actions (' + Object.entries(noise).map(([t, c]) => c + ' ' + t).join(', ') + '). The relevant list above is COMPLETE: every credential/secret read, outbound network call, and destructive or rule-gated action this session is listed there; these ' + noiseN + ' are non-salient in-repo reads/edits/builds only.]');
  }
  lines.push('  --- recent ---');
  for (let i = recentStart; i < total; i++) {
    const a = actions[i];
    lines.push('  ' + (i + 1) + '. ' + (a.summary || a.tool) + (a.outcome ? ' -> ' + a.outcome : ''));
  }
  return lines.join('\\n');
}

export function compressSessionLog(actions: SessionAction[], rules?: Rule[]): string {
  if (actions.length === 0) return '';
  // Cloud grade path passes the active rules → retrieval-style context. Local
  // (no rules arg, or local storage mode) keeps the dump below, UNTOUCHED.
  if (rules !== undefined && !isLocalStorageMode()) return compressSessionLogRetrieval(actions, rules);
  const total = actions.length;
  const lines: string[] = [];

  if (total > 200) {
    const old = actions.slice(0, total - 200);
    const counts: Record<string, number> = {};
    const dirs = new Set<string>();
    for (const a of old) {
      counts[a.tool] = (counts[a.tool] || 0) + 1;
      if (a.file) {
        const dir = a.file.split('/').slice(0, -1).join('/') || '.';
        dirs.add(dir);
      }
    }
    const parts = Object.entries(counts).map(([t, c]) => c + ' ' + t).join(', ');
    const dirHint = dirs.size > 0 ? ' (' + [...dirs].slice(0, 3).join(', ') + ')' : '';
    lines.push('  [' + old.length + ' earlier: ' + parts + dirHint + ']');
  }

  const tier2Start = Math.max(0, total - 200);
  const tier2End = Math.max(0, total - 10);
  for (let i = tier2Start; i < tier2End; i++) {
    const a = actions[i];
    const target = a.file || a.summary.slice(0, 40);
    const out = a.outcome ? ' -> ' + a.outcome : '';
    lines.push('  ' + (i + 1) + '. ' + a.tool + ' ' + target + out);
  }

  const tier1Start = Math.max(0, total - 10);
  if (tier2End > tier2Start) lines.push('  --- recent ---');
  for (let i = tier1Start; i < total; i++) {
    const a = actions[i];
    const out = a.outcome ? ' -> ' + a.outcome : '';
    lines.push('  ' + (i + 1) + '. ' + a.summary + out);
  }

  return 'SESSION HISTORY (' + total + ' actions):\\n' + lines.join('\\n');
}

// Cloud: fetch the BOUNDED, indexed session context from Timescale (salient +
// recent + complete summary) — flat cost at any session length. Returns null if
// the session isn't streamed yet (brand-new / streaming lagging) so the caller
// falls back to the local path.
async function fetchSessionContext(sessionId: string): Promise<string | null> {
  const jwt = loadJwt();
  if (!jwt) return null;
  try {
    const r = await fetch(GATEWAY_URL + '/api/v1/hook/session-context', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + jwt },
      body: JSON.stringify({ session_id: sessionId }),
      signal: AbortSignal.timeout(3000),
    });
    if (!r.ok) return null;
    const d = await r.json() as { context?: string };
    return (d && typeof d.context === 'string' && d.context) ? d.context : null;
  } catch {
    return null;
  }
}

// Grade-time session log. CLOUD → Timescale-backed bounded context (this is the
// million-tool-call path: no full-history read); falls back to the local
// retrieval compressor only if the session isn't streamed yet. LOCAL → the
// on-device dump, completely UNTOUCHED.
export async function sessionLogForGrade(sessionId: string, rules: Rule[]): Promise<string> {
  if (isLocalStorageMode()) return compressSessionLog(readSessionLog(sessionId));
  const ctx = await fetchSessionContext(sessionId);
  if (ctx) return ctx;
  return compressSessionLog(readSessionLog(sessionId), rules);
}

export function cleanupSessionLog(sessionId: string): void {
  const logPath = sessionLogPath(sessionId);
  if (!logPath) return;
  try { unlinkSync(logPath); } catch {}
}

// ─── Verdict Parsing ───

export interface Verdict {
  ok: boolean;
  reason: string;
  suggestedFix: string;
  ruleId: string;
  ruleMode: string;
  severity: string;
  category: string;
}

export function parseVerdict(resp: string): Verdict {
  const verdict: Verdict = {
    ok: true,
    reason: '',
    suggestedFix: '',
    ruleId: '',
    ruleMode: '',
    severity: 'low',
    category: 'clean',
  };

  // Flatten newlines for easier regex
  const flat = resp.replace(/\\n/g, ' ');
  const outerMatch = flat.match(/<synkro-verdict>(.*)<\\/synkro-verdict>/);
  if (!outerMatch) return verdict;
  const inner = outerMatch[1];

  const okMatch = inner.match(/<ok>(.*?)<\\/ok>/);
  if (okMatch) verdict.ok = okMatch[1].trim() !== 'false';

  const decisionMatch = inner.match(/<decision>(.*?)<\\/decision>/) || inner.match(/<overall>(.*?)<\\/overall>/);
  if (decisionMatch) {
    const d = decisionMatch[1].trim().toLowerCase();
    if (d === 'block' || d === 'blocking' || d === 'deny' || d === 'fail') verdict.ok = false;
    else if (d === 'allow' || d === 'pass' || d === 'approve') verdict.ok = true;
  }

  const reasonMatch = inner.match(/<reason>(.*?)<\\/reason>/) || inner.match(/<reasoning>(.*?)<\\/reasoning>/);
  if (reasonMatch) verdict.reason = reasonMatch[1].trim();

  const fixMatch = inner.match(/<suggested_fix>(.*?)<\\/suggested_fix>/);
  if (fixMatch) verdict.suggestedFix = fixMatch[1].trim();

  if (!verdict.ok) {
    const ruleIdMatch = inner.match(/<rule_id>(.*?)<\\/rule_id>/);
    const ruleModeMatch = inner.match(/<rule_mode>(.*?)<\\/rule_mode>/);
    const sevMatch = inner.match(/<risk_level>(.*?)<\\/risk_level>/);

    if (ruleIdMatch) {
      verdict.ruleId = ruleIdMatch[1].trim();
    } else {
      // Try to find inside a <violation> block
      const violationMatch = inner.match(/<violation>(.*?)<\\/violation>/);
      if (violationMatch) {
        const vBlock = violationMatch[1];
        const vRuleId = vBlock.match(/<rule_id>(.*?)<\\/rule_id>/);
        if (vRuleId) verdict.ruleId = vRuleId[1].trim();
        if (!verdict.reason) {
          const vReason = vBlock.match(/<reason>(.*?)<\\/reason>/);
          if (vReason) verdict.reason = vReason[1].trim();
        }
        if (!verdict.suggestedFix) {
          const vFix = vBlock.match(/<suggested_fix>(.*?)<\\/suggested_fix>/);
          if (vFix) verdict.suggestedFix = vFix[1].trim();
        }
        if (!sevMatch) {
          const vSev = vBlock.match(/<severity>(.*?)<\\/severity>/);
          if (vSev) verdict.severity = vSev[1].trim();
        }
      }
    }

    if (ruleModeMatch) verdict.ruleMode = ruleModeMatch[1].trim();
    if (sevMatch) verdict.severity = sevMatch[1].trim();
    verdict.severity = verdict.severity || 'high';

    const catMatch = inner.match(/<category>(.*?)<\\/category>/);
    verdict.category = catMatch ? catMatch[1].trim() : 'uncategorized';

    // Fallback: extract rule ID from reason text
    if (!verdict.ruleId && verdict.reason) {
      const rMatch = verdict.reason.match(/[Rr]\\d{3}/);
      if (rMatch) verdict.ruleId = rMatch[0];
    }
  }

  return verdict;
}

export interface CombinedFinding {
  id: string;
  reason: string;
  suggestedFix: string;
  severity: string;
  ruleMode: string;
  codeSnippet: string;
}

// Splits a grade-edit-cwe verdict (one <synkro-verdict> whose <violation> blocks
// MIX org-rule ids and cwe-### ids) into (a) a rules Verdict the existing rule
// enforcement consumes verbatim and (b) a list of CWE findings for the CWE block
// path. A block whose rule_id starts with "cwe-" is a CWE finding; anything else
// is an org-rule violation (org rules take precedence). Org-rule verdict is ok
// UNLESS a non-cwe violation is present — so a CWE-only failure still passes the
// rule gate while blocking on CWE.
export function parseCombinedVerdict(resp: string): { ruleVerdict: Verdict; cweFindings: CombinedFinding[] } {
  const base = parseVerdict(resp);
  const ruleVerdict: Verdict = { ok: true, reason: '', suggestedFix: '', ruleId: '', ruleMode: '', severity: 'low', category: 'clean' };
  const cweFindings: CombinedFinding[] = [];
  const blocks = resp.match(/<violation>[\\s\\S]*?<\\/violation>/g) || [];
  let firstRule: CombinedFinding | null = null;
  for (const b of blocks) {
    const idRaw = (b.match(/<rule_id>([^<]+)<\\/rule_id>/) || [])[1];
    if (!idRaw) continue;
    const id = idRaw.trim();
    const finding: CombinedFinding = {
      id,
      reason: ((b.match(/<reason>([\\s\\S]*?)<\\/reason>/) || [])[1] || '').trim(),
      suggestedFix: ((b.match(/<suggested_fix>([\\s\\S]*?)<\\/suggested_fix>/) || [])[1] || '').trim(),
      severity: ((b.match(/<severity>([^<]+)<\\/severity>/) || [])[1] || '').trim(),
      ruleMode: ((b.match(/<rule_mode>([^<]+)<\\/rule_mode>/) || [])[1] || '').trim(),
      codeSnippet: ((b.match(/<code_snippet>([\\s\\S]*?)<\\/code_snippet>/) || [])[1] || '').replace(/^\\n+|\\n+$/g, ''),
    };
    if (/^cwe-/i.test(id)) {
      finding.id = id.replace(/^cwe-/i, 'CWE-');
      if (!cweFindings.some(f => f.id === finding.id)) cweFindings.push(finding);
    } else if (!firstRule) {
      firstRule = finding;
    }
  }
  if (firstRule) {
    ruleVerdict.ok = false;
    ruleVerdict.ruleId = firstRule.id;
    ruleVerdict.reason = firstRule.reason;
    ruleVerdict.suggestedFix = firstRule.suggestedFix;
    ruleVerdict.ruleMode = firstRule.ruleMode;
    ruleVerdict.severity = firstRule.severity || 'high';
    ruleVerdict.category = base.category || 'uncategorized';
  } else if (base.ok === false && base.ruleId && !/^cwe-/i.test(base.ruleId)) {
    // No <violation> blocks but a top-level rule_id failure (defensive).
    ruleVerdict.ok = false;
    ruleVerdict.ruleId = base.ruleId;
    ruleVerdict.reason = base.reason;
    ruleVerdict.suggestedFix = base.suggestedFix;
    ruleVerdict.ruleMode = base.ruleMode;
    ruleVerdict.severity = base.severity || 'high';
  }
  return { ruleVerdict, cweFindings };
}

// ─── Telemetry Dispatch ───

// Gated cloud telemetry POST — fires only when storage mode is 'cloud'. The
// local PGLite spool (appendLocalTelemetry) always gets the data regardless,
// so 'local' storage keeps everything on the machine.
export function shipCloud(jwt: string, path: string, body: Record<string, any>, timeoutMs = 3000): void {
  if ((process.env.SYNKRO_STORAGE_MODE || 'local') !== 'cloud') return;
  fetch(GATEWAY_URL + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + jwt },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(timeoutMs),
  }).catch(() => {});
}

export function dispatchCapture(
  jwt: string,
  hookType: string,
  verdictStr: string,
  severity: string,
  category: string,
  toolName: string,
  repo: string,
  sessionId: string,
  captureDepth: string,
  opts?: {
    command?: string;
    reasoning?: string;
    rulesChecked?: Rule[] | string;
    violatedRules?: string[];
    recentUserMessages?: string[];
    ccModel?: string;
    linesAdded?: number;
    linesRemoved?: number;
    codeChange?: string;
  },
): void {
  // Fire-and-forget
  const eventId = mintEventId('evt');
  const model = normalizeCaptureModel(opts?.ccModel || 'unknown');

  const body: Record<string, any> = {
    capture_type: 'local_verdict',
    event_id: eventId,
    // Source-time of the action. The ingest handler uses this for created_at
    // — without it, the spool drain stamps every event with NOW() and a batch
    // of actions taken minutes apart all collapse to the drain instant.
    _ts: new Date().toISOString(),
    hook_type: hookType,
    verdict: verdictStr,
    severity,
    category,
    cc_model: model,
    model,
    tool_name: toolName,
  };
  const resolvedRepo = repo || detectRepo('') || readCachedRepo();
  if (resolvedRepo) body.repo = resolvedRepo;
  if (sessionId) body.session_id = sessionId;

  // Local telemetry always gets full content — data never leaves the machine
  const localBody = { ...body };
  if (opts) {
    if (opts.command) localBody.command = opts.command;
    if (opts.reasoning) localBody.reasoning = opts.reasoning;
    if (opts.rulesChecked) localBody.rules_checked = opts.rulesChecked;
    if (opts.violatedRules) localBody.violated_rules = opts.violatedRules;
    if (opts.recentUserMessages) localBody.recent_user_messages = opts.recentUserMessages;
    if (opts.linesAdded != null) localBody.lines_added = opts.linesAdded;
    if (opts.linesRemoved != null) localBody.lines_removed = opts.linesRemoved;
    if (opts.codeChange) localBody.code_change = opts.codeChange;
  }
  appendLocalTelemetry(localBody);

  // Cloud copy carries the same full content as the local spool; shipCloud
  // gates on storage mode (no-op when storage is local).
  if (opts) {
    if (opts.command) body.command = opts.command;
    if (opts.reasoning) body.reasoning = opts.reasoning;
    if (opts.rulesChecked) body.rules_checked = opts.rulesChecked;
    if (opts.violatedRules) body.violated_rules = opts.violatedRules;
    if (opts.recentUserMessages) body.recent_user_messages = opts.recentUserMessages;
    if (opts.linesAdded != null) body.lines_added = opts.linesAdded;
    if (opts.linesRemoved != null) body.lines_removed = opts.linesRemoved;
    if (opts.codeChange) body.code_change = opts.codeChange;
  }
  shipCloud(jwt, '/api/v1/hook/capture', body);
}

// ─── Durable Telemetry Spool ───
// Telemetry must survive process death, container restarts, and ingest-server
// backpressure. Every event is appended synchronously to a local JSONL spool.
// drainSpool streams claim files line-by-line (never loads multi-GB into RAM),
// ships in batches, and on partial failure only re-spools the batches that
// did not land — never the whole claim file (that caused 2GB amplification).

const TELEMETRY_SPOOL = join(HOME, '.synkro', 'telemetry-spool.jsonl');
const SPOOL_DRAIN_PREFIX = 'telemetry-spool.jsonl.draining.';
const SPOOL_DRAIN_LOCK = join(HOME, '.synkro', 'telemetry-spool.drain.lock');
const SPOOL_MAX_CLAIM_BYTES = 50 * 1024 * 1024;
const SPOOL_BATCH_SIZE = 200;
const SPOOL_DRAIN_LOCK_STALE_MS = 120_000;

/** Stable id for spool rows — required for idempotent ingest (ON CONFLICT). */
export function mintEventId(prefix = 'evt'): string {
  return prefix + '_' + Date.now() + '_' + process.pid;
}

export function appendLocalTelemetry(body: Record<string, any>): void {
  if ((process.env.SYNKRO_STORAGE_MODE || 'local') !== 'local') return;
  const event = { ...body };
  // Redact secrets from free-text fields before they reach the spool/dashboard,
  // for any caller that passes raw tool-call content (idempotent if already scrubbed).
  for (const k of ['command', 'reasoning', 'summary', 'target', 'content']) {
    if (typeof event[k] === 'string') event[k] = scrubSecrets(event[k]);
  }
  if (!event.event_id) {
    const ct = String(event.capture_type || '');
    const prefix = ct === 'usage_tick' ? 'usage' : ct === 'edit_scan' ? 'edit' : 'evt';
    event.event_id = mintEventId(prefix);
  }
  if (!event._ts) event._ts = new Date().toISOString();
  try {
    appendFileSync(TELEMETRY_SPOOL, JSON.stringify(event) + '\\n');
  } catch {}
  // Realtime: fire-and-forget POST to the local server so events appear
  // in the dashboard immediately. Spool file remains the durable fallback.
  try {
    const port = process.env.SYNKRO_MCP_PORT || '18931';
    let token = '';
    try { token = readFileSync(join(HOME, '.synkro', '.mcp-jwt'), 'utf-8').trim(); } catch {}
    if (token) {
      fetch('http://127.0.0.1:' + port + '/api/ingest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify(event),
        signal: AbortSignal.timeout(3000),
      }).catch(() => {});
    }
  } catch {}
}

function tryAcquireDrainLock(): boolean {
  try {
    if (existsSync(SPOOL_DRAIN_LOCK)) {
      if (Date.now() - statSync(SPOOL_DRAIN_LOCK).mtimeMs < SPOOL_DRAIN_LOCK_STALE_MS) return false;
      unlinkSync(SPOOL_DRAIN_LOCK);
    }
    mkdirSync(dirname(SPOOL_DRAIN_LOCK), { recursive: true });
    writeFileSync(SPOOL_DRAIN_LOCK, String(process.pid) + '\\n');
    return true;
  } catch {
    return false;
  }
}

function releaseDrainLock(): void {
  try { unlinkSync(SPOOL_DRAIN_LOCK); } catch {}
}

async function postSpoolBatch(mcpPort: string, mcpToken: string, events: any[]): Promise<boolean> {
  if (!events.length) return true;
  try {
    const resp = await fetch('http://127.0.0.1:' + mcpPort + '/api/ingest/batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + mcpToken },
      body: JSON.stringify({ events }),
      signal: AbortSignal.timeout(30000),
    });
    if (!resp.ok) {
      const errBody = await resp.text().catch(() => '');
      log('drainSpool batch HTTP ' + resp.status + ': ' + errBody.slice(0, 120));
    }
    return resp.ok;
  } catch (e) {
    log('drainSpool batch error: ' + ((e as Error).message || String(e)).slice(0, 120));
    return false;
  }
}

function quarantineOversizedClaim(claimPath: string): void {
  const stuck = claimPath + '.STUCK-OVERSIZED.bak';
  try {
    renameSync(claimPath, stuck);
    log('drainSpool quarantined oversized claim → ' + basename(stuck));
  } catch (e) {
    log('drainSpool quarantine failed: ' + String(e));
  }
}

async function drainClaimFile(claimPath: string, mcpPort: string, mcpToken: string): Promise<void> {
  let sz = 0;
  try { sz = statSync(claimPath).size; } catch { return; }
  if (sz === 0) {
    try { unlinkSync(claimPath); } catch {}
    return;
  }
  if (sz > SPOOL_MAX_CLAIM_BYTES) {
    quarantineOversizedClaim(claimPath);
    return;
  }

  const pending: any[] = [];
  let batch: any[] = [];
  let failed = false;

  const rl = createInterface({
    input: createReadStream(claimPath, { encoding: 'utf8' }),
    crlfDelay: Infinity,
  });

  for await (const line of rl) {
    if (failed) {
      const t = line.trim();
      if (!t) continue;
      try { pending.push(JSON.parse(t)); } catch {}
      continue;
    }
    const t = line.trim();
    if (!t) continue;
    try {
      batch.push(JSON.parse(t));
    } catch {
      continue;
    }
    if (batch.length < SPOOL_BATCH_SIZE) continue;
    const chunk = batch.splice(0, SPOOL_BATCH_SIZE);
    if (!(await postSpoolBatch(mcpPort, mcpToken, chunk))) {
      pending.push(...chunk);
      failed = true;
    }
  }

  if (!failed && batch.length > 0) {
    if (!(await postSpoolBatch(mcpPort, mcpToken, batch))) {
      pending.push(...batch);
      failed = true;
    } else {
      batch = [];
    }
  } else if (failed && batch.length > 0) {
    pending.push(...batch);
  }

  if (pending.length === 0) {
    try { unlinkSync(claimPath); } catch {}
    return;
  }

  try {
    for (const evt of pending) {
      appendFileSync(TELEMETRY_SPOOL, JSON.stringify(evt) + '\\n');
    }
    try { unlinkSync(claimPath); } catch {}
    log('drainSpool re-spooled ' + pending.length + ' events from ' + basename(claimPath));
  } catch (e) {
    log('drainSpool re-spool failed: ' + String(e));
  }
}

export async function drainSpool(): Promise<void> {
  if (!tryAcquireDrainLock()) return;

  const dir = join(HOME, '.synkro');
  const claimed: string[] = [];

  try {
    try {
      if (existsSync(TELEMETRY_SPOOL) && statSync(TELEMETRY_SPOOL).size > 0) {
        const claim = join(dir, SPOOL_DRAIN_PREFIX + process.pid + '.' + Date.now());
        renameSync(TELEMETRY_SPOOL, claim);
        claimed.push(claim);
      }
    } catch {}

    try {
      for (const f of readdirSync(dir)) {
        if (!f.startsWith(SPOOL_DRAIN_PREFIX)) continue;
        if (f.includes('.STUCK-') || f.endsWith('.bak')) continue;
        const full = join(dir, f);
        if (claimed.indexOf(full) !== -1) continue;
        try {
          if (Date.now() - statSync(full).mtimeMs > 30000) claimed.push(full);
        } catch {}
      }
    } catch {}

    if (claimed.length === 0) return;

    const mcpPort = process.env.SYNKRO_MCP_PORT || '18931';
    let mcpToken = '';
    try { mcpToken = readFileSync(join(HOME, '.synkro', '.mcp-jwt'), 'utf-8').trim(); } catch {}
    if (!mcpToken) return;

    for (const f of claimed) {
      await drainClaimFile(f, mcpPort, mcpToken);
    }
  } finally {
    releaseDrainLock();
  }
}

// ─── Rule Mode Lookup ───

export function ruleMode(ruleId: string, rules: Rule[]): 'ask' | 'fix' {
  if (!ruleId || !rules.length) return 'ask';
  const matched = rules.filter(r => r.rule_id === ruleId);
  if (matched.some(r => r.mode === 'blocking' || r.mode === 'ask')) return 'ask';
  return normalizeMode(matched[0]?.mode);
}

// ─── Content Reconstruction ───

function patchTextFromToolInput(toolInput: any): string {
  return String(toolInput.patch ?? toolInput.content ?? toolInput.code_edit ?? toolInput.new_string ?? toolInput.input ?? toolInput.diff ?? '');
}

function filePathFromPatch(patchText: string): string {
  const match = patchText.match(/^\\*\\*\\* (?:Add|Update|Delete) File:\\s*(.+)$/m);
  return match?.[1]?.trim() || '';
}

function contentFromPatch(patchText: string): string {
  const addedOrContext = patchText
    .split('\\n')
    .filter(line => (line.startsWith('+') && !line.startsWith('+++')) || line.startsWith(' '))
    .map(line => line.slice(1))
    .join('\\n')
    .trim();
  return addedOrContext || patchText;
}

export function filePathFromToolInput(toolInput: any): string {
  return toolInput.file_path || toolInput.notebook_path || toolInput.path || toolInput.target_file || filePathFromPatch(patchTextFromToolInput(toolInput));
}

export function reconstructContent(toolName: string, toolInput: any, filePath: string, cwd?: string): string {
  // Cap the grade payload (64KB ~= 16K tokens) so a huge Write/whole-file edit
  // cannot push grader inference past its 45s budget. Applies UNIFORMLY to every
  // path, including the previously-uncapped Write/StrReplace/default branches.
  return reconstructContentInner(toolName, toolInput, filePath, cwd).slice(0, 65536);
}
function reconstructContentInner(toolName: string, toolInput: any, filePath: string, cwd?: string): string {
  const canRead = filePath && cwd && isPathUnder(filePath, cwd);
  switch (toolName) {
    case 'ApplyPatch':
    case 'apply_patch':
      return contentFromPatch(patchTextFromToolInput(toolInput));
    case 'Write':
      return toolInput.content || toolInput.contents || '';
    case 'edit_file':
    case 'reapply':
      return toolInput.content || toolInput.new_string || toolInput.code_edit || '';
    case 'Edit': {
      let content = '';
      try {
        if (canRead && existsSync(filePath)) {
          content = readFileSync(filePath, 'utf-8').slice(0, 65536);
        }
      } catch {}
      const oldStr = toolInput.old_string || '';
      const newStr = toolInput.new_string || '';
      if (oldStr && content.includes(oldStr)) {
        return content.replace(oldStr, newStr);
      }
      return content || newStr;
    }
    case 'MultiEdit': {
      let content = '';
      try {
        if (canRead && existsSync(filePath)) {
          content = readFileSync(filePath, 'utf-8').slice(0, 65536);
        }
      } catch {}
      const edits = Array.isArray(toolInput.edits) ? toolInput.edits : [];
      for (const edit of edits) {
        if (!edit || typeof edit !== 'object') continue;
        const old = edit.old_string || '';
        const nw = edit.new_string || '';
        if (old && content.includes(old)) {
          content = content.replace(old, nw);
        }
      }
      return content;
    }
    case 'NotebookEdit':
    case 'edit_notebook':
      return toolInput.new_source || '';
    case 'StrReplace':
      return toolInput.new_string || toolInput.content || toolInput.code_edit || '';
    default:
      return toolInput.content || toolInput.new_string || toolInput.code_edit || '';
  }
}

// ─── HTTP with Retry ───

export async function postWithRetry(url: string, body: any, jwt: string, timeout = 8000): Promise<any> {
  let currentJwt = jwt;
  let resp: Response;
  try {
    resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + currentJwt },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(timeout),
    });
  } catch {
    return null;
  }

  let data: any;
  try { data = await resp.json(); } catch { return null; }

  // Retry on token expiry
  if (data?.detail && (data.detail.includes('Token has expired') || data.detail.includes('Invalid or expired token'))) {
    try {
      currentJwt = await refreshJwt(currentJwt);
      const resp2 = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + currentJwt },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(timeout),
      });
      data = await resp2.json();
    } catch {
      return null;
    }
  }

  return data;
}

// ─── Read Stdin ───

export async function readStdin(): Promise<string> {
  const chunks: Buffer[] = [];
  for await (const chunk of process.stdin) {
    chunks.push(Buffer.isBuffer(chunk) ? chunk : Buffer.from(chunk));
  }
  return Buffer.concat(chunks).toString('utf-8');
}

// ─── Transcript path + message extraction (CC + Cursor) ───

/** Cursor stores transcripts under ~/.cursor/projects/{slug}/agent-transcripts/ */
export function cursorProjectSlug(workspaceRoot: string): string {
  return workspaceRoot.replace(new RegExp('^[/]+'), '').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/** Resolve transcript JSONL — payload field, CURSOR_TRANSCRIPT_PATH, or Cursor agent-transcripts dir. */
export function resolveTranscriptPath(payload: Record<string, unknown>): string {
  const explicit = typeof payload.transcript_path === 'string' ? payload.transcript_path.trim() : '';
  if (explicit && existsSync(explicit)) return explicit;

  const fromEnv = (process.env.CURSOR_TRANSCRIPT_PATH || '').trim();
  if (fromEnv && existsSync(fromEnv)) return fromEnv;

  if (!isCursorHookFormat()) return explicit || fromEnv || '';

  const sessionId = hookSessionId(payload);
  const workspaceRoots = Array.isArray(payload.workspace_roots)
    ? (payload.workspace_roots as unknown[]).filter((r): r is string => typeof r === 'string')
    : [];
  const cwd = (typeof payload.cwd === 'string' && payload.cwd)
    || workspaceRoots[0]
    || (process.env.CURSOR_PROJECT_DIR || '');
  if (!sessionId || !cwd) return explicit || fromEnv || '';

  const base = join(HOME, '.cursor', 'projects', cursorProjectSlug(cwd), 'agent-transcripts');
  const nested = join(base, sessionId, sessionId + '.jsonl');
  if (existsSync(nested)) return nested;
  if (existsSync(join(base, sessionId))) return nested;
  const flat = join(base, sessionId + '.jsonl');
  if (existsSync(flat)) return flat;

  return explicit || fromEnv || '';
}

function transcriptEntryType(entry: Record<string, unknown>): string {
  return String(entry.type || entry.role || '');
}

function transcriptContentBlock(entry: Record<string, unknown>): Record<string, unknown> | null {
  const msg = entry.message as Record<string, unknown> | string | undefined;
  const content = (msg && typeof msg === 'object' ? msg.content : undefined) ?? entry.content;
  if (content && typeof content === 'object' && !Array.isArray(content)) {
    return content as Record<string, unknown>;
  }
  return null;
}

function isProviderRedactedPlaceholder(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  return t === '[REDACTED]' || /^\\[REDACTED\\](\\s*\\[REDACTED\\])*$/.test(t);
}

function extractContentBlockText(block: Record<string, unknown>): string {
  const type = String(block.type || '');
  if (type === 'text' && typeof block.text === 'string') return block.text;
  if ((type === 'thinking' || type === 'redacted_thinking') && typeof block.thinking === 'string') {
    return block.thinking;
  }
  if (type === 'tool_result') {
    const c = block.content;
    if (typeof c === 'string') return c;
    if (Array.isArray(c)) {
      return c.map((part: unknown) => {
        if (typeof part === 'string') return part;
        const p = part as Record<string, unknown>;
        return typeof p.text === 'string' ? p.text : '';
      }).filter(Boolean).join('\\n');
    }
    return '';
  }
  return '';
}

function thoughtOverlayPath(sessionId: string): string {
  return join(HOME, '.synkro', 'sessions', sessionId, 'thought-overlay.jsonl');
}

/** Cursor afterAgentThought delivers full thinking; transcript JSONL stores [REDACTED]. Queue for transcript sync. */
export function appendThoughtOverlay(sessionId: string, text: string): void {
  const trimmed = text.trim();
  if (!sessionId || !trimmed || isProviderRedactedPlaceholder(trimmed)) return;
  const path = thoughtOverlayPath(sessionId);
  mkdirSync(dirname(path), { recursive: true });
  appendFileSync(path, JSON.stringify({ text: trimmed, ts: Date.now() }) + '\\n', 'utf-8');
}

function consumeThoughtOverlay(sessionId: string): string {
  const path = thoughtOverlayPath(sessionId);
  if (!existsSync(path)) return '';
  try {
    const lines = readFileSync(path, 'utf-8').split('\\n').filter(l => l.trim());
    if (lines.length === 0) return '';
    const first = JSON.parse(lines[0]) as { text?: string };
    const rest = lines.slice(1).join('\\n');
    if (rest.trim()) writeFileSync(path, rest + (rest.endsWith('\\n') ? '' : '\\n'), 'utf-8');
    else unlinkSync(path);
    const text = typeof first.text === 'string' ? first.text.trim() : '';
    return text && !isProviderRedactedPlaceholder(text) ? text : '';
  } catch {
    return '';
  }
}

/** Push a single conversation turn (used by Cursor afterAgentThought/afterAgentResponse hooks). */
export async function pushConversationMessage(
  sessionId: string,
  role: 'user' | 'assistant',
  content: string,
  opts: { gitRepo?: string; patchRedacted?: boolean; seq?: number } = {},
): Promise<boolean> {
  const text = content.trim().slice(0, 8000);
  if (!sessionId || !text || isProviderRedactedPlaceholder(text)) return false;

  const seq = typeof opts.seq === 'number'
    ? opts.seq
    : Date.now() % 1_000_000_000;

  // Cloud: send to the API transcript-sync (org from login JWT) instead of the
  // local MCP server.
  if (deployIsCloud()) {
    return syncTranscriptCloud(sessionId, opts.gitRepo || '', [{
      message_index: seq, type: role, content: text, timestamp: new Date().toISOString(),
    }], []);
  }

  const mcpPort = process.env.SYNKRO_MCP_PORT || '18931';
  let mcpToken = '';
  try { mcpToken = readFileSync(join(HOME, '.synkro', '.mcp-jwt'), 'utf-8').trim(); } catch {}
  if (!mcpToken) return false;

  try {
    const body: Record<string, unknown> = {
      session_id: sessionId,
      repo: opts.gitRepo || '',
      messages: [{
        type: role,
        content: text,
        ts: new Date().toISOString(),
        message_index: seq,
        patch_redacted: opts.patchRedacted ?? true,
      }],
    };
    const resp = await fetch('http://127.0.0.1:' + mcpPort + '/api/conversation-sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + mcpToken },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(5000),
    });
    return resp.ok;
  } catch {
    return false;
  }
}

function extractTranscriptEntryText(
  entry: Record<string, unknown>,
  maxLen = 8000,
  sessionId = '',
): string {
  const block = transcriptContentBlock(entry);
  if (block) {
    const type = String(block.type || '');
    if (type === 'tool_use' || type === 'tool_call') return '';
    let text = extractContentBlockText(block);
    if (isProviderRedactedPlaceholder(text) && sessionId) {
      const overlay = consumeThoughtOverlay(sessionId);
      if (overlay) text = overlay;
    }
    return text ? text.slice(0, maxLen) : '';
  }

  const msg = entry.message as Record<string, unknown> | string | undefined;
  const content = (msg && typeof msg === 'object' ? msg.content : undefined) ?? entry.content;
  if (typeof content === 'string') {
    let text = content;
    if (isProviderRedactedPlaceholder(text) && sessionId) {
      const overlay = consumeThoughtOverlay(sessionId);
      if (overlay) text = overlay;
    }
    return text ? text.slice(0, maxLen) : '';
  }
  if (Array.isArray(content)) {
    const text = content.map((c: unknown) => {
      if (typeof c === 'string') return c;
      const b = c as Record<string, unknown>;
      const type = String(b?.type || '');
      if (type === 'tool_use' || type === 'tool_call') return '';
      return extractContentBlockText(b);
    }).filter(Boolean).join('\\n\\n').trim();
    if (!text) return '';
    if (isProviderRedactedPlaceholder(text) && sessionId) {
      const overlay = consumeThoughtOverlay(sessionId);
      if (overlay) return overlay.slice(0, maxLen);
    }
    return text.slice(0, maxLen);
  }
  if (typeof msg === 'string') return msg.slice(0, maxLen);
  return '';
}

/** Offset-tracked ingest of new user/assistant turns into PGLite conversation_messages. */
export async function syncConversationTranscript(
  sessionId: string,
  transcriptPath: string,
  gitRepo = '',
): Promise<{ ingested: number; messages: Array<Record<string, unknown>> }> {
  if (!sessionId || !transcriptPath || !existsSync(transcriptPath)) return { ingested: 0, messages: [] };

  const offsetDir = join(HOME, '.synkro', '.transcript-offsets');
  mkdirSync(offsetDir, { recursive: true });
  const offsetFile = join(offsetDir, sessionId);
  let offset = 0;
  if (existsSync(offsetFile)) {
    try { offset = parseInt(readFileSync(offsetFile, 'utf-8').trim(), 10) || 0; } catch {}
  }

  const raw = readFileSync(transcriptPath, 'utf-8');
  const allLines = raw.split('\\n').filter(l => l.trim());
  const totalLines = allLines.length;
  if (totalLines <= offset) return { ingested: 0, messages: [] };

  let startIdx = offset;
  const delta = totalLines - offset;
  if (delta > 200) startIdx = totalLines - 200;

  const messages: Array<Record<string, unknown>> = [];
  for (let i = startIdx; i < totalLines; i++) {
    try {
      const entry = JSON.parse(allLines[i]) as Record<string, unknown>;
      const kind = transcriptEntryType(entry);
      if (kind !== 'user' && kind !== 'assistant') continue;
      const msgObj = entry.message as Record<string, unknown> | undefined;
      const content = (msgObj && typeof msgObj === 'object' ? msgObj.content : undefined) ?? entry.content;
      const singleBlock = transcriptContentBlock(entry);
      // Tool calls FIRST — a tool-only assistant turn (Edit/Bash/Read, no text) is
      // most of a coding session and must still be captured; skipping on empty text
      // dropped both the turn and its tool calls.
      const blocks = (kind === 'assistant')
        ? (Array.isArray(content) ? content : (singleBlock ? [singleBlock] : []))
        : [];
      const toolCalls = blocks
        .filter((c: unknown) => {
          const b = c as Record<string, unknown>;
          return b?.type === 'tool_use' || b?.type === 'tool_call';
        })
        .map((c: unknown) => {
          const b = c as Record<string, unknown>;
          return {
            name: b.name,
            input: JSON.stringify(b.input || b.arguments || {}).slice(0, 20000),
            id: b.id,
          };
        });
      const text = extractTranscriptEntryText(entry, 8000, sessionId);
      if (!text && toolCalls.length === 0) continue;

      const msg: Record<string, unknown> = {
        message_index: i,
        type: kind,
        content: text || ('↳ ' + toolCalls.map((t: any) => t.name).join(', ')),
        ts: entry.timestamp || null,
      };
      if (kind === 'assistant') {
        if (toolCalls.length > 0) msg.tool_calls = toolCalls;
        // Per-turn usage + model so the session view can show input/output/cache
        // tokens and cost line-by-line (not just the session aggregate).
        if (msgObj && typeof msgObj.model === 'string') msg.model = msgObj.model;
        const u = msgObj && (msgObj.usage as Record<string, unknown> | undefined);
        if (u && typeof u === 'object') {
          msg.usage = {
            input_tokens: Number(u.input_tokens) || 0,
            output_tokens: Number(u.output_tokens) || 0,
            cache_creation_input_tokens: Number(u.cache_creation_input_tokens) || 0,
            cache_read_input_tokens: Number(u.cache_read_input_tokens) || 0,
          };
        }
        if (msgObj && typeof msgObj === 'object') {
          msg.model = msgObj.model || null;
          const u = msgObj.usage as Record<string, unknown> | undefined;
          if (u) {
            msg.usage = {
              input_tokens: u.input_tokens,
              output_tokens: u.output_tokens,
              cache_creation_input_tokens: u.cache_creation_input_tokens,
              cache_read_input_tokens: u.cache_read_input_tokens,
            };
          }
        }
      }
      messages.push(msg);
    } catch {}
  }

  if (messages.length === 0) {
    writeFileSync(offsetFile, String(totalLines), 'utf-8');
    return { ingested: 0, messages: [] };
  }

  // Cloud: ship the tail to the API transcript-sync (org from login JWT). Map the
  // local message shape (ts) to the cloud route's (timestamp).
  if (deployIsCloud()) {
    const cloudMsgs = messages.map((m) => ({
      message_index: m.message_index,
      type: m.type,
      content: m.content,
      model: (m.model as string | null) ?? undefined,
      timestamp: (m.ts as string | null) ?? undefined,
      tool_calls: m.tool_calls,
    }));
    const ok = await syncTranscriptCloud(sessionId, gitRepo, cloudMsgs, []);
    if (ok) writeFileSync(offsetFile, String(totalLines), 'utf-8');
    return { ingested: ok ? messages.length : 0, messages };
  }

  const rawPort = parseInt(process.env.SYNKRO_MCP_PORT || '18931', 10);
  const mcpPort = (rawPort > 0 && rawPort < 65536) ? rawPort : 18931;
  let mcpToken = '';
  try { mcpToken = readFileSync(join(HOME, '.synkro', '.mcp-jwt'), 'utf-8').trim(); } catch {}
  if (!mcpToken) return { ingested: 0, messages };

  try {
    const resp = await fetch('http://127.0.0.1:' + mcpPort + '/api/conversation-sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + mcpToken },
      body: JSON.stringify({ session_id: sessionId, repo: gitRepo, messages }),
      signal: AbortSignal.timeout(5000),
    });
    if (resp.ok) {
      writeFileSync(offsetFile, String(totalLines), 'utf-8');
      const data = await resp.json() as { ingested?: number };
      return { ingested: data.ingested ?? messages.length, messages };
    }
  } catch {}
  return { ingested: 0, messages };
}

export interface TranscriptContext {
  userIntent: string;
  recentUserMessages: string[];
  recentMessages: Array<{ type: string; text: string }>;
  recentActions: Array<{ tool: string; input: string }>;
  sessionSummary: string;
  ccModel: string;
  ccUsage: Record<string, any>;
}

export function extractTranscript(transcriptPath: string | undefined): TranscriptContext {
  const ctx: TranscriptContext = {
    userIntent: '',
    recentUserMessages: [],
    recentMessages: [],
    recentActions: [],
    sessionSummary: '',
    ccModel: '',
    ccUsage: {},
  };

  if (!transcriptPath || !existsSync(transcriptPath)) return ctx;

  try {
    const raw = readFileSync(transcriptPath, 'utf-8');
    const lines = raw.split('\\n').filter(l => l.trim());
    // Take the last 400 lines
    const tail = lines.slice(-400);

    const parsed: any[] = [];
    for (const line of tail) {
      try { parsed.push(JSON.parse(line)); } catch {}
    }

    // Recent user messages (last 5)
    const userMsgs: string[] = [];
    for (const entry of parsed) {
      if (transcriptEntryType(entry) !== 'user') continue;
      const text = extractTranscriptEntryText(entry);
      if (text) userMsgs.push(text);
    }
    ctx.recentUserMessages = userMsgs.slice(-5);
    ctx.userIntent = ctx.recentUserMessages[ctx.recentUserMessages.length - 1] || '';

    // Recent messages (last 10, user + assistant)
    const msgs: Array<{ type: string; text: string }> = [];
    for (const entry of parsed) {
      const kind = transcriptEntryType(entry);
      if (kind !== 'user' && kind !== 'assistant') continue;
      const text = extractTranscriptEntryText(entry, 500);
      if (text) msgs.push({ type: kind, text });
    }
    ctx.recentMessages = msgs.slice(-10);

    // Recent tool calls (last 5)
    const actions: Array<{ tool: string; input: string }> = [];
    for (const entry of parsed) {
      if (transcriptEntryType(entry) !== 'assistant') continue;
      const msg = entry.message;
      const content = msg?.content ?? entry.content;
      if (!Array.isArray(content)) continue;
      for (const block of content) {
        if (block.type !== 'tool_use' && block.type !== 'tool_call') continue;
        actions.push({
          tool: block.name || '',
          input: JSON.stringify(block.input || block.arguments || {}).slice(0, 200),
        });
      }
    }
    ctx.recentActions = actions.slice(-5);

    // Session summary
    for (const entry of parsed) {
      if (entry.type === 'summary' && entry.summary) {
        ctx.sessionSummary = entry.summary;
      }
    }

    // CC model
    const assistantEntries = parsed.filter(e => transcriptEntryType(e) === 'assistant');
    if (assistantEntries.length > 0) {
      const last = assistantEntries[assistantEntries.length - 1];
      ctx.ccModel = last.message?.model || '';
      const usage = last.message?.usage;
      if (usage) {
        ctx.ccUsage = {
          input_tokens: usage.input_tokens,
          output_tokens: usage.output_tokens,
          cache_creation_input_tokens: usage.cache_creation_input_tokens,
          cache_read_input_tokens: usage.cache_read_input_tokens,
        };
      }
    }
  } catch {}

  return ctx;
}

// ─── Last Prompt ───

export function readLastPrompt(sessionId?: string): string {
  try {
    if (sessionId) {
      const perSession = join(SESSIONS_DIR, sessionId + '.last-prompt');
      if (existsSync(perSession)) return readFileSync(perSession, 'utf-8').trim();
    }
    if (!existsSync(LAST_PROMPT_FILE)) return '';
    return readFileSync(LAST_PROMPT_FILE, 'utf-8').trim();
  } catch {
    return '';
  }
}

// ─── Find Nearest Package Dependencies ───

export function findNearestDeps(filePath: string): Record<string, string> {
  let dir = dirname(filePath);
  while (dir !== '/' && dir !== '.') {
    const pkg = join(dir, 'package.json');
    if (existsSync(pkg)) {
      try {
        const data = JSON.parse(readFileSync(pkg, 'utf-8'));
        return { ...(data.dependencies || {}), ...(data.devDependencies || {}) };
      } catch {}
    }
    const parent = dirname(dir);
    if (parent === dir) break;
    dir = parent;
  }
  return {};
}

// ─── Consent Tracking ───

const CONSENT_FILE = join(HOME, '.synkro', '.local-consent');

export function consentGrant(sessionId: string, hash: string): void {
  try {
    const dir = dirname(CONSENT_FILE);
    if (!existsSync(dir)) mkdirSync(dir, { recursive: true });
    const line = sessionId + '\\t' + hash + '\\tactive\\n';
    const { appendFileSync } = require('node:fs');
    appendFileSync(CONSENT_FILE, line, 'utf-8');
  } catch {}
}

export function consentHasActive(sessionId: string, hash: string): boolean {
  try {
    if (!existsSync(CONSENT_FILE)) return false;
    const content = readFileSync(CONSENT_FILE, 'utf-8');
    return content.includes(sessionId + '\\t' + hash + '\\tactive');
  } catch {
    return false;
  }
}

export function consentConsume(sessionId: string, hash: string): void {
  try {
    if (!existsSync(CONSENT_FILE)) return;
    const content = readFileSync(CONSENT_FILE, 'utf-8');
    const target = sessionId + '\\t' + hash + '\\tactive';
    const replacement = sessionId + '\\t' + hash + '\\tconsumed';
    const updated = content.split('\\n').map(l => l === target ? replacement : l).join('\\n');
    writeFileSync(CONSENT_FILE, updated, 'utf-8');
  } catch {}
}

// ─── Crypto Hash ───

export function hashCommand(cmd: string): string {
  const { createHash } = require('node:crypto');
  return createHash('sha256').update(cmd).digest('hex').slice(0, 16);
}

// ─── Edit line delta + transcript usage ───

export function countTextLines(text: string): number {
  if (!text) return 0;
  return text.split('\\n').length;
}

/** Count lines added/removed from a write, search-replace, or multi-edit payload. */
export function countEditLineDelta(source: {
  writeContent?: string;
  /** When set, Write counts net delta vs the file on disk instead of full file length. */
  existingContent?: string;
  old_string?: string;
  new_string?: string;
  edits?: Array<{ old_string?: string; new_string?: string }>;
}): { linesAdded: number; linesRemoved: number } {
  let linesAdded = 0;
  let linesRemoved = 0;
  if (source.writeContent != null && source.writeContent !== '') {
    const newLines = countTextLines(String(source.writeContent));
    if (source.existingContent != null && source.existingContent !== '') {
      const oldLines = countTextLines(String(source.existingContent));
      return {
        linesAdded: Math.max(0, newLines - oldLines),
        linesRemoved: Math.max(0, oldLines - newLines),
      };
    }
    return { linesAdded: newLines, linesRemoved: 0 };
  }
  if (source.old_string != null && source.new_string != null) {
    const oldLines = countTextLines(String(source.old_string));
    const newLines = countTextLines(String(source.new_string));
    return {
      linesAdded: Math.max(0, newLines - oldLines),
      linesRemoved: Math.max(0, oldLines - newLines),
    };
  }
  if (Array.isArray(source.edits)) {
    for (const e of source.edits) {
      if (!e || typeof e !== 'object') continue;
      const oldLines = countTextLines(String(e.old_string || ''));
      const newLines = countTextLines(String(e.new_string || ''));
      linesAdded += Math.max(0, newLines - oldLines);
      linesRemoved += Math.max(0, oldLines - newLines);
    }
  }
  return { linesAdded, linesRemoved };
}

// Build the recorded edit diff (code_change) for the audit trail, same format as
// the routed hook's summarizeEdit: trim the common prefix/suffix between the file
// before the edit and the proposed full content, then emit minus/plus prefixed,
// line-numbered rows (the dashboard CodeChangeCard colors each row by its leading
// sign). Without this the event detail falls back to the raw file/content dump
// instead of an added/removed diff.
export function summarizeEditDiff(baseContent: string, proposed: string): string {
  const EDIT_AUDIT_CAP = 16000;
  const a = baseContent ? baseContent.split('\\n') : [];
  const b = proposed ? proposed.split('\\n') : [];
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) { endA--; endB--; }
  const removed = a.slice(start, endA);
  const added = b.slice(start, endB);
  const out: string[] = [];
  for (let i = 0; i < removed.length; i++) out.push('-' + String(start + 1 + i).padStart(5) + ' ' + removed[i]);
  for (let i = 0; i < added.length; i++) out.push('+' + String(start + 1 + i).padStart(5) + ' ' + added[i]);
  let diff = out.join('\\n');
  if (diff.length > EDIT_AUDIT_CAP) diff = diff.slice(0, EDIT_AUDIT_CAP) + '\\n... [truncated]';
  return diff;
}

/** Normalize model names so the same Cursor session doesn't split across agent buckets. */
export function normalizeCaptureModel(model: string): string {
  const m = (model || '').trim();
  if (!m || m === 'unknown') return 'unknown';
  if (m.startsWith('cursor/') || m.startsWith('claude-') || m.startsWith('gpt-') || m.startsWith('gemini-')) return m;
  if (/^composer/i.test(m) || m === 'auto' || m === 'default') return 'cursor/' + m;
  return m;
}

/** Line metrics for guard_checks — Cursor Edit/StrReplace post deltas via afterFileEdit. */
export function captureLineMetrics(
  agentKind: 'cursor' | 'claude_code',
  toolName: string,
  linesAdded: number,
  linesRemoved: number,
): { linesAdded?: number; linesRemoved?: number } {
  if (agentKind === 'cursor' && toolName !== 'Write') return {};
  return { linesAdded, linesRemoved };
}

function usageTextFromTranscriptEntry(entry: Record<string, unknown>): string {
  const msg = entry.message as Record<string, unknown> | undefined;
  const content = (msg && typeof msg === 'object' ? msg.content : undefined) ?? entry.content;
  if (typeof content === 'string') return content;
  if (content && typeof content === 'object' && !Array.isArray(content)) {
    const block = content as Record<string, unknown>;
    if (block.type === 'text' && typeof block.text === 'string') return block.text;
    if (typeof block.text === 'string') return block.text;
    return '';
  }
  if (!Array.isArray(content)) return '';
  return content.map((c: unknown) => {
    if (typeof c === 'string') return c;
    const b = c as Record<string, unknown>;
    if (b?.type === 'text' && typeof b.text === 'string') return b.text;
    if (typeof b?.text === 'string') return b.text;
    if (b?.type === 'tool_use' || b?.type === 'tool_call') {
      return JSON.stringify(b.input || b.arguments || {});
    }
    return '';
  }).join('\\n');
}

function addUsageBlock(
  result: { model: string; totals: Record<string, number> },
  u: Record<string, unknown> | null | undefined,
): boolean {
  if (!u) return false;
  const inp = Number(u.input_tokens ?? u.inputTokens ?? 0) || 0;
  const out = Number(u.output_tokens ?? u.outputTokens ?? 0) || 0;
  const cw = Number(u.cache_creation_input_tokens ?? u.cacheCreationInputTokens ?? 0) || 0;
  const cr = Number(u.cache_read_input_tokens ?? u.cacheReadInputTokens ?? 0) || 0;
  if (inp + out + cw + cr <= 0) return false;
  result.totals.in += inp;
  result.totals.out += out;
  result.totals.cw += cw;
  result.totals.cr += cr;
  return true;
}

function msgModel(entry: Record<string, unknown>): string {
  const msg = entry.message as Record<string, unknown> | undefined;
  return typeof msg?.model === 'string' ? msg.model : '';
}

/** Sum token usage from CC or Cursor agent-transcript JSONL. */
export function aggregateUsage(
  transcriptPath: string,
  opts?: { modelFallback?: string },
): { model: string; totals: Record<string, number> } {
  const result = { model: opts?.modelFallback || '', totals: { in: 0, out: 0, cw: 0, cr: 0 } };
  if (!transcriptPath || !existsSync(transcriptPath)) return result;
  // CWE-22: only ever read the agent's own transcript, which lives under HOME
  // (~/.claude, ~/.cursor). Never follow an arbitrary external path.
  if (!isPathUnder(transcriptPath, HOME)) return result;

  // INCREMENTAL DELTA: returns ONLY the usage in transcript bytes appended since
  // the last call (a byte offset is tracked per transcript in a sidecar). All three
  // usage hooks (Claude UserPromptSubmit + Stop, Cursor transcript-sync) share that
  // offset, so their deltas never overlap; the DB then INCREMENTS by each delta, so
  // usage climbs per message in both harnesses. We never re-read the whole
  // (100s-of-MB) transcript — the old full read OOM'd bun and dropped the tick.
  const key = (transcriptPath.split('/').pop() || 'session').replace(/[^A-Za-z0-9._-]/g, '_');
  const stateFile = join(SESSIONS_DIR, key + '.usage.json');
  let offset = 0, sawExplicit = false, model = '';
  try {
    const s = JSON.parse(readFileSync(stateFile, 'utf-8'));
    if (s && typeof s.offset === 'number') { offset = s.offset; sawExplicit = !!s.sawExplicit; model = s.model || ''; }
  } catch {}

  let fd = -1;
  try {
    const size = statSync(transcriptPath).size;
    if (size < offset) { offset = 0; sawExplicit = false; } // rotated/truncated
    // Never read more than 16MB in one shot; first sight of a giant existing
    // transcript jumps to the tail rather than OOM on the whole history.
    const MAX_READ = 16 * 1024 * 1024;
    let start = offset, capped = false;
    if (size - start > MAX_READ) { start = size - MAX_READ; capped = true; }
    const len = size - start;
    if (len > 0) {
      fd = openSync(transcriptPath, 'r');
      const buf = Buffer.alloc(len);
      readSync(fd, buf, 0, len, start);
      const lines = buf.toString('utf-8').split('\\n');
      if (capped && lines.length) lines.shift(); // drop the partial first line when we jumped mid-file
      for (const line of lines) {
        if (!line.trim()) continue;
        let entry: Record<string, unknown>;
        try { entry = JSON.parse(line) as Record<string, unknown>; } catch { continue; }
        const msg = entry.message as Record<string, unknown> | undefined;
        const modelInfo = entry.modelInfo as Record<string, unknown> | undefined;
        const m = (typeof modelInfo?.modelName === 'string' && modelInfo.modelName)
          || (typeof modelInfo?.model === 'string' && modelInfo.model)
          || (typeof entry.model === 'string' && entry.model)
          || msgModel(entry);
        if (m) { model = m; result.model = m; }

        const tokenCount = (entry.tokenCount ?? entry.token_count) as Record<string, unknown> | undefined;
        // result.totals is the DELTA for THIS call only (it never carries prior totals).
        const had = addUsageBlock(result, tokenCount)
          || addUsageBlock(result, msg?.usage as Record<string, unknown>)
          || addUsageBlock(result, entry.usage as Record<string, unknown>);
        if (had) { sawExplicit = true; continue; }
        if (sawExplicit) continue;

        // Fallback (Cursor / transcripts without usage blocks): estimate from text.
        const text = usageTextFromTranscriptEntry(entry);
        if (!text) continue;
        const est = Math.ceil(text.length / 4);
        const role = String(entry.role || entry.type || '');
        if (role === 'user') result.totals.in += est;
        else if (role === 'assistant') result.totals.out += est;
      }
    }
    // Advance the offset (delta consumed). Ship is best-effort like the other
    // hooks — a rare dropped tick loses only that one delta, not the running total.
    try { mkdirSync(SESSIONS_DIR, { recursive: true }); writeFileSync(stateFile, JSON.stringify({ offset: size, model, sawExplicit })); } catch {}
  } catch {} finally {
    if (fd >= 0) { try { closeSync(fd); } catch {} }
  }
  if (!result.model) result.model = model || opts?.modelFallback || '';
  return result;
}

/** Emit a usage_tick telemetry row when transcript usage is non-zero. */
export function emitUsageTick(params: {
  sessionId: string;
  usage: { model: string; totals: Record<string, number> };
  hookType: string;
  gitRepo?: string;
  modelFallback?: string;
}): void {
  const { sessionId, usage, hookType, gitRepo, modelFallback } = params;
  if (!sessionId || usage.totals.in + usage.totals.out <= 0) return;
  let model = usage.model || modelFallback || 'unknown';
  if (modelFallback && !usage.model) model = modelFallback;
  if (isCursorHookFormat() && model && !model.startsWith('cursor/') && model !== 'cursor') {
    model = 'cursor/' + model;
  }
  const body = {
    capture_type: 'usage_tick',
    event_id: mintEventId('usage'),
    hook_type: hookType,
    verdict: 'allow',
    severity: 'none',
    session_id: sessionId,
    model,
    cc_model: model,
    cc_usage: {
      input_tokens: usage.totals.in,
      output_tokens: usage.totals.out,
      cache_creation_input_tokens: usage.totals.cw,
      cache_read_input_tokens: usage.totals.cr,
    },
    ...(gitRepo ? { repo: gitRepo } : {}),
  };
  appendLocalTelemetry(body); // local spool → usage_ticks (no-op in cloud mode)
  // Cloud: ship the tick to /hook/capture → usage_ticks too. appendLocalTelemetry
  // is gated to local mode, so without this the cloud usage_ticks table stayed
  // EMPTY — no token/cost data for the Agents page. (/hook/capture is on the
  // long-lived MCP-token allowlist; loadJwt returns that token in cloud.)
  const jwt = loadJwt();
  if (jwt) shipCloud(jwt, '/api/v1/hook/capture', body);
}

export function cursorModelFromPayload(payload: Record<string, unknown>): string {
  const rawModel = String(payload.model ?? payload.model_id ?? '');
  if (!rawModel) return 'cursor';
  return rawModel.startsWith('cursor/') ? rawModel : 'cursor/' + rawModel;
}

// ─── Scan Finding Dispatch ───

export type ScanFindingInput = {
  finding_type: 'cwe' | 'cve';
  finding_id: string;
  severity?: string;
  detail?: string;
  description?: string;
  package_name?: string;
  package_version?: string;
  fixed_version?: string;
  aliases?: string[];
  references?: Array<{ type: string; url: string }>;
  cwe_name?: string;
  code_snippet?: string;
  offending_lines?: string;
};

/** Persist open scan_findings rows for a block and flush the telemetry spool. */
export function emitBlockScanFindings(
  jwt: string,
  captureDepth: string,
  ctx: { session_id: string; file_path: string; repo?: string },
  findings: ScanFindingInput[],
  fallback?: ScanFindingInput,
): void {
  const rows = findings.length > 0 ? findings : (fallback ? [fallback] : []);
  for (const f of rows) {
    dispatchFinding(jwt, {
      session_id: ctx.session_id,
      file_path: ctx.file_path,
      repo: ctx.repo,
      status: 'open',
      // These are BLOCK findings — Synkro denied the action, nothing landed.
      // prevented → recorded as auto-resolved on the server.
      prevented: true,
      ...f,
    }, captureDepth);
  }
  if (rows.length > 0) drainSpool().catch(() => {});
}

/**
 * Derive a 1-based file line range ("42" or "42-45") for a grader-reported
 * offending snippet by locating it verbatim in the reconstructed file. Mirrors
 * the container scanner's locateSnippetLines so cloud + local storage record
 * the same lines. Returns undefined when the snippet can't be located.
 */
export function cweSnippetLines(haystack: string, snippet?: string): string | undefined {
  if (!haystack || !snippet) return undefined;
  const trimmed = snippet.replace(/^\\n+|\\n+$/g, '');
  if (!trimmed) return undefined;
  let idx = haystack.indexOf(trimmed);
  let spanLines = trimmed.split('\\n').length;
  if (idx < 0) {
    const firstLine = trimmed.split('\\n').map((l) => l.trim()).find(Boolean);
    if (!firstLine) return undefined;
    idx = haystack.indexOf(firstLine);
    if (idx < 0) return undefined;
    spanLines = 1;
  }
  const startLine = haystack.slice(0, idx).split('\\n').length;
  const endLine = startLine + spanLines - 1;
  return endLine > startLine ? startLine + '-' + endLine : String(startLine);
}

export function dispatchFinding(
  jwt: string,
  finding: {
    session_id: string;
    file_path: string;
    repo?: string;
    finding_type: 'cwe' | 'cve';
    finding_id: string;
    severity?: string;
    status: 'open' | 'resolved' | 'exempted';
    detail?: string;
    description?: string;
    package_name?: string;
    package_version?: string;
    fixed_version?: string;
    aliases?: string[];
    references?: Array<{ type: string; url: string }>;
    cwe_name?: string;
    code_snippet?: string;
    offending_lines?: string;
    prevented?: boolean;
  },
  captureDepth: string,
): void {
  const localEntry: Record<string, any> = {
    capture_type: 'scan_finding',
    ...finding,
  };
  appendLocalTelemetry(localEntry);

  // Cloud copy carries the full finding; shipCloud gates on storage mode.
  const cloudBody: Record<string, any> = {
    finding_type: finding.finding_type,
    finding_id: finding.finding_id,
    severity: finding.severity,
    status: finding.status,
    session_id: finding.session_id,
    file_path: finding.file_path,
    repo: finding.repo,
    package_name: finding.package_name,
    package_version: finding.package_version,
    fixed_version: finding.fixed_version,
    aliases: finding.aliases,
    references: finding.references,
    cwe_name: finding.cwe_name,
    detail: finding.detail,
    description: finding.description,
    code_snippet: finding.code_snippet,
    offending_lines: finding.offending_lines,
    prevented: finding.prevented,
  };
  shipCloud(jwt, '/api/v1/hook/finding', cloudBody);
}

export function dispatchScanResult(
  jwt: string,
  scan: {
    session_id: string;
    file_path: string;
    scan_type: 'cve' | 'cwe' | 'pkg';
    result: 'pass' | 'block' | 'error';
    finding_count: number;
    finding_ids?: string[];
    severity?: string;
    repo?: string;
  },
): void {
  const localEntry: Record<string, any> = {
    capture_type: 'scan_result',
    event_id: 'scan_' + Date.now() + '_' + process.pid,
    _ts: new Date().toISOString(),
    ...scan,
  };
  appendLocalTelemetry(localEntry);
  shipCloud(jwt, '/api/v1/hook/scan-result', {
    scan_type: scan.scan_type,
    result: scan.result,
    finding_count: scan.finding_count,
    finding_ids: scan.finding_ids,
    severity: scan.severity,
    session_id: scan.session_id,
    file_path: scan.file_path,
    repo: scan.repo,
  });
}

// ─── Hook tool-name sets (CC + Cursor) ───

export const EDIT_TOOL_NAMES = new Set([
  'Edit', 'Write', 'MultiEdit', 'NotebookEdit', 'StrReplace',
  'edit_file', 'reapply', 'edit_notebook', 'ApplyPatch', 'apply_patch',
]);
export const SHELL_TOOL_NAMES = new Set([
  'Bash', 'Shell', 'Read', 'Grep', 'Glob', 'terminal', 'run_terminal_cmd', 'execute_command',
]);
export const AGENT_TOOL_NAMES = new Set(['Agent', 'Task']);
export const PLAN_TOOL_NAMES = new Set(['ExitPlanMode', 'SwitchMode', 'CreatePlan']);

export function isEditTool(toolName: string): boolean {
  return EDIT_TOOL_NAMES.has(toolName);
}
export function isShellTool(toolName: string): boolean {
  return SHELL_TOOL_NAMES.has(toolName);
}
export function isAgentTool(toolName: string): boolean {
  return AGENT_TOOL_NAMES.has(toolName);
}
export function isPlanTool(toolName: string): boolean {
  return PLAN_TOOL_NAMES.has(toolName);
}

export function hookSessionId(payload: Record<string, unknown>): string {
  return String(payload.session_id ?? payload.conversation_id ?? '');
}

export function isCursorHookFormat(): boolean {
  return process.env.SYNKRO_HOOK_FORMAT === 'cursor' || process.argv.includes('--cursor');
}

// Cursor reads CC hooks from ~/.claude/settings.json and fires them alongside
// its own ~/.cursor/hooks.json entries. When that happens, agentKind is
// 'claude_code' but the payload model is non-Claude (e.g. gpt-5.5).
// Return true so the CC hook can bail out and let Cursor's hooks handle it.
export function isCursorInvokingCcHook(agentKind: string, model: string): boolean {
  if (agentKind === 'cursor') return false;
  if (!model || model === 'unknown' || model === '') return false;
  return !model.startsWith('claude-');
}

let cursorHookExited = false;
let hookFinalized = false;

export function setupCursorHookSignals(): void {
  if (!isCursorHookFormat()) return;
  process.on('SIGTERM', () => outputEmpty());
}

function cursorHookExit(): never {
  cursorHookExited = true;
  hookFinalized = true;
  process.exit(0);
}

// Self-imposed deadline. CC/Cursor kill the hook process at the configured hook
// timeout with a signal; if a grade fetch is still in flight when that happens,
// the runtime dumps the in-flight request body (the grader prompt) to stderr and
// exits non-zero — surfacing as a "hook error" toast with leaked prompt text.
// To prevent that, each grading hook arms this watchdog a few seconds UNDER its
// configured budget: when it fires we emit a clean empty result and exit 0
// ourselves, so the harness never has to kill us. The timer is unref'd, so a
// hook that finishes early exits normally and the watchdog never runs.
export function installHookWatchdog(budgetMs: number): void {
  const t = setTimeout(() => {
    if (hookFinalized) return;
    hookFinalized = true;
    try { process.stdout.write('{}\\n'); } catch {}
    process.exit(0);
  }, budgetMs);
  if (typeof (t as any).unref === 'function') (t as any).unref();
}

// ─── Grader-unavailable diagnostic log ───
// Records every time a hook tried to call the local grader and fell open
// because the call failed. JSONL at ~/.synkro/grader-unavailable.log so the
// user can pinpoint cause (timeout vs ECONNREFUSED vs HTTP 5xx vs sick pool)
// instead of guessing from a one-shot system message in the CC UI.

const UNAVAIL_LOG = join(HOME, '.synkro', 'grader-unavailable.log');

export function isGraderNotConfigured(errorMessage: string): boolean {
  const lower = errorMessage.toLowerCase();
  return lower.includes('no worker') || lower.includes('not configured') || lower.includes('agent_kind') || lower.includes('no pool');
}

// Categorize a grader failure into a stable machine category + a human "why"
// (with a fix hint). Shared by the terminal message and the diagnostic log so
// the two never disagree about the reason.
export function classifyGraderError(errorMessage: string): { category: string; why: string } {
  const e = (errorMessage || '').toLowerCase();
  const snippet = (errorMessage || '').replace(/\\s+/g, ' ').trim().slice(0, 160);
  if (errorMessage === 'SYNKRO_CHANNEL_DOWN') {
    return { category: 'channel_down', why: 'grader container not running (run \`synkro status\`)' };
  }
  if (isGraderNotConfigured(errorMessage)) {
    return { category: 'not_configured', why: 'grader not configured for this agent — add it to synkro.toml and run \`synkro install\`' };
  }
  if (/tim(e|ed)\\s*out|aborted|operation was aborted/.test(e)) {
    return { category: 'timeout', why: 'grade timed out (45s) — grader cold/slow, or its Claude token is invalid (re-run \`synkro install\`)' };
  }
  if (/\\b401\\b|unauthorized|token expired|invalid token|\\b403\\b|forbidden/.test(e)) {
    return { category: 'auth', why: 'auth rejected — session token expired (re-run \`synkro login\`, or \`synkro install\` if the grader Claude token is stale)' };
  }
  if (/econnrefused|enotfound|eai_again|fetch failed|socket|econnreset|getaddrinfo|network/.test(e)) {
    return { category: 'unreachable', why: 'grader unreachable — network or container down' };
  }
  if (/\\b5\\d\\d\\b/.test(e)) {
    return { category: 'server_error', why: 'grader server error: ' + snippet };
  }
  return { category: 'unknown', why: snippet || 'unknown error' };
}

export function graderUnavailableMessage(hook: string, target: string, errorMessage: string, agentKind: AgentKind = 'claude_code'): string {
  const base = hook + ' ' + target + ' → ';
  if (isGraderNotConfigured(errorMessage)) {
    const agent = agentKind === 'cursor' ? 'Cursor' : 'Claude Code';
    return base + 'local grader not configured for ' + agent + '. Add this agent to your synkro.toml file and run \`synkro install\`.';
  }
  // Always surface WHY (not just "unavailable"), categorized, with a fix hint —
  // the full raw error + the exact graded prompt are in grader-unavailable.log.
  const where = process.env.SYNKRO_DEPLOY_LOCATION === 'cloud' ? 'cloud grader' : 'local grader';
  const { why } = classifyGraderError(errorMessage);
  return base + where + ' unavailable — ' + why + '; skipped (full detail: ~/.synkro/grader-unavailable.log)';
}

// Diagnostic record for a skipped grade — the categorized reason AND the EXACT
// prompt that was sent for inference (gradeInput), so you can see WHAT was being
// graded when it failed. Routing differs by mode:
//   • CLOUD → ship to Timescale via /api/v1/cli/grader-unavailable (central, so
//     we can debug customer failures), enriched on timeout/outage with the
//     container's own claude error (option B). NOT written to the user's machine.
//   • LOCAL → the on-device ~/.synkro/grader-unavailable.log file.
// Fire-and-forget in cloud mode: callers don't await — the pending fetch keeps
// the hook process alive until it settles (or the watchdog ends the run).
export async function logGraderUnavailable(
  hook: string, target: string, errorMessage: string,
  gradeInput?: string, ctx?: { sessionId?: string; repo?: string },
): Promise<void> {
  const { category, why } = classifyGraderError(errorMessage);
  // Redact any secret from the diagnostic fields before they are persisted
  // (Timescale in cloud, on-device log in local). The grade input especially can
  // carry a credential (a .env edit, a bash command) — debug diagnostics must
  // never store a raw secret. classifyGraderError ran on the original above.
  target = scrubSecrets(target || '');
  errorMessage = scrubSecrets(errorMessage || '');
  gradeInput = gradeInput ? scrubSecrets(gradeInput) : gradeInput;

  if (process.env.SYNKRO_DEPLOY_LOCATION === 'cloud') {
    try {
      const jwt = loadJwt();
      if (!jwt) return;
      // Only chase the container's internal reason for failures where it adds
      // signal (a timeout/outage hides the real claude error); skip it for fast,
      // already-explanatory failures like auth.
      let sickReason: string | null = null;
      if (category === 'timeout' || category === 'unreachable' || category === 'server_error') {
        sickReason = await fetchContainerSickReason(jwt);
      }
      await fetch(GATEWAY_URL + '/api/v1/cli/grader-unavailable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + jwt },
        body: JSON.stringify({
          hook, target, category, why,
          error: (errorMessage || '').slice(0, 2000),
          grade_input: gradeInput ? String(gradeInput).slice(0, 20000) : null,
          sick_reason: sickReason,
          session_id: ctx?.sessionId || null,
          repo: ctx?.repo || null,
        }),
        signal: AbortSignal.timeout(3000),
      }).catch(() => {});
    } catch {
      // best-effort — never let diagnostics cascade into a hook failure
    }
    return;
  }

  // Local mode: on-device diagnostic log.
  try {
    const entry: Record<string, unknown> = {
      ts: new Date().toISOString(), mode: 'local',
      hook, target, category, why,
      error: (errorMessage || '').slice(0, 1000),
    };
    if (gradeInput) entry.gradeInput = String(gradeInput).slice(0, 20000);
    appendFileSync(UNAVAIL_LOG, JSON.stringify(entry) + '\\n', 'utf-8');
  } catch {
    // best-effort — never let logging failure cascade into a hook failure
  }
}

// ─── Output Helpers ───

export function outputJson(obj: any): void {
  if (isCursorHookFormat()) {
    if (obj?.permission === 'allow') {
      const u = typeof obj.user_message === 'string' ? obj.user_message : '';
      const a = typeof obj.agent_message === 'string' ? obj.agent_message : u;
      if (u || a) {
        if (!cursorHookExited) {
          cursorHookExited = true;
          process.stdout.write(JSON.stringify({ permission: 'allow' }) + '\\n');
        }
        cursorHookExit();
      }
    }
    const hso = obj?.hookSpecificOutput;
    const sys = typeof obj?.systemMessage === 'string' ? obj.systemMessage : '';
    if (hso?.permissionDecision === 'deny') {
      const reason = hso.permissionDecisionReason || hso.additionalContext || sys;
      if (!cursorHookExited) {
        cursorHookExited = true;
        process.stdout.write(JSON.stringify({
          permission: 'deny',
          user_message: sys || reason,
          agent_message: hso.additionalContext || reason,
        }) + '\\n');
      }
      cursorHookExit();
    }
    const addCtx = typeof hso?.additionalContext === 'string' ? hso.additionalContext : '';
    const ctx = sys || addCtx;
    if (ctx) {
      if (!cursorHookExited) {
        cursorHookExited = true;
        process.stdout.write(JSON.stringify({ permission: 'allow' }) + '\\n');
      }
      cursorHookExit();
    }
    outputEmpty();
    return;
  }
  if (hookFinalized) return;
  hookFinalized = true;
  console.log(JSON.stringify(obj));
}

export function outputEmpty(): void {
  if (isCursorHookFormat()) {
    if (!cursorHookExited) {
      cursorHookExited = true;
      try { process.stdout.write('{}\\n'); } catch {}
    }
    cursorHookExit();
  }
  if (hookFinalized) return;
  hookFinalized = true;
  console.log('{}');
}
`;


// ─── CC PreToolUse Edit/Write/MultiEdit/NotebookEdit pre-check (TypeScript) ───

export const EDIT_PRECHECK_TS = `#!/usr/bin/env bun
import {
  loadJwt, ensureFreshJwt, detectRepo, loadConfig, route, tag, localGrade, cloudTaskDrift,
  parseVerdict, parseCombinedVerdict, combinedEditGradeEnabled, dispatchCapture, dispatchFinding, ruleMode, reconstructContent, editSecretDenial, isPathUnder, postWithRetry,
  readStdin, extractTranscript, readLastPrompt, findNearestDeps, filePathFromToolInput,
  appendSessionAction, readSessionLog, compressSessionLog, sessionLogForGrade, log,
  outputJson, outputEmpty, setupCursorHookSignals, installHookWatchdog, isEditTool, hookSessionId, GATEWAY_URL,
  logGraderUnavailable, graderUnavailableMessage, filterRules, ruleFilterText, normalizeMode, countEditLineDelta, summarizeEditDiff,
  captureLineMetrics, cursorModelFromPayload, resolveTranscriptPath, isCursorInvokingCcHook,
  loadSynkroFile, effectiveGraderPool, synkroFilePresent, noSynkroSkipMessage,
  type HookConfig, type Rule,
} from './_synkro-common.ts';
import { existsSync, readFileSync } from 'node:fs';
import { basename, join } from 'node:path';

const agentKind = (process.env.SYNKRO_HOOK_FORMAT === 'cursor' || process.argv.includes('--cursor')) ? 'cursor' : 'claude_code';

async function main() {
  setupCursorHookSignals();
  installHookWatchdog(48000);
  try {
    const input = await readStdin();
    if (!input.trim()) { outputEmpty(); return; }

    const payload = JSON.parse(input);
    const toolName = payload.tool_name || '';
    if (!isEditTool(toolName)) {
      outputEmpty();
      return;
    }

    const toolInput = payload.tool_input || {};
    const sessionId = hookSessionId(payload);
    const toolUseId = payload.tool_use_id || '';
    const workspaceRoots = Array.isArray(payload.workspace_roots) ? payload.workspace_roots as string[] : [];
    const cwd = payload.cwd || workspaceRoots[0] || '';
    const permissionMode = payload.permission_mode || '';
    const transcriptPath = resolveTranscriptPath(payload);

    const filePath = filePathFromToolInput(toolInput);
    if (!filePath) { outputEmpty(); return; }

    if (filePath.includes('/.synkro/hooks/')) { outputEmpty(); return; }

    if (!synkroFilePresent(cwd)) {
      const _skipMsg = noSynkroSkipMessage(sessionId);
      if (_skipMsg) outputJson({ systemMessage: _skipMsg, hookSpecificOutput: { hookEventName: 'PreToolUse', additionalContext: _skipMsg } });
      else outputEmpty();
      return;
    }

    const fileShort = basename(filePath);
    log('editGuard checking: ' + fileShort);

    appendSessionAction(sessionId, { ts: new Date().toISOString(), tool: toolName || 'Edit', summary: 'editing ' + fileShort, file: filePath });

    const gitRepo = detectRepo(cwd, transcriptPath, filePath, workspaceRoots);

    let jwt = loadJwt();
    if (!jwt) { outputEmpty(); return; }
    jwt = await ensureFreshJwt(jwt);

    // Reconstruct proposed content
    const proposed = reconstructContent(toolName, toolInput, filePath, cwd);
    if (!proposed) { outputEmpty(); return; }

    // Build diff field
    let diffField: any = null;
    if (toolInput.old_string != null || toolInput.new_string != null || toolInput.edits != null) {
      diffField = {};
      if (toolInput.old_string != null) diffField.old_string = toolInput.old_string;
      if (toolInput.new_string != null) diffField.new_string = toolInput.new_string;
      if (toolInput.edits != null) diffField.edits = toolInput.edits;
    }

    const fullPath = filePath.startsWith('/') ? filePath : (cwd ? join(cwd, filePath) : filePath);
    let existingContent = '';
    if (toolName === 'Write' && existsSync(fullPath)) {
      try { existingContent = readFileSync(fullPath, 'utf-8'); } catch {}
    }

    // Compute lines added/removed (Write = net delta vs file on disk)
    const { linesAdded, linesRemoved } = countEditLineDelta(
      toolName === 'Write'
        ? {
            writeContent: toolInput.content || toolInput.contents || '',
            existingContent,
          }
        : {
            old_string: toolInput.old_string,
            new_string: toolInput.new_string,
            edits: Array.isArray(toolInput.edits) ? toolInput.edits : undefined,
          },
    );
    const lineMetrics = captureLineMetrics(agentKind, toolName, linesAdded, linesRemoved);

    // Read file before edit for cloud payload
    let fileBefore = '';
    if (toolName !== 'Write' && filePath && isPathUnder(filePath, cwd || '.') && existsSync(fullPath)) {
      try { fileBefore = readFileSync(fullPath, 'utf-8').slice(0, 65536); } catch {}
    }

    // Audit diff (code_change): added/removed lines for the event detail's Code Change
    // card. Base = the file before this edit (Write → file on disk if any). Captured on
    // every edit verdict below so cloud-graded events show the diff, not a raw dump.
    const editDiff = summarizeEditDiff(toolName === 'Write' ? existingContent : fileBefore, proposed);

    // Extract transcript context
    const transcript = extractTranscript(transcriptPath);
    const lastPrompt = readLastPrompt(sessionId);

    const captureModel = agentKind === 'cursor'
      ? cursorModelFromPayload(payload)
      : (transcript.ccModel || String(payload.model ?? payload.model_id ?? ''));

    if (isCursorInvokingCcHook(agentKind, captureModel)) { outputEmpty(); return; }

    // Model detection: prefer transcript (CC), fall back to payload (Cursor)
    if (!transcript.ccModel) {
      transcript.ccModel = captureModel;
    }

    // Load config and decide route
    const config = await loadConfig(jwt);
    const synkroFile = loadSynkroFile(cwd);
    const graderPool = effectiveGraderPool(synkroFile, agentKind, config);
    const rt = await route(config, synkroFile);
    const tagStr = tag(rt, config, graderPool);

    if (config.silent) {
      outputJson({ systemMessage: tagStr + ' editGuard → skipped (silent mode)' });
      return;
    }

    // Secret firewall: block an edit that HARDCODES a literal credential before it
    // reaches the grader/model. Flags only the NEW content (never a pre-existing
    // secret already in the file).
    {
      const _den = editSecretDenial(toolName, toolInput, fileShort, tagStr);
      if (_den) { outputJson(_den); return; }
    }

    if (rt === 'local') {
      // ─── Local grading: org rules ONLY (channel 1, port 18929) ───
      // CAP COUPLING: 4000 chars keeps the grade payload small enough to finish
      // well inside the timing budget (hook abort 45s < channel 120s < dispatch
      // 125s). Raising this materially grows per-grade inference time and, under
      // concurrency, pushes the latency tail toward those ceilings — so if you
      // raise it, revisit WORKER_DISPATCH_TIMEOUT_MS / the channel timeout first.
      const proposedShort = proposed.slice(0, 4000);
      // The TASK grade sees the DIFF for large files, so a contradiction or drift DEEP
      // in a big file is visible — the old "first 4KB of the whole file" window missed
      // any change past ~2KB. Small files keep full content (cleaner for the grader);
      // editDiff is the already-computed changed-lines summary (- old / + new).
      const taskGradeContent = (proposed.length <= 3500 || !editDiff)
        ? proposedShort
        : ('Changed lines of ' + filePath + ' (- old / + new):\\n' + editDiff).slice(0, 4000);
      const sessionLog = await sessionLogForGrade(sessionId, config.rules);
      const graderContent = 'file=' + filePath + ' content=' + proposedShort;
      const relevantRules = await filterRules(
        ruleFilterText(graderContent, transcript.userIntent || lastPrompt),
        config.rules,
      );
      const buildGraderPrompt = (contentWindow: string) => [
        'Working directory: ' + (cwd || '.'),
        'Repo: ' + (gitRepo || 'unknown'),
        sessionLog,
        'File: ' + filePath,
        'Proposed content:',
        contentWindow,
        'User intent (last human message): ' + (transcript.userIntent || 'none stated'),
        'Last user prompt: ' + (lastPrompt || 'none'),
        'Org rules: ' + JSON.stringify(relevantRules),
      ].join('\\n');
      const graderPrompt = buildGraderPrompt(proposedShort);

      // Cloud task-adherence (additive, informational): fire the task-only grade in
      // PARALLEL with the rule grade so it adds no latency, then merge its progress
      // message into the pass output. Fail-silent ('' on any issue). Sent as a
      // synthetic Write of the reconstructed content so the container needs no base
      // bytes. cwd = the repo identifier the container keys active_tasks on.
      const taskDriftP = process.env.SYNKRO_DEPLOY_LOCATION === 'cloud'
        ? cloudTaskDrift({ payload: { tool_name: 'Write', tool_input: { file_path: filePath, content: taskGradeContent } }, harness: agentKind, cwd: gitRepo, sessionId }, jwt)
        : Promise.resolve({ systemMessage: '', additionalContext: '', decision: 'allow' as const });

      // ─── Combined org-rules + CWE in ONE inference (SYNKRO_COMBINED_EDIT_GRADE) ───
      // Self-contained early-return branch — the default two-grade path below is
      // left untouched. Appends the CWE rule set to the rules prompt, runs one
      // grade-edit-cwe call, then splits the verdict: org-rule violations drive
      // the same ask/fix enforcement; cwe-### violations drive a fix-mode block.
      // The cwe-precheck hook no-ops when this flag is on (see CWE_PRECHECK_TS).
      if (combinedEditGradeEnabled(synkroFile)) {
        const fileExt = (filePath.match(/\\.([^.\\/]+)$/) || ['', ''])[1].toLowerCase();
        let cweRules: any[] = [];
        try {
          const cr = await fetch(GATEWAY_URL + '/api/v1/cwe-rules?ext=' + encodeURIComponent(fileExt), {
            headers: { Authorization: 'Bearer ' + jwt },
            signal: AbortSignal.timeout(4000),
          });
          if (cr.ok) cweRules = ((await cr.json() as any) || {}).rules || [];
        } catch { /* CWE rules optional — rule grading still runs in the combined pass */ }

        const cweSuffix = '\\n\\nCWE rules to ALSO check the proposed content against (emit cwe-### violations with a <code_snippet>): ' + JSON.stringify(cweRules);
        const buildCombined = (win: string) => buildGraderPrompt(win) + cweSuffix;

        // Large-file strategy — mirrors local cweScanner.ts: rather than one giant
        // single inference (which blows the 45s cloud budget AND truncated to the
        // first 4000 chars, missing changes deeper in the file), split content over
        // the threshold into TWO overlapping halves and grade them IN PARALLEL.
        // Each half is ~half the size → faster → fits the timeout; the whole file
        // is covered; findings are merged.
        const SPLIT_THRESHOLD = 4000, OVERLAP = 500;
        let cResponses: string[];
        try {
          if (proposed.length > SPLIT_THRESHOLD) {
            const mid = Math.floor(proposed.length / 2);
            cResponses = await Promise.all([
              localGrade('edit-cwe', buildCombined(proposed.slice(0, mid + OVERLAP)), undefined, graderPool),
              localGrade('edit-cwe', buildCombined(proposed.slice(mid - OVERLAP)), undefined, graderPool),
            ]);
          } else {
            cResponses = [await localGrade('edit-cwe', buildCombined(proposed), undefined, graderPool)];
          }
        } catch (err) {
          const errMsg = (err as Error).message || String(err);
          logGraderUnavailable('editGuard', fileShort, errMsg, buildCombined(proposed), { sessionId, repo: gitRepo });
          outputJson({ systemMessage: tagStr + ' ' + graderUnavailableMessage('editGuard', fileShort, errMsg, graderPool) });
          return;
        }

        // Merge across halves: a rule violation in EITHER half blocks (first one
        // wins, keeping its reason/severity); CWE findings are unioned by id.
        let ruleVerdict: any = null;
        const cweMap = new Map<string, any>();
        for (const r of cResponses) {
          const parsed = parseCombinedVerdict(r);
          if (!ruleVerdict) ruleVerdict = parsed.ruleVerdict;
          else if (!parsed.ruleVerdict.ok && ruleVerdict.ok) ruleVerdict = parsed.ruleVerdict;
          for (const f of parsed.cweFindings) if (f.id && !cweMap.has(f.id)) cweMap.set(f.id, f);
        }
        if (!ruleVerdict) ruleVerdict = { ok: true };
        const cweFindings = [...cweMap.values()];
        const editContent = 'file=' + filePath + ' content=' + proposed.slice(0, 2000);
        const violatedRules = ruleVerdict.ruleId ? [ruleVerdict.ruleId] : [];
        // Include EVERY flagged CWE's description (not a 5-cap) so the agent can fix
        // all of them — even 30 in one edit. Per-finding fallback: reason -> CWE name.
        const cweClean = (s) => String(s == null ? '' : s).split('').map(c => { const k = c.charCodeAt(0); return (k < 32 || k === 127) ? ' ' : c; }).join('').replace(/  +/g, ' ').trim().slice(0, 400);
        const cweId = (s) => String(s == null ? 'CWE-UNKNOWN' : s).replace(/[^A-Za-z0-9_-]/g, '').slice(0, 20) || 'CWE-UNKNOWN';
        // One sentence each (trim at first . ! or ?) so several CWEs in one edit stay tight.
        const cweOne = (s) => { const t = cweClean(s); const i = t.search(/[.!?]( [A-Z]|$)/); return (i >= 0 ? t.slice(0, i + 1) : t).slice(0, 220); };
        // One line per CWE: explanation + Fix. Drop the Fix when it just restates the
        // explanation so a single CWE never double-prints the same prose.
        const cweBlock = cweFindings
          .map(f => { const why = cweOne(f.reason || f.name || 'code weakness'); const fix = f.suggestedFix ? cweOne(f.suggestedFix) : ''; const showFix = fix && why.toLowerCase().indexOf(fix.slice(0, 40).toLowerCase()) === -1; return '[' + cweId(f.id) + '] ' + why + (showFix ? ' Fix: ' + fix : ''); })
          .join('\\n');
        for (const f of cweFindings) {
          dispatchFinding(jwt, { session_id: sessionId, file_path: filePath, finding_type: 'cwe', finding_id: f.id, status: 'open' }, config.captureDepth);
        }

        // Resolve the parallel task grade NOW so a CONTRADICTION folds into the rule/CWE
        // block too — the agent gets rules + CWE + task drift in ONE deny instead of
        // discovering the contradiction a retry later. taskDriftP has been running in
        // parallel, so this rarely adds wall-clock. Only a contradiction (decision==='deny')
        // is folded into a block; progress/off-spec messages stay on the pass path below.
        const cTask = await taskDriftP;
        const cTaskSys = cTask.systemMessage;
        const taskDeny = cTask.decision === 'deny' ? (cTask.additionalContext || cTaskSys) : '';

        // Org-rule violation → ask/fix enforcement (CWE findings appended to the deny).
        if (!ruleVerdict.ok) {
          const mode = normalizeMode(ruleVerdict.ruleMode || ruleMode(ruleVerdict.ruleId, config.rules));
          const guardReason = (ruleVerdict.ruleId ? '(' + ruleVerdict.ruleId + ') ' : '') + (ruleVerdict.reason || 'policy violation');
          let denyReason = mode === 'fix'
            ? 'Guard: ' + guardReason + '\\nFix all issues before retrying. Do NOT ask the user to make the edit manually — resolve the violation in code yourself.'
            : 'Guard: ' + guardReason + '\\nAsk the user for explicit consent before retrying.';
          if (cweBlock) denyReason += '\\nAlso resolve these CWE weaknesses in code:\\n' + cweBlock;
          if (taskDeny) denyReason += '\\nThis edit ALSO contradicts the active task — fix that too:\\n' + taskDeny;
          dispatchCapture(jwt, 'edit', 'block', ruleVerdict.severity || 'critical', ruleVerdict.category || 'security',
            toolName, gitRepo, sessionId, config.captureDepth, {
              command: editContent, reasoning: guardReason,
              rulesChecked: relevantRules, violatedRules,
              ccModel: captureModel, codeChange: editDiff, ...lineMetrics,
            });
          outputJson({
            systemMessage: tagStr + ' editGuard ' + fileShort + ' → blocked: ' + guardReason + (cweFindings.length ? ' (+' + cweFindings.length + ' CWE)' : '') + (taskDeny ? ' (+task)' : ''),
            hookSpecificOutput: { hookEventName: 'PreToolUse', permissionDecision: 'deny', permissionDecisionReason: denyReason, additionalContext: denyReason },
          });
          return;
        }

        // Rules clean but CWE weakness(es) → fix-mode block (resolve in code; task drift
        // folded in too if this edit also contradicts the active task).
        if (cweFindings.length > 0) {
          let ctx = 'CWE: ' + cweBlock + '\\nFix all issues before retrying. Do NOT ask the user to make the edit manually — resolve the weakness in code yourself.';
          if (taskDeny) ctx += '\\nThis edit ALSO contradicts the active task — fix that too:\\n' + taskDeny;
          outputJson({
            systemMessage: tagStr + ' editGuard ' + fileShort + ' → blocked (' + cweFindings.map(f => f.id).join(', ') + ')' + (taskDeny ? ' (+task)' : ''),
            hookSpecificOutput: { hookEventName: 'PreToolUse', permissionDecision: 'deny', permissionDecisionReason: ctx, additionalContext: ctx },
          });
          return;
        }

        // Both clean.
        dispatchCapture(jwt, 'edit', 'pass', 'clean', ruleVerdict.category || 'trivial_edit',
          toolName, gitRepo, sessionId, config.captureDepth, {
            command: editContent, reasoning: ruleVerdict.reason || 'no violations (rules + CWE)',
            rulesChecked: relevantRules, violatedRules: [],
            ccModel: captureModel, codeChange: editDiff, ...lineMetrics,
          });
        const cPassLine = tagStr + ' editGuard ' + fileShort + ' → pass: ' + (ruleVerdict.reason || 'clean (rules + CWE)');
        if (cTask.decision === 'deny') {
          // Cloud parity with local: a task CONTRADICTION hard-blocks the edit and
          // tells the AGENT (additionalContext) to fix it before proceeding.
          outputJson({ systemMessage: cPassLine + (cTaskSys ? '\\n' + cTaskSys : ''), hookSpecificOutput: { hookEventName: 'PreToolUse', permissionDecision: 'deny', permissionDecisionReason: cTask.additionalContext || cTaskSys, additionalContext: cTask.additionalContext || cTaskSys } });
        } else {
          // On-track / off-spec: pass, but pipe any task additionalContext to the AGENT
          // (not just systemMessage to the user) so drift/regression actually steers.
          outputJson({ systemMessage: cPassLine + (cTaskSys ? '\\n' + cTaskSys : ''), hookSpecificOutput: { hookEventName: 'PreToolUse', additionalContext: 'Synkro combined edit judge (rules + CWE). ' + (ruleVerdict.reason || 'clean') + (cTask.additionalContext ? '\\n[task] ' + cTask.additionalContext : '') } });
        }
        return;
      }

      let gradeResp: string;
      try {
        gradeResp = await localGrade('edit', graderPrompt, undefined, graderPool);
      } catch (err) {
        const errMsg = (err as Error).message || String(err);
        logGraderUnavailable('editGuard', fileShort, errMsg, graderPrompt, { sessionId, repo: gitRepo });
        outputJson({ systemMessage: tagStr + ' ' + graderUnavailableMessage('editGuard', fileShort, errMsg, graderPool) });
        return;
      }

      const verdict = parseVerdict(gradeResp);
      const editContent = 'file=' + filePath + ' content=' + proposed.slice(0, 2000);
      const violatedRules = verdict.ruleId ? [verdict.ruleId] : [];

      if (!verdict.ok) {
        const mode = normalizeMode(verdict.ruleMode || ruleMode(verdict.ruleId, config.rules));
        const guardReason = (verdict.ruleId ? '(' + verdict.ruleId + ') ' : '') + (verdict.reason || 'policy violation');

        // fix mode: the grader still produces a fix, but the MODEL applies it (no
        // human poll) — surface our suggested fix so the agent picks it up rather
        // than re-deriving. ask mode: no fix in the deny text; the poll directive
        // (below) lets the HUMAN choose among candidates.
        const denyReason = mode === 'fix'
          ? 'Guard: ' + guardReason + (verdict.suggestedFix ? '\\nRequired fix (apply this exactly): ' + verdict.suggestedFix : '') + '\\nFix all issues before retrying. Do NOT ask the user to make the edit manually — resolve the violation in code yourself.'
          : 'Guard: ' + guardReason + '\\nAsk the user for explicit consent before retrying.';
        dispatchCapture(jwt, 'edit', 'block', verdict.severity || 'critical', verdict.category || 'security',
          toolName, gitRepo, sessionId, config.captureDepth, {
            command: editContent, reasoning: guardReason,
            rulesChecked: relevantRules, violatedRules,
            ccModel: captureModel, codeChange: editDiff, ...lineMetrics,
          });
        // Ask-mode only: turn this flag into a human-preference label (additive; '' on any failure).
        let pollDirective = '';
        if (mode !== 'fix' && verdict.suggestedFix) {
          pollDirective = await buildFixPollDirective({
            candA: verdict.suggestedFix, code: proposed, ruleId: verdict.ruleId,
            sessionId, filePath, model: captureModel, graderPrompt, graderPool,
          });
        }
        outputJson({
          systemMessage: tagStr + ' editGuard ' + fileShort + ' → blocked: ' + guardReason,
          hookSpecificOutput: { hookEventName: 'PreToolUse', permissionDecision: 'deny', permissionDecisionReason: denyReason, additionalContext: denyReason + pollDirective },
        });
        return;
      }

      // Clean
      dispatchCapture(jwt, 'edit', 'pass', 'clean', verdict.category || 'trivial_edit',
        toolName, gitRepo, sessionId, config.captureDepth, {
          command: editContent, reasoning: verdict.reason || 'no policy violations detected',
          rulesChecked: relevantRules, violatedRules: [],
          ccModel: captureModel, codeChange: editDiff, ...lineMetrics,
        });
      const passLine = tagStr + ' editGuard ' + fileShort + ' → pass: ' + (verdict.reason || 'no policy violations detected');
      const rTask = await taskDriftP;
      const rTaskSys = rTask.systemMessage;
      if (rTask.decision === 'deny') {
        outputJson({ systemMessage: passLine + (rTaskSys ? '\\n' + rTaskSys : ''), hookSpecificOutput: { hookEventName: 'PreToolUse', permissionDecision: 'deny', permissionDecisionReason: rTask.additionalContext || rTaskSys, additionalContext: rTask.additionalContext || rTaskSys } });
      } else {
        outputJson({ systemMessage: passLine + (rTaskSys ? '\\n' + rTaskSys : ''), hookSpecificOutput: { hookEventName: 'PreToolUse', additionalContext: 'Synkro local edit judge. ' + (verdict.reason || 'no policy violations detected') + (rTask.additionalContext ? '\\n[task] ' + rTask.additionalContext : '') } });
      }
      return;
    }

    // ─── Cloud grading ───
    const deps = findNearestDeps(filePath);
    const isHeadless = ['acceptEdits', 'bypassPermissions', 'plan', 'auto'].includes(permissionMode)
      || process.env.SYNKRO_HEADLESS === '1';

    const body = {
      hook_event: 'PreToolUse',
      tool_name: toolName,
      tool_input: toolInput,
      file_path: filePath,
      content: proposed,
      file_before: fileBefore || null,
      diff: diffField,
      dependencies: deps,
      user_intent: transcript.userIntent || null,
      last_user_message: lastPrompt || null,
      recent_user_messages: transcript.recentUserMessages,
      recent_messages: transcript.recentMessages,
      recent_actions: transcript.recentActions,
      session_history: await sessionLogForGrade(sessionId, config.rules),
      session_id: sessionId || null,
      tool_use_id: toolUseId || null,
      cwd: cwd || null,
      repo: gitRepo || null,
      permission_mode: permissionMode || null,
      headless: isHeadless,
    };

    const resp = await postWithRetry(GATEWAY_URL + '/api/v1/hook/judge', body, jwt, 8000);

    if (!resp) {
      log('editGuard ' + fileShort + ' → error (timeout)');
      outputEmpty();
      return;
    }

    if (!resp.hook_response || typeof resp.hook_response !== 'object') {
      log('editGuard ' + fileShort + ' → pass (no hook_response)');
      outputEmpty();
      return;
    }

    const hookResp = resp.hook_response;
    const decision = hookResp?.hookSpecificOutput?.permissionDecision;

    if (decision === 'deny' || decision === 'ask') {
      log('editGuard ' + fileShort + ' → BLOCKED');
      // Strip permissionDecision — we use systemMessage only
      const cleaned = { ...hookResp };
      if (cleaned.hookSpecificOutput) {
        cleaned.hookSpecificOutput = { ...cleaned.hookSpecificOutput };
        delete cleaned.hookSpecificOutput.permissionDecision;
        delete cleaned.hookSpecificOutput.permissionDecisionReason;
      }
      outputJson(cleaned);
    } else {
      const reason = hookResp.reason || '';
      log('editGuard ' + fileShort + ' → pass' + (reason ? ': ' + reason : ''));
      outputJson(hookResp);
    }
  } catch (err) {
    log('editGuard error: ' + String(err));
    outputEmpty();
  }
}

main();
`;


// ─── CC PreToolUse CWE scan (standalone, channel 2) (TypeScript) ───

export const CWE_PRECHECK_TS = String.raw`#!/usr/bin/env bun
import {
  loadJwt, ensureFreshJwt, detectRepo, loadConfig, cweRoute, tag,
  localGradeCwe, parseVerdict, reconstructContent, editSecretDenial, readStdin, log,
  outputJson, outputEmpty, setupCursorHookSignals, installHookWatchdog, isEditTool, isShellTool, isCursorHookFormat,
  extractShellCodeWrites, hookSessionId, filePathFromToolInput, emitBlockScanFindings, cweSnippetLines, dispatchFinding, dispatchCapture, GATEWAY_URL,
  logGraderUnavailable, graderUnavailableMessage, resolveTranscriptPath, isCursorInvokingCcHook,
  loadSynkroFile, effectiveGraderPool, synkroFilePresent, noSynkroSkipMessage, combinedEditGradeEnabled,
} from './_synkro-common.ts';
import { basename, extname, resolve, join, dirname } from 'node:path';
import { readFileSync, readdirSync, existsSync } from 'node:fs';

const agentKind = (process.env.SYNKRO_HOOK_FORMAT === 'cursor' || process.argv.includes('--cursor')) ? 'cursor' : 'claude_code';

function detectModel(payload: Record<string, unknown>): string {
  const raw = String(payload.model ?? payload.model_id ?? '');
  if (agentKind === 'cursor') return raw ? (raw.startsWith('cursor/') ? raw : 'cursor/' + raw) : 'cursor';
  return raw || '';
}

interface PackageCapability {
  name: string;
  description: string;
  capabilities: string[];
  sourceExcerpt: string;
}

const NON_CODE_EXTS = new Set([
  '.md', '.mdx', '.txt', '.rst', '.adoc', '.org',
  '.log', '.csv', '.tsv', '.html', '.htm',
  '.lock', '.gitignore', '.dockerignore', '.npmignore',
  '.svg', '.png', '.jpg', '.jpeg', '.gif', '.webp', '.ico', '.pdf',
  '.woff', '.woff2', '.ttf', '.otf',
]);

interface CweScanTarget {
  filePath: string;
  cweContent: string;
  cweDiffSection: string;
  toolName: string;
  toolInput: any;
  /** Full reconstructed file (or written content) — used to locate snippet line ranges. */
  proposed: string;
}

const JS_DANGEROUS_MODULES = new Set([
  'child_process', 'net', 'dgram', 'http', 'https', 'fs', 'vm',
  'worker_threads', 'cluster', 'dns', 'tls', 'crypto',
]);

const PY_DANGEROUS_MODULES = new Set([
  'subprocess', 'os', 'socket', 'requests', 'urllib', 'ctypes',
  'importlib', 'shutil', 'tempfile', 'webbrowser',
]);

function detectNewImports(toolName: string, toolInput: any, fileExt: string): string[] {
  const newCode = toolName === 'Edit' || toolName === 'edit_file' || toolName === 'reapply'
    ? (toolInput.new_string || '')
    : toolName === 'ApplyPatch' || toolName === 'apply_patch'
      ? (toolInput.patch || toolInput.content || toolInput.code_edit || '')
    : toolName === 'MultiEdit'
      ? (Array.isArray(toolInput.edits) ? toolInput.edits.map((e: any) => e?.new_string || '').join('\n') : '')
    : (toolInput.content || toolInput.code_edit || '');

  if (!newCode) return [];
  const imports: string[] = [];

  if (['.ts', '.tsx', '.js', '.jsx', '.mjs', '.cjs'].includes(fileExt)) {
    const jsMatches = newCode.matchAll(/(?:from\s+['"]|require\s*\(\s*['"])([^./'"@][^'"]*|@[^'"]+)['"]/g);
    for (const m of jsMatches) {
      const pkg = m[1].startsWith('@') ? m[1].split('/').slice(0, 2).join('/') : m[1].split('/')[0];
      if (pkg && !imports.includes(pkg)) imports.push(pkg);
    }
  } else if (['.py'].includes(fileExt)) {
    const pyMatches = newCode.matchAll(/(?:^import\s+|^from\s+)([a-zA-Z_]\w*)/gm);
    for (const m of pyMatches) {
      if (m[1] && !imports.includes(m[1])) imports.push(m[1]);
    }
  }

  return imports;
}

function scanPackageCapabilities(pkgName: string, cwd: string): PackageCapability | null {
  const pkgDir = resolve(cwd, 'node_modules', pkgName);
  if (!existsSync(pkgDir)) return null;

  try {
    const pkgJsonPath = join(pkgDir, 'package.json');
    if (!existsSync(pkgJsonPath)) return null;
    const pkgJson = JSON.parse(readFileSync(pkgJsonPath, 'utf-8'));

    const capabilities: string[] = [];
    const dangerousFiles: string[] = [];

    function scanDir(dir: string, depth: number) {
      if (depth > 3 || dangerousFiles.length >= 3) return;
      let entries: string[];
      try { entries = readdirSync(dir); } catch { return; }
      for (const entry of entries) {
        if (entry === 'node_modules' || entry.startsWith('.')) continue;
        const full = join(dir, entry);
        if (entry.endsWith('.js') || entry.endsWith('.mjs') || entry.endsWith('.cjs') || entry.endsWith('.ts')) {
          try {
            const src = readFileSync(full, 'utf-8').slice(0, 5000);
            for (const mod of JS_DANGEROUS_MODULES) {
              if (src.includes("'" + mod + "'") || src.includes('"' + mod + '"')) {
                if (!capabilities.includes(mod)) capabilities.push(mod);
                if (dangerousFiles.length < 3) {
                  const short = full.replace(pkgDir + '/', '');
                  const lines = src.split('\n').filter(l =>
                    JS_DANGEROUS_MODULES.has(l.match(/require\s*\(\s*['"]([^'"]+)['"]\)/)?.[1] || '') ||
                    JS_DANGEROUS_MODULES.has(l.match(/from\s+['"]([^'"]+)['"]/)?.[1] || '')
                  ).slice(0, 5).join('\n');
                  if (lines) dangerousFiles.push('--- ' + short + ' ---\n' + lines);
                }
                break;
              }
            }
          } catch {}
        } else {
          try {
            const stat = require('node:fs').statSync(full);
            if (stat.isDirectory()) scanDir(full, depth + 1);
          } catch {}
        }
      }
    }

    scanDir(pkgDir, 0);

    if (capabilities.length === 0) return null;

    return {
      name: pkgName,
      description: pkgJson.description || '',
      capabilities,
      sourceExcerpt: dangerousFiles.join('\n'),
    };
  } catch {
    return null;
  }
}

async function main() {
  setupCursorHookSignals();
  installHookWatchdog(48000);
  try {
    const input = await readStdin();
    if (!input.trim()) { outputEmpty(); return; }

    const payload = JSON.parse(input);
    const toolName = payload.tool_name || '';
    const toolInput = payload.tool_input || {};
    const sessionId = hookSessionId(payload);
    const workspaceRoots = Array.isArray(payload.workspace_roots) ? payload.workspace_roots as string[] : [];
    const cwd = payload.cwd || workspaceRoots[0] || '';
    const transcriptPath = resolveTranscriptPath(payload);
    const shellCommand = typeof payload.command === 'string' ? payload.command.trim() : '';
    const ccModel = detectModel(payload);

    if (isCursorInvokingCcHook(agentKind, ccModel)) { outputEmpty(); return; }

    if (!synkroFilePresent(cwd)) {
      const _skipMsg = noSynkroSkipMessage(sessionId);
      if (_skipMsg) outputJson({ systemMessage: _skipMsg, hookSpecificOutput: { hookEventName: 'PreToolUse', additionalContext: _skipMsg } });
      else outputEmpty();
      return;
    }

    const targets: CweScanTarget[] = [];

    if (isCursorHookFormat() && (shellCommand || isShellTool(toolName))) {
      const cmd = shellCommand || String(toolInput.command || '');
      if (!cmd) { outputEmpty(); return; }
      for (const w of extractShellCodeWrites(cmd, cwd)) {
        if (w.filePath.includes('/.synkro/hooks/')) continue;
        const ext = extname(w.filePath).toLowerCase();
        if (NON_CODE_EXTS.has(ext)) continue;
        targets.push({
          filePath: w.filePath,
          cweContent: w.content,
          cweDiffSection: '',
          toolName: toolName || 'Shell',
          toolInput: {},
          proposed: w.content,
        });
      }
      if (targets.length === 0) { outputEmpty(); return; }
    } else if (isEditTool(toolName)) {
      const filePath = filePathFromToolInput(toolInput);
      if (!filePath) { outputEmpty(); return; }
      if (filePath.includes('/.synkro/hooks/')) { outputEmpty(); return; }
      const fileExt = extname(filePath).toLowerCase();
      if (NON_CODE_EXTS.has(fileExt)) { outputEmpty(); return; }

      const proposed = reconstructContent(toolName, toolInput, filePath, cwd);
      if (!proposed) { outputEmpty(); return; }

      let cweContent: string;
      let cweDiffSection = '';
      if (toolName === 'Edit' || toolName === 'MultiEdit' || toolName === 'edit_file' || toolName === 'reapply' || toolName === 'ApplyPatch' || toolName === 'apply_patch') {
        const newStr = toolName === 'Edit' || toolName === 'edit_file' || toolName === 'reapply'
          ? (toolInput.new_string || '')
          : toolName === 'ApplyPatch' || toolName === 'apply_patch'
            ? (toolInput.patch || toolInput.content || toolInput.code_edit || '')
          : (Array.isArray(toolInput.edits) ? toolInput.edits.map((e: any) => e?.new_string || '').join('\n') : '');
        cweDiffSection = newStr.slice(0, 4000);
        const changeIdx = proposed.indexOf(newStr);
        if (changeIdx >= 0 && proposed.length > 6000) {
          const start = Math.max(0, changeIdx - 2000);
          const end = Math.min(proposed.length, changeIdx + newStr.length + 2000);
          cweContent = proposed.slice(start, end);
        } else {
          cweContent = proposed.slice(0, 6000);
        }
      } else {
        cweContent = proposed.slice(0, 4000);
      }

      targets.push({ filePath, cweContent, cweDiffSection, toolName, toolInput, proposed });
    } else {
      outputEmpty();
      return;
    }

    let jwt = loadJwt();
    if (!jwt) { outputEmpty(); return; }
    jwt = await ensureFreshJwt(jwt);

    const config = await loadConfig(jwt);
    const synkroFile = loadSynkroFile(cwd);
    const graderPool = effectiveGraderPool(synkroFile, agentKind, config);
    const rt = await cweRoute(config, synkroFile);

    // Combined-grade mode: the edit-precheck hook runs CWE inside its single
    // grade-edit-cwe call, so this standalone CWE scan must no-op — otherwise the
    // edit would be graded for CWE twice (defeating the inference-merge).
    if (combinedEditGradeEnabled(synkroFile)) { outputEmpty(); return; }

    if (config.silent) {
      outputJson({ systemMessage: '[synkro:' + rt + ':cweScan:' + (graderPool === 'claude_code' ? 'claude' : graderPool) + '] skipped (silent mode)' });
      return;
    }

    for (const scan of targets) {
      const filePath = scan.filePath;
      const cweContent = scan.cweContent;
      const cweDiffSection = scan.cweDiffSection;
      const scanToolName = scan.toolName;
      const scanToolInput = scan.toolInput;
      const proposedFull = scan.proposed || scan.cweContent;
      const gitRepo = detectRepo(cwd, transcriptPath, filePath, workspaceRoots);
      const fileShort = basename(filePath);
      const fileExt = extname(filePath).toLowerCase();

    const exemptedCwes = new Set<string>();
    for (const ex of config.scanExemptions) {
      if (ex.cwe_id && filePath.includes(ex.path)) {
        exemptedCwes.add(ex.cwe_id.toUpperCase());
      }
    }

    const graderLabel = graderPool === 'claude_code' ? 'claude' : graderPool;
    const cweTag = '[synkro:' + rt + ':cweScan:' + graderLabel + ']';

    // Secret firewall: a hardcoded credential in the edit is CWE-798 — block it
    // locally (deterministic, no model) before the CWE grade, flagging only the
    // NEW content the edit introduces.
    {
      const _den = editSecretDenial(scanToolName, scanToolInput, fileShort, cweTag);
      if (_den) { outputJson(_den); return; }
    }

    if (rt === 'skip') {
      outputJson({ systemMessage: cweTag + ' ' + graderUnavailableMessage('cweGuard', fileShort, 'channel down', graderPool) });
      return;
    }

    if (rt === 'byok') {
      let packageContext: PackageCapability[] | undefined;
      if (cwd) {
        const newImports = detectNewImports(scanToolName, scanToolInput, fileExt);
        if (newImports.length > 0) {
          const caps = newImports
            .slice(0, 5)
            .map(pkg => scanPackageCapabilities(pkg, cwd))
            .filter((c): c is PackageCapability => c !== null);
          if (caps.length > 0) packageContext = caps;
        }
      }
      const scanBody: any = { file_path: filePath, content: cweContent };
      if (packageContext) {
        scanBody.package_context = packageContext.map(c => ({
          name: c.name, description: c.description, capabilities: c.capabilities, source_excerpt: c.sourceExcerpt,
        }));
      }

      let cweResp: any;
      try {
        const resp = await fetch(GATEWAY_URL + '/api/v1/cwe-scan', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + jwt },
          body: JSON.stringify(scanBody),
          signal: AbortSignal.timeout(12000),
        });
        if (!resp.ok) {
          outputJson({ systemMessage: cweTag + ' ' + fileShort + ' \u2192 cloud CWE scan failed (HTTP ' + resp.status + '), skipped' });
          return;
        }
        cweResp = await resp.json();
      } catch {
        outputJson({ systemMessage: cweTag + ' ' + fileShort + ' \u2192 cloud CWE scan timeout, skipped' });
        return;
      }

      const findings = Array.isArray(cweResp?.findings) ? cweResp.findings : [];
      if (cweResp?.action === 'deny' && findings.length > 0) {
        const activeCweIds = findings
          // CWE is a separate, always-ask grader — a detected weakness is ALWAYS
          // surfaced regardless of the rule's stored mode (fix/blocking/ask).
          // Only path exemptions suppress it (matches the local-route branch).
          .map((f: any) => f.cwe)
          .filter((id: string) => !exemptedCwes.has(id.toUpperCase()));

        if (activeCweIds.length === 0) {
          continue;
        }

        const displayIds = activeCweIds.slice(0, 3).join(', ');
        const count = activeCweIds.length;
        const label = count === 1 ? 'match' : 'matches';
        const cweMsg = cweTag + ' ' + fileShort + ' \u2192 ' + count + ' CWE ' + label + ' (' + displayIds + ')';

        // Sanitize any scan-derived text before embedding it in the agent message
        // (CWE-20): bound length + strip control chars; constrain the CWE id shape.
        const sanitizeDesc = (s: any) => String(s == null ? '' : s).replace(/[\x00-\x1F\x7F]/g, ' ').replace(/\s{2,}/g, ' ').trim().slice(0, 400);
        const sanitizeId = (s: any) => String(s == null ? '' : s).replace(/[^A-Za-z0-9_-]/g, '').slice(0, 20).toUpperCase() || 'CWE-UNKNOWN';
        // ONE sentence, hard-capped \u2014 keeps each line tight so 4+ CWEs in a single edit
        // never blow the message-size limit. Trims at the first sentence terminator.
        const oneSentence = (s: any) => { const t = sanitizeDesc(s); const m = t.match(/^[\s\S]*?[.!?](\s+(?=[A-Z])|$)/); return (m ? m[0] : t).trim().slice(0, 220); };
        // ONE line per flagged CWE: a one-sentence explanation + a one-sentence Fix.
        // The Fix is dropped when it merely restates the explanation (the grader
        // sometimes echoes it) so the line never double-prints the same prose.
        const cweSeen = new Set<string>();
        const cweBlocks = activeCweIds
          .map((cweId: string) => {
            const sid = sanitizeId(cweId);
            if (cweSeen.has(sid)) return '';
            cweSeen.add(sid);
            const f = findings.find((x: any) => x.cwe === cweId);
            const why = oneSentence(f && (f.reason || f.name) ? (f.reason || f.name) : 'code weakness detected');
            const fix = f && f.suggested_fix ? oneSentence(f.suggested_fix) : '';
            const showFix = !!fix && !why.toLowerCase().includes(fix.slice(0, 40).toLowerCase());
            return '[' + sid + '] ' + why + (showFix ? ' Fix: ' + fix : '');
          })
          .filter((line: string) => line.length > 0);
        // Bound the total: list as many CWEs as fit, spill the rest to a "+N more" note
        // (agent fixes the listed ones, then re-runs to surface the remainder).
        const MAX_CWE_CHARS = 4000;
        let denyDetail = cweBlocks.join('\n');
        if (denyDetail.length > MAX_CWE_CHARS) {
          const kept: string[] = [];
          let used = 0;
          for (const b of cweBlocks) { if (used + b.length + 1 > MAX_CWE_CHARS) break; kept.push(b); used += b.length + 1; }
          const more = cweBlocks.length - kept.length;
          denyDetail = kept.join('\n') + (more > 0 ? '\n(+' + more + ' more CWE weakness' + (more === 1 ? '' : 'es') + ' \u2014 fix these first, then re-run to surface the rest)' : '');
        }
        const ctx = 'CWE weaknesses to fix (' + count + '):\n' + denyDetail + '\nFix ALL of the above before retrying. Do NOT ask the user to make the edit manually \u2014 resolve every weakness in code yourself.';

        emitBlockScanFindings(
          jwt,
          config.captureDepth,
          { session_id: sessionId, file_path: filePath, repo: gitRepo || undefined },
          activeCweIds.map((cweId) => {
            const f = findings.find((x: any) => x.cwe === cweId);
            const snippet = f?.code_snippet || undefined;
            return {
              finding_type: 'cwe' as const,
              finding_id: cweId,
              severity: f?.severity || 'high',
              detail: f?.reason || 'code weakness detected',
              cwe_name: f?.name || undefined,
              code_snippet: snippet,
              offending_lines: cweSnippetLines(proposedFull, snippet),
            };
          }),
          {
            finding_type: 'cwe',
            finding_id: activeCweIds[0] || 'CWE-UNKNOWN',
            severity: findings[0]?.severity || 'high',
            detail: denyDetail,
          },
        );

        dispatchCapture(jwt, 'cwe', 'block', findings[0]?.severity || 'high', 'security',
          scanToolName, gitRepo, sessionId, config.captureDepth, {
            command: (isShellTool(scanToolName) ? 'shell write ' : 'edit ') + filePath,
            reasoning: denyDetail,
            violatedRules: activeCweIds,
            ccModel,
          });

        outputJson({
          systemMessage: cweMsg,
          hookSpecificOutput: { hookEventName: 'PreToolUse', permissionDecision: 'deny', permissionDecisionReason: ctx, additionalContext: ctx },
        });
        return;
      }

      dispatchFinding(jwt, {
        session_id: sessionId,
        file_path: filePath,
        finding_type: 'cwe',
        finding_id: 'pass',
        status: 'resolved',
      }, config.captureDepth);
      continue;
    }

    if (rt === 'local') {
      let cweRules: any[] = [];
      let cweRuleFetchFailed = false;
      try {
        const resp = await fetch(GATEWAY_URL + '/api/v1/cwe-rules?ext=' + encodeURIComponent(fileExt), {
          headers: { Authorization: 'Bearer ' + jwt },
          signal: AbortSignal.timeout(4000),
        });
        if (!resp.ok) {
          log('CWE rules fetch failed: HTTP ' + resp.status);
          cweRuleFetchFailed = true;
        } else {
          const data = await resp.json() as any;
          cweRules = data.rules || [];
        }
      } catch (fetchErr: any) {
        log('CWE rules fetch error: ' + (fetchErr?.message || String(fetchErr)));
        cweRuleFetchFailed = true;
      }

      if (cweRuleFetchFailed) {
        outputJson({ systemMessage: cweTag + ' ' + fileShort + ' \u2192 CWE rules unavailable \u2014 scan skipped' });
        return;
      }

      if (cweRules.length === 0) {
        outputJson({ systemMessage: cweTag + ' ' + fileShort + ' \u2192 clean (no CWE rules for ' + fileExt + ')' });
        return;
      }

      let localPkgContext = '';
      if (cwd) {
        const newImports = detectNewImports(scanToolName, scanToolInput, fileExt);
        const caps = newImports.slice(0, 5)
          .map(pkg => scanPackageCapabilities(pkg, cwd))
          .filter((c): c is PackageCapability => c !== null);
        if (caps.length > 0) {
          localPkgContext = '\n\nImported packages with notable capabilities:\n' +
            caps.map(c => '- ' + c.name + ' (claims: "' + c.description + '")\n  Capabilities: ' + c.capabilities.join(', ') + '\n' + c.sourceExcerpt).join('\n');
        }
      }

      const exemptionNote = exemptedCwes.size > 0
        ? '\n\nEXEMPTED CWEs (already known, DO NOT report these — focus on NEW weaknesses only): ' + [...exemptedCwes].join(', ')
        : '';

      function buildCwePrompt(content: string): string {
        const diffBlock = cweDiffSection
          ? '\n\n=== NEW/CHANGED CODE (evaluate THIS for CWE weaknesses) ===\n' + cweDiffSection + '\n=== END NEW CODE ===\n\nThe surrounding content below is CONTEXT ONLY to help you understand imports, variables, and scope. Do NOT report issues found only in the context section.\n'
          : '';
        return [
          'File: ' + filePath,
          diffBlock,
          'Full content window:',
          content,
          '',
          'CWE rules to check against:',
          JSON.stringify(cweRules),
        ].join('\n') + localPkgContext + exemptionNote;
      }

      const SPLIT_THRESHOLD = 4000;
      const OVERLAP = 500;
      let gradeResponses: string[] = [];

      if (cweContent.length > SPLIT_THRESHOLD) {
        const mid = Math.floor(cweContent.length / 2);
        const chunk1 = cweContent.slice(0, mid + OVERLAP);
        const chunk2 = cweContent.slice(mid - OVERLAP);
        try {
          const [resp1, resp2] = await Promise.all([
            localGradeCwe(buildCwePrompt(chunk1), graderPool),
            localGradeCwe(buildCwePrompt(chunk2), graderPool),
          ]);
          gradeResponses = [resp1, resp2];
        } catch (gradeErr: any) {
          const reason = gradeErr?.message || String(gradeErr);
          logGraderUnavailable('cweGuard', fileShort, reason, buildCwePrompt(cweContent), { sessionId, repo: gitRepo });
          outputJson({ systemMessage: cweTag + ' ' + graderUnavailableMessage('cweGuard', fileShort, reason, graderPool) });
          return;
        }
      } else {
        try {
          gradeResponses = [await localGradeCwe(buildCwePrompt(cweContent), graderPool)];
        } catch (gradeErr: any) {
          const reason = gradeErr?.message || String(gradeErr);
          logGraderUnavailable('cweGuard', fileShort, reason, buildCwePrompt(cweContent), { sessionId, repo: gitRepo });
          outputJson({ systemMessage: cweTag + ' ' + graderUnavailableMessage('cweGuard', fileShort, reason, graderPool) });
          return;
        }
      }

      const cweIds: string[] = [];
      const fixes: Record<string, string> = {};
      const reasons: Record<string, string> = {};
      const snippets: Record<string, string> = {};
      let mergedReason = '';
      let mergedSeverity = '';
      let mergedCategory = '';
      let anyFailed = false;

      for (const gradeResp of gradeResponses) {
        // PRIMARY shape — the live CWE grader returns ONE JSON object:
        //   {"cweIds":[..],"reasons":{id:..},"fixes":{id:..},"snippets":{id:..},"mergedReason":".."}
        // Parse it directly so each CWE keeps its OWN contextual reason + specific
        // fix. Critically this also bounds detection to cweIds — NOT a regex scrape of
        // the prose (which used to grab CWE-### tokens out of mergedReason, e.g. a
        // "no CWE-502 found" sentence, and over-report findings).
        try {
          const objM = gradeResp.match(/\{[\s\S]*"cweIds"[\s\S]*\}/);
          if (objM) {
            const p = JSON.parse(objM[0]);
            if (Array.isArray(p.cweIds)) {
              const ids = p.cweIds.map((x: any) => String(x).trim().replace(/^cwe-/i, 'CWE-')).filter((id: string) => /^CWE-\d+$/.test(id));
              if (ids.length > 0) {
                anyFailed = true;
                if (!mergedReason && typeof p.mergedReason === 'string') mergedReason = p.mergedReason;
                for (const id of ids) {
                  if (!cweIds.includes(id)) cweIds.push(id);
                  const rsn = p.reasons && typeof p.reasons[id] === 'string' ? p.reasons[id] : '';
                  const fx = p.fixes && typeof p.fixes[id] === 'string' ? p.fixes[id] : '';
                  const sn = p.snippets && typeof p.snippets[id] === 'string' ? p.snippets[id] : '';
                  if (rsn && !reasons[id]) reasons[id] = rsn;
                  if (fx && !fixes[id]) fixes[id] = fx;
                  if (sn && !snippets[id]) snippets[id] = sn;
                }
                continue;
              }
            }
          }
        } catch { /* not the JSON-object shape — fall through to legacy parsing */ }
        // The CWE grader emits findings, but its wrapper VARIES per call:
        //   {"findings":[...]}  |  <findings>[...]</findings>  |  a bare [...] array.
        // Extract findings from any of them. Without this, parseVerdict sees no
        // <synkro-verdict> wrapper and defaults to ok:true, silently dropping every
        // detected CWE (a format mismatch). The <synkro-verdict> XML path below is
        // the fallback for the legacy format.
        let jsonFindings: any[] | null = null;
        try {
          const tagM = gradeResp.match(/<findings>([\s\S]*?)<\/findings>/);
          const src = tagM ? tagM[1] : gradeResp;
          const objM = src.match(/\{[\s\S]*"findings"[\s\S]*\}/);
          const arrM = src.match(/\[[\s\S]*\]/);
          if (objM) { const p = JSON.parse(objM[0]); if (Array.isArray(p.findings)) jsonFindings = p.findings; }
          if (!jsonFindings && arrM) { const p = JSON.parse(arrM[0]); if (Array.isArray(p)) jsonFindings = p; }
        } catch { /* not parseable JSON — fall through to XML parsing */ }
        if (jsonFindings) {
          if (jsonFindings.length > 0) {
            anyFailed = true;
            for (const f of jsonFindings.slice(0, 8)) {
              const id = String(f.cwe || f.rule_id || '').trim().replace(/^cwe-/i, 'CWE-');
              // Validate the shape (CWE-<number>) before using it as an object key —
              // the grade response is model output, so reject anything that isn't a
              // real CWE id (also blocks __proto__/constructor key injection).
              if (!/^CWE-\d+$/.test(id)) continue;
              if (!cweIds.includes(id)) cweIds.push(id);
              if (f.suggested_fix && !fixes[id]) fixes[id] = String(f.suggested_fix);
              if ((f.reason || f.summary || f.detail) && !reasons[id]) reasons[id] = String(f.reason || f.summary || f.detail);
              if (f.code_snippet && !snippets[id]) snippets[id] = String(f.code_snippet);
              if (!mergedReason && (f.summary || f.detail)) mergedReason = String(f.summary || f.detail);
              if (!mergedSeverity && f.severity) mergedSeverity = String(f.severity);
              if (!mergedCategory && f.category) mergedCategory = String(f.category);
            }
          }
          continue;
        }
        const v = parseVerdict(gradeResp);
        if (!v.ok) {
          anyFailed = true;
          if (v.reason) mergedReason = mergedReason || v.reason;
          if (v.severity) mergedSeverity = mergedSeverity || v.severity;
          if (v.category) mergedCategory = mergedCategory || v.category;
          const ruleIdMatches = gradeResp.match(/<rule_id>([^<]+)<\/rule_id>/g) || [];
          for (const m of ruleIdMatches.slice(0, 5)) {
            const id = m.replace(/<\/?rule_id>/g, '').trim().replace(/^cwe-/, 'CWE-');
            if (id && !cweIds.includes(id)) cweIds.push(id);
          }
          const fMatches = gradeResp.match(/<(?:suggested_fix|required_fix)>([\s\S]*?)<\/(?:suggested_fix|required_fix)>/g) || [];
          const respIds = ruleIdMatches.map(rm => rm.replace(/<\/?rule_id>/g, '').trim().replace(/^cwe-/, 'CWE-'));
          for (let i = 0; i < Math.min(respIds.length, fMatches.length); i++) {
            if (!fixes[respIds[i]]) fixes[respIds[i]] = fMatches[i].replace(/<\/?(?:suggested_fix|required_fix)>/g, '').trim();
          }
          // Per-<violation> pass for the verbatim snippet (code may contain '<'),
          // paired with its rule_id so each finding carries its own offending code.
          const vBlocks = gradeResp.match(/<violation>[\s\S]*?<\/violation>/g) || [];
          for (const block of vBlocks.slice(0, 8)) {
            const bId = block.match(/<rule_id>([^<]+)<\/rule_id>/)?.[1];
            if (!bId) continue;
            const id = bId.trim().replace(/^cwe-/i, 'CWE-');
            const snM = block.match(/<code_snippet>([\s\S]*?)<\/code_snippet>/);
            if (snM && !snippets[id]) {
              const snip = snM[1].replace(/^\n+|\n+$/g, '');
              if (snip.trim()) snippets[id] = snip;
            }
            // Capture THIS violation's own reason so the deny can show a per-CWE
            // explanation, not just one merged reason for the whole batch.
            const rM = block.match(/<reason>([\s\S]*?)<\/reason>/);
            if (rM && !reasons[id]) { const rs = rM[1].trim(); if (rs) reasons[id] = rs; }
          }
        }
      }

      const verdict = anyFailed
        ? { ok: false, reason: mergedReason, severity: mergedSeverity, category: mergedCategory }
        : { ok: true, reason: '', severity: '', category: '' };

      if (!verdict.ok) {
        const activeCweIds = cweIds.filter(id => !exemptedCwes.has(id.toUpperCase()));

        if (activeCweIds.length === 0) {
          continue;
        }

        const cweNameMap = new Map<string, string>();
        for (const r of cweRules) {
          if (r.cwe && r.name) cweNameMap.set(r.cwe.toUpperCase(), r.name);
        }

        const displayIds = activeCweIds.slice(0, 3).join(', ');
        const count = activeCweIds.length;
        const label = count === 1 ? 'match' : 'matches';
        const cweMsg = cweTag + ' ' + fileShort + ' \u2192 ' + count + ' CWE ' + label + ' (' + displayIds + ')';
        // ONE sentence, hard-capped (strip control chars, trim at first terminator) so
        // the deny stays tight even with several CWEs in one edit.
        const oneSentence = (s: any) => { const t = String(s == null ? '' : s).replace(/[\x00-\x1F\x7F]/g, ' ').replace(/\s{2,}/g, ' ').trim().slice(0, 400); const m = t.match(/^[\s\S]*?[.!?](\s+(?=[A-Z])|$)/); return (m ? m[0] : t).trim().slice(0, 220); };
        // ONE line per flagged CWE: its own one-sentence explanation + a one-sentence Fix.
        // Per-CWE reason (reasons[id]) → merged reason → CWE name → generic fallback. The
        // Fix is dropped when it just restates the explanation so no line double-prints.
        const cweBlocks = activeCweIds.map(id => {
          const why = oneSentence(reasons[id] || verdict.reason || cweNameMap.get(id.toUpperCase()) || 'code weakness detected');
          const fix = fixes[id] ? String(fixes[id]).replace(/[\x00-\x08\x0B-\x1F\x7F]/g, ' ').trim().slice(0, 1200) : '';
          const showFix = !!fix && !why.toLowerCase().includes(fix.slice(0, 40).toLowerCase());
          return '[' + id + '] ' + why + (showFix ? ' REQUIRED FIX (apply exactly — do not substitute a different library/format/approach): ' + fix : '');
        });
        // Bound the total: list as many CWEs as fit, spill the rest to a "+N more" note.
        let denyDetail = cweBlocks.join('\n');
        if (denyDetail.length > 4000) {
          const kept: string[] = [];
          let used = 0;
          for (const b of cweBlocks) { if (used + b.length + 1 > 4000) break; kept.push(b); used += b.length + 1; }
          const more = cweBlocks.length - kept.length;
          denyDetail = kept.join('\n') + (more > 0 ? '\n(+' + more + ' more CWE weakness' + (more === 1 ? '' : 'es') + ' — fix these first, then re-run to surface the rest)' : '');
        }
        const ctx = 'CWE weaknesses to fix (' + count + '):\n' + denyDetail + '\nThese are REQUIRED FIXES — apply each one exactly as given; do NOT substitute your own approach or a different library/format. Do NOT ask the user to make the edit manually — resolve every weakness in code yourself.';

        emitBlockScanFindings(
          jwt,
          config.captureDepth,
          { session_id: sessionId, file_path: filePath, repo: gitRepo || undefined },
          activeCweIds.map((cweId) => {
            const snippet = snippets[cweId] || undefined;
            return {
              finding_type: 'cwe' as const,
              finding_id: cweId,
              severity: verdict.severity || 'high',
              detail: verdict.reason || 'code weakness detected',
              cwe_name: cweNameMap.get(cweId.toUpperCase()) || undefined,
              code_snippet: snippet,
              offending_lines: cweSnippetLines(proposedFull, snippet),
            };
          }),
          {
            finding_type: 'cwe',
            finding_id: activeCweIds[0] || 'CWE-UNKNOWN',
            severity: verdict.severity || 'high',
            detail: verdict.reason || denyDetail,
          },
        );

        dispatchCapture(jwt, 'cwe', 'block', verdict.severity || 'high', verdict.category || 'security',
          scanToolName, gitRepo, sessionId, config.captureDepth, {
            command: (isShellTool(scanToolName) ? 'shell write ' : 'edit ') + filePath,
            reasoning: denyDetail,
            violatedRules: activeCweIds,
            ccModel,
          });

        outputJson({
          systemMessage: cweMsg,
          hookSpecificOutput: { hookEventName: 'PreToolUse', permissionDecision: 'deny', permissionDecisionReason: ctx, additionalContext: ctx },
        });
        return;
      }

      dispatchFinding(jwt, {
        session_id: sessionId,
        file_path: filePath,
        finding_type: 'cwe',
        finding_id: 'pass',
        status: 'resolved',
      }, config.captureDepth);
      continue;
    }
    }

    outputEmpty();
  } catch (err) {
    log('cweGuard error: ' + String(err));
    outputEmpty();
  }
}

main();
`;


// ─── CC PreToolUse CVE scan (standalone, curl only) (TypeScript) ───

export const CVE_PRECHECK_TS = `#!/usr/bin/env bun
import {
  loadJwt, ensureFreshJwt, detectRepo, loadConfig, route, tag,
  reconstructContent, readStdin, findNearestDeps, filePathFromToolInput, log,
  outputJson, outputEmpty, setupCursorHookSignals, isEditTool, hookSessionId, dispatchFinding, dispatchCapture, dispatchScanResult, extractTranscript, emitBlockScanFindings, resolveTranscriptPath, GATEWAY_URL,
  isCursorHookFormat, synkroFilePresent, noSynkroSkipMessage,
} from './_synkro-common.ts';
import { basename } from 'node:path';
import { readFileSync } from 'node:fs';

const MANIFEST_NAMES = new Set([
  'package.json', 'requirements.txt', 'requirements-dev.txt', 'requirements-test.txt',
  'Pipfile', 'go.mod', 'go.sum', 'Gemfile', 'pom.xml', 'Cargo.toml', 'composer.json', 'pyproject.toml',
]);

function isManifest(filename: string): boolean {
  if (MANIFEST_NAMES.has(filename)) return true;
  if (filename.startsWith('requirements') && filename.endsWith('.txt')) return true;
  if (filename.startsWith('build.gradle')) return true;
  if (filename.endsWith('.cabal')) return true;
  return false;
}

async function main() {
  setupCursorHookSignals();
  try {
    const input = await readStdin();
    if (!input.trim()) { outputEmpty(); return; }

    const payload = JSON.parse(input);
    const toolName = payload.tool_name || '';
    if (!isEditTool(toolName)) {
      outputEmpty();
      return;
    }

    const _m = String(payload.model ?? payload.model_id ?? '');
    if (!isCursorHookFormat() && _m && !_m.startsWith('claude-')) { outputEmpty(); return; }

    const toolInput = payload.tool_input || {};
    const sessionId = hookSessionId(payload);
    const workspaceRoots = Array.isArray(payload.workspace_roots) ? payload.workspace_roots as string[] : [];
    const cwd = payload.cwd || workspaceRoots[0] || '';
    const transcriptPath = resolveTranscriptPath(payload);

    const filePath = filePathFromToolInput(toolInput);
    if (!filePath) { outputEmpty(); return; }
    const gitRepo = detectRepo(cwd, transcriptPath, filePath, workspaceRoots);

    if (filePath.includes('/.synkro/hooks/')) { outputEmpty(); return; }

    if (!synkroFilePresent(cwd)) {
      const _skipMsg = noSynkroSkipMessage(sessionId);
      if (_skipMsg) outputJson({ systemMessage: _skipMsg, hookSpecificOutput: { hookEventName: 'PreToolUse', additionalContext: _skipMsg } });
      else outputEmpty();
      return;
    }

    const fileShort = basename(filePath);

    let jwt = loadJwt();
    if (!jwt) { outputEmpty(); return; }
    jwt = await ensureFreshJwt(jwt);

    const config = await loadConfig(jwt);

    if (config.silent) {
      outputJson({ systemMessage: '[synkro:cveScan] ' + fileShort + ' → skipped (silent mode)' });
      return;
    }

    // CVE scanning has no local/cloud distinction from the user's view — it's an
    // OSV database lookup that always runs server-side — so the tag omits the
    // location segment that grading/CWE carry. Just [synkro:cveScan].
    const cveTag = '[synkro:cveScan]';

    // Reconstruct proposed content
    const proposed = reconstructContent(toolName, toolInput, filePath, cwd);
    if (!proposed) {
      outputJson({ systemMessage: cveTag + ' ' + fileShort + ' → skip (no content)' });
      return;
    }

    const proposedShort = proposed.slice(0, 4000);

    // For code files, find nearest package.json and extract deps
    let deps: Record<string, string> = {};
    if (!isManifest(fileShort)) {
      deps = findNearestDeps(filePath);
    }

    // For package.json edits, diff the proposed content against the file on
    // disk and scan only the added/upgraded packages. Catches the case the
    // bash installScan misses: a bare \`pnpm install\` after a manifest bump
    // arrives with no package tokens on the cmdline, so the install-time
    // scanner has nothing to feed OSV. By gating at the edit, the install
    // that follows is implicitly safe — there's nothing new to scan.
    if (fileShort === 'package.json') {
      try {
        let currentContent = '';
        try { currentContent = readFileSync(filePath, 'utf-8'); } catch {}
        const extractMod: any = await import(new URL('./installExtractCore.ts', import.meta.url).href);
        const deltaPkgs = extractMod.extractPackageJsonDelta(currentContent, proposed) as Array<{ name: string; version: string; ecosystem: string }>;
        if (deltaPkgs.length > 0) {
          const pkgScanResp = await fetch(GATEWAY_URL + '/api/v1/pkg-scan', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + jwt },
            body: JSON.stringify({ command: 'edit ' + filePath, packages: deltaPkgs }),
            signal: AbortSignal.timeout(8000),
          }).then(r => r.json()).catch(() => null) as any;
          const action = pkgScanResp?.action || 'allow';
          if (action === 'block') {
            const pkgResults = Array.isArray(pkgScanResp?.packages) ? pkgScanResp.packages : [];
            const summary = pkgScanResp?.summary || (deltaPkgs.length + ' package(s) flagged');
            const violatedIds: string[] = [];
            const cveFindings: Array<{
              finding_type: 'cve'; finding_id: string; severity?: string; detail?: string;
              description?: string; package_name?: string; package_version?: string;
              fixed_version?: string; aliases?: string[]; references?: Array<{ type: string; url: string }>;
            }> = [];
            for (const p of pkgResults) {
              if (Array.isArray(p?.findings)) {
                for (const f of p.findings) {
                  const aliasCve = Array.isArray(f?.aliases) ? f.aliases.find((a: string) => a.startsWith('CVE-')) : null;
                  const fid = aliasCve || f?.id || 'unknown';
                  if (fid && !violatedIds.includes(fid)) violatedIds.push(fid);
                  cveFindings.push({
                    finding_type: 'cve',
                    finding_id: fid,
                    severity: f?.severity || 'high',
                    detail: f?.summary || f?.title || 'vulnerable dependency',
                    description: f?.details || undefined,
                    package_name: p?.name,
                    package_version: p?.version,
                    fixed_version: f?.fixed || undefined,
                    aliases: f?.aliases || undefined,
                    references: f?.references || undefined,
                  });
                }
              }
            }
            emitBlockScanFindings(
              jwt,
              config.captureDepth,
              { session_id: sessionId, file_path: filePath, repo: gitRepo || undefined },
              cveFindings,
              {
                finding_type: 'cve',
                finding_id: violatedIds[0] || 'SYNKRO_PKGSCAN',
                severity: 'critical',
                detail: summary.slice(0, 500),
                package_name: deltaPkgs[0]?.name,
                package_version: deltaPkgs[0]?.version,
              },
            );
            // Model name isn't in the PreToolUse payload — pull it from the
            // transcript (last assistant entry's message.model). Matches what
            // the bash judge does at capture time.
            let ccModel: string | undefined = String(payload.model ?? payload.model_id ?? '') || undefined;
            if (!ccModel && transcriptPath) {
              try { ccModel = extractTranscript(transcriptPath).ccModel || undefined; } catch {}
            }
            dispatchCapture(jwt, 'pkg', 'block', 'critical', 'security',
              toolName, gitRepo, sessionId, config.captureDepth, {
                command: 'edit ' + filePath, reasoning: summary.slice(0, 200),
                violatedRules: violatedIds,
                ccModel,
              });
            dispatchScanResult(jwt, {
              session_id: sessionId, file_path: filePath, scan_type: 'pkg',
              result: 'block', finding_count: violatedIds.length,
              finding_ids: violatedIds, severity: 'critical',
              repo: gitRepo || undefined,
            });
            const tagStr = '[synkro:pkgScan]';
            const denyReason = tagStr + ' BLOCKED: ' + summary + '\\nDo not write this version. Pick a fixed/safe version instead.';
            outputJson({
              systemMessage: denyReason,
              hookSpecificOutput: { hookEventName: 'PreToolUse', permissionDecision: 'deny', permissionDecisionReason: denyReason, additionalContext: denyReason },
            });
            return;
          }
        }
      } catch (err) {
        log('pkgDeltaScan error: ' + String(err));
      }
    }

    // CVE scan via OSV API
    const cveBody = {
      file_path: filePath,
      content: proposedShort,
      dependencies: deps,
    };

    let cveResp: any;
    try {
      const resp = await fetch(GATEWAY_URL + '/api/v1/cve-scan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + jwt },
        body: JSON.stringify(cveBody),
        signal: AbortSignal.timeout(8000),
      });
      cveResp = await resp.json();
    } catch {
      outputJson({ systemMessage: cveTag + ' ' + fileShort + ' → error (timeout)' });
      return;
    }

    const findings = Array.isArray(cveResp?.findings) ? cveResp.findings : [];
    if (findings.length > 0) {
      const cveRows = findings.slice(0, 10).map((f: any) => {
        const cveId = (f.aliases || []).find((a: string) => a.startsWith('CVE-')) || f.id || 'unknown';
        return {
          finding_type: 'cve' as const,
          finding_id: cveId,
          severity: f.severity || 'high',
          detail: f.summary || f.title || 'vulnerable dependency',
          description: f.details || undefined,
          package_name: f.package || undefined,
          package_version: f.version || undefined,
          fixed_version: f.fixed || undefined,
          aliases: f.aliases || undefined,
          references: f.references || undefined,
        };
      });
      emitBlockScanFindings(
        jwt,
        config.captureDepth,
        { session_id: sessionId, file_path: filePath, repo: gitRepo || undefined },
        cveRows,
        cveRows[0],
      );

      const formatFinding = (f: any): string => {
        const id = (f.aliases || []).find((a: string) => a.startsWith('CVE-')) || f.id || '?';
        const pkg = f.package || '?';
        const ver = f.version || '?';
        const title = f.title || f.summary || 'vulnerable';
        const fix = f.fixed ? ' (fix: >=' + f.fixed + ')' : ' (no safe version)';
        return '[' + id + '] ' + pkg + '@' + ver + ': ' + title + fix;
      };

      const top3 = findings.slice(0, 3).map(formatFinding).join('; ');
      const count = findings.length;
      const label = count === 1 ? 'advisory' : 'advisories';
      const cveMsg = cveTag + ' ' + fileShort + ' → ' + count + ' ' + label;
      const ctx = 'CVE: ' + top3 + '\\nFix all issues before retrying. Do NOT ask the user to make the edit manually — upgrade the vulnerable dependencies yourself.';

      const cveIds = findings.slice(0, 10).map((f: any) =>
        (f.aliases || []).find((a: string) => a.startsWith('CVE-')) || f.id || 'unknown'
      );
      let cveCcModel: string | undefined = String(payload.model ?? payload.model_id ?? '') || undefined;
      if (!cveCcModel && transcriptPath) {
        try { cveCcModel = extractTranscript(transcriptPath).ccModel || undefined; } catch {}
      }
      dispatchCapture(jwt, 'cve', 'block', 'critical', 'security',
        toolName, gitRepo, sessionId, config.captureDepth, {
          command: 'edit ' + filePath,
          reasoning: top3,
          violatedRules: cveIds,
          ccModel: cveCcModel,
        });
      dispatchScanResult(jwt, {
        session_id: sessionId, file_path: filePath, scan_type: 'cve',
        result: 'block', finding_count: findings.length,
        finding_ids: cveIds, severity: 'critical',
        repo: gitRepo || undefined,
      });

      outputJson({
        systemMessage: cveMsg,
        hookSpecificOutput: { hookEventName: 'PreToolUse', permissionDecision: 'deny', permissionDecisionReason: ctx, additionalContext: ctx },
      });
      return;
    }

    dispatchScanResult(jwt, {
      session_id: sessionId, file_path: filePath, scan_type: 'cve',
      result: 'pass', finding_count: 0,
      repo: gitRepo || undefined,
    });
    outputJson({ systemMessage: cveTag + ' ' + fileShort + ' → clean' });
  } catch (err) {
    log('cveGuard error: ' + String(err));
    outputEmpty();
  }
}

main();
`;


// ─── CC/Cursor PreToolUse Install Scan (standalone, fires before bash judge) ───

export const INSTALL_SCAN_TS = `#!/usr/bin/env bun
import {
  loadJwt, ensureFreshJwt, detectRepo, loadConfig, route, tag,
  readStdin, runInstallScan, emitBlockScanFindings, dispatchCapture, dispatchScanResult, hashCommand,
  outputJson, outputEmpty, setupCursorHookSignals, isShellTool, hookSessionId, log, GATEWAY_URL,
  resolveTranscriptPath, isCursorHookFormat, loadSynkroFile, effectiveGraderPool, synkroFilePresent, noSynkroSkipMessage,
} from './_synkro-common.ts';
import { writeFileSync, mkdirSync } from 'node:fs';
import { join } from 'node:path';

const SCAN_CACHE_DIR = (process.env.HOME || '/tmp') + '/.synkro/.scan-cache';

async function main() {
  setupCursorHookSignals();
  try {
    const input = await readStdin();
    if (!input.trim()) { outputEmpty(); return; }

    const payload = JSON.parse(input);
    const _m = String(payload.model ?? payload.model_id ?? '');
    if (!isCursorHookFormat() && _m && !_m.startsWith('claude-')) { outputEmpty(); return; }

    const toolInput = payload.tool_input || {};
    const command = typeof payload.command === 'string' ? payload.command : (toolInput.command || '');
    if (!command) { outputEmpty(); return; }

    const toolName = payload.tool_name || '';
    // beforeShellExecution supplies command directly; preToolUse uses tool_name + tool_input.
    if (!isShellTool(toolName) && typeof payload.command !== 'string') { outputEmpty(); return; }

    const _cwd = payload.cwd || (Array.isArray(payload.workspace_roots) ? payload.workspace_roots[0] : '') || '';
    if (!synkroFilePresent(_cwd)) {
      const _skipMsg = noSynkroSkipMessage(hookSessionId(payload));
      if (_skipMsg) outputJson({ systemMessage: _skipMsg, hookSpecificOutput: { hookEventName: 'PreToolUse', additionalContext: _skipMsg } });
      else outputEmpty();
      return;
    }

    let jwt = loadJwt();
    if (!jwt) { outputEmpty(); return; }
    jwt = await ensureFreshJwt(jwt);

    const scan = await runInstallScan(command, jwt);

    if (scan.scanned) {
      try {
        mkdirSync(SCAN_CACHE_DIR, { recursive: true });
        writeFileSync(join(SCAN_CACHE_DIR, hashCommand(command)), JSON.stringify(scan), 'utf-8');
      } catch {}
    }

    if (!scan.scanned) { outputEmpty(); return; }

    const sessionId = hookSessionId(payload);
    const workspaceRoots = Array.isArray(payload.workspace_roots) ? payload.workspace_roots as string[] : [];
    const cwd = payload.cwd || workspaceRoots[0] || '';
    const transcriptPath = resolveTranscriptPath(payload);
    const repo = detectRepo(cwd, transcriptPath, command, workspaceRoots);
    const config = await loadConfig(jwt);
    const isCursor = isCursorHookFormat();
    const synkroFile = loadSynkroFile(cwd);
    const graderPool = effectiveGraderPool(synkroFile, isCursor ? 'cursor' : 'claude_code', config);
    const rt = await route(config, synkroFile);
    const tagStr = tag(rt, config, graderPool);

    const rawModel = String(payload.model ?? payload.model_id ?? '');
    const model = isCursor ? (rawModel ? (rawModel.startsWith('cursor/') ? rawModel : 'cursor/' + rawModel) : 'cursor') : (rawModel || '');

    if (scan.action === 'block') {
      const pkgLabel = scan.scannedLabel || '';
      const pkgName = pkgLabel.split('@')[0] || undefined;
      const pkgVersion = pkgLabel.includes('@') ? pkgLabel.split('@').slice(1).join('@') : undefined;
      emitBlockScanFindings(
        jwt,
        config.captureDepth,
        { session_id: sessionId, file_path: command, repo: repo || undefined },
        scan.findings.map((f) => ({
          finding_type: 'cve' as const,
          finding_id: f.advisoryId + ':' + f.name,
          severity: f.severity,
          detail: f.detail,
          package_name: f.name,
          package_version: f.version,
        })),
        {
          finding_type: 'cve',
          finding_id: 'SYNKRO_PKGSCAN',
          severity: 'critical',
          detail: scan.blockContext.slice(0, 500) || scan.summary || 'Blocked package install',
          package_name: pkgName,
          package_version: pkgVersion,
        },
      );
      dispatchCapture(jwt, 'pkg', 'block', 'critical', 'security',
        'Bash', repo, sessionId, config.captureDepth, {
          command, reasoning: scan.blockContext.slice(0, 200),
          violatedRules: scan.violatedIds,
          ccModel: model || undefined,
        });
      dispatchScanResult(jwt, {
        session_id: sessionId, file_path: command, scan_type: 'pkg',
        result: 'block', finding_count: scan.violatedIds?.length || 1,
        finding_ids: scan.violatedIds, severity: 'critical',
        repo: repo || undefined,
      });
      const denyReason = '[synkro:installScan] BLOCKED: ' + scan.summary + '\\nDo not retry this install. Suggest a safe version to the user instead.';
      outputJson({
        systemMessage: denyReason,
        hookSpecificOutput: { hookEventName: 'PreToolUse', permissionDecision: 'deny', permissionDecisionReason: denyReason, additionalContext: denyReason },
      });
    } else if (scan.action === 'warn') {
      outputJson({
        systemMessage: '[synkro:installScan] ' + scan.summary,
        hookSpecificOutput: { hookEventName: 'PreToolUse', additionalContext: '[synkro:installScan] ' + scan.summary },
      });
    } else {
      outputEmpty();
    }
  } catch (err) {
    log('installScan error: ' + String(err));
    outputEmpty();
  }
}

main();
`;


// ─── CC PreToolUse Bash/Read/Grep/Glob judge (TypeScript) ───

export const BASH_JUDGE_TS = String.raw`#!/usr/bin/env bun
import process from 'node:process';
import {
  loadJwt, ensureFreshJwt, detectRepo, loadConfig, route, tag, localGrade,
  parseVerdict, dispatchCapture, dispatchFinding, ruleMode, postWithRetry, readStdin,
  extractTranscript, readLastPrompt, appendSessionAction, readSessionLog, compressSessionLog, sessionLogForGrade, log,
  outputJson, outputEmpty, setupCursorHookSignals, installHookWatchdog, isShellTool, hookSessionId, GATEWAY_URL,
  logGraderUnavailable, graderUnavailableMessage, filterRules, ruleFilterText, normalizeMode, appendLocalTelemetry, isSafeInRepoRead,
  loadSynkroFile, effectiveGraderPool, synkroFilePresent, noSynkroSkipMessage,
  hashCommand, resolveTranscriptPath, isCursorHookFormat,
  detectHardSecrets, secretRemediation, scrubSecrets,
  type HookConfig, type Rule,
} from './_synkro-common.ts';
import { createHash } from 'node:crypto';
import { existsSync, statSync, readFileSync, writeFileSync, mkdirSync, unlinkSync } from 'node:fs';
import { join } from 'node:path';

const SCAN_CACHE_DIR = (process.env.HOME || '/tmp') + '/.synkro/.scan-cache';

function readCachedScan(command: string): any | null {
  try {
    const path = join(SCAN_CACHE_DIR, hashCommand(command));
    if (!existsSync(path)) return null;
    const data = JSON.parse(readFileSync(path, 'utf-8'));
    unlinkSync(path);
    return data;
  } catch { return null; }
}

const DEDUP_DIR = process.env.HOME + '/.synkro/.dedup';
const DEDUP_TTL_MS = 3000;

function isDuplicate(command: string, sessionId: string): boolean {
  const hash = createHash('md5').update(sessionId + ':' + command).digest('hex').slice(0, 12);
  const marker = DEDUP_DIR + '/' + hash;
  try {
    if (existsSync(marker)) {
      const age = Date.now() - statSync(marker).mtimeMs;
      if (age < DEDUP_TTL_MS) return true;
    }
  } catch {}
  try {
    mkdirSync(DEDUP_DIR, { recursive: true });
    writeFileSync(marker, '', { flag: 'w' });
  } catch {}
  return false;
}

async function main() {
  setupCursorHookSignals();
  installHookWatchdog(48000);
  try {
    const input = await readStdin();
    if (!input.trim()) { outputEmpty(); return; }

    const payload = JSON.parse(input);
    const toolName = payload.tool_name || '';
    if (!isShellTool(toolName)) {
      outputEmpty();
      return;
    }

    const _m = String(payload.model ?? payload.model_id ?? '');
    if (!isCursorHookFormat() && _m && !_m.startsWith('claude-')) { outputEmpty(); return; }

    const toolInput = payload.tool_input || {};
    const sessionId = hookSessionId(payload);
    const toolUseId = payload.tool_use_id || '';
    const workspaceRoots = Array.isArray(payload.workspace_roots) ? payload.workspace_roots as string[] : [];
    const cwd = payload.cwd || workspaceRoots[0] || '';
    const permissionMode = payload.permission_mode || '';
    const transcriptPath = resolveTranscriptPath(payload);
    const transcript = extractTranscript(transcriptPath);

    let command = '';
    switch (toolName) {
      case 'Bash':
      case 'Shell':
      case 'terminal':
      case 'run_terminal_cmd':
      case 'execute_command':
        command = toolInput.command || ''; break;
      case 'Read': command = 'cat ' + (toolInput.file_path || ''); break;
      case 'Grep': command = "grep -r '" + (toolInput.pattern || '') + "' " + (toolInput.path || '.'); break;
      case 'Glob': command = "find . -name '" + (toolInput.pattern || '') + "'"; break;
    }
    if (!command) { outputEmpty(); return; }

    const gitRepo = detectRepo(cwd, transcriptPath, command, workspaceRoots);

    if (!synkroFilePresent(cwd)) {
      const _skipMsg = noSynkroSkipMessage(sessionId);
      if (_skipMsg) outputJson({ systemMessage: _skipMsg, hookSpecificOutput: { hookEventName: 'PreToolUse', additionalContext: _skipMsg } });
      else outputEmpty();
      return;
    }

    if (isDuplicate(command, sessionId)) {
      log('bashGuard skip (dedup): ' + command.slice(0, 80));
      outputEmpty();
      return;
    }

    const cmdShort = command.slice(0, 80);
    log('bashGuard checking: ' + cmdShort);

    // Load JWT + routing config eagerly so even the short-circuit message
    // carries the live pack name + local/cloud tag. Cost: ~200-500ms for the
    // config fetch (network call, no caching). The fetch is unavoidable for
    // the LLM path anyway — we just pay it sooner so the short-circuit can
    // produce a properly-tagged system message.
    let jwt = loadJwt();
    if (!jwt) { outputEmpty(); return; }
    jwt = await ensureFreshJwt(jwt);

    const config = await loadConfig(jwt);
    const synkroFile = loadSynkroFile(cwd);
    const graderPool = effectiveGraderPool(synkroFile, 'claude_code', config);
    const rt = await route(config, synkroFile);
    const tagStr = tag(rt, config, graderPool);

    if (config.silent) {
      outputJson({ systemMessage: tagStr + ' bashGuard → skipped (silent mode)' });
      return;
    }

    // ─── Secret firewall: BLOCK a literal credential in the command BEFORE it runs
    // or reaches the grader — the value never lands in a file, shell history, OR the
    // LLM provider (no leak to Anthropic/Cursor). Deterministic + local, no model
    // call. High-confidence formats only (a false block stops the user), and
    // $VAR / placeholder refs never trip it. MUST run BEFORE the safe-read fast-path
    // below — otherwise an echo/printf of a literal secret is treated as a safe read
    // and waved through. A genuine read carries no literal secret, so this never
    // false-blocks legitimate reads. ───
    const secretHits = detectHardSecrets(command);
    if (secretHits.length) {
      const types = secretHits.map(h => h.type).join(', ');
      const remediation = secretRemediation(command, secretHits);
      log('bashGuard ' + cmdShort + ' → BLOCKED: literal credential (' + types + ')');
      appendLocalTelemetry({
        capture_type: 'local_verdict',
        verdict: 'block',
        hook_type: 'bash',
        category: 'secret_exposure',
        tool_name: toolName,
        command: scrubSecrets(command).slice(0, 200),
        session_id: sessionId,
        repo: gitRepo || cwd,
        cc_model: transcript.ccModel,
        reasoning: 'Literal credential (' + types + ') in the proposed command — blocked locally before grading; never sent to the model.',
      });
      outputJson({
        systemMessage: tagStr + ' bashGuard → BLOCKED: literal credential in command (' + types + '). Not sent to the grader; use an env var reference instead.',
        hookSpecificOutput: { hookEventName: 'PreToolUse', permissionDecision: 'deny', permissionDecisionReason: remediation, additionalContext: remediation },
      });
      return;
    }

    // Safe in-repo read fast-path — runs AFTER the secret firewall above so a
    // literal credential is never waved through as a "safe read".
    if (isSafeInRepoRead(toolName, command, cwd)) {
      log('bashGuard ' + cmdShort + ' → instant allow (safe in-repo read)');
      appendLocalTelemetry({
        capture_type: 'local_verdict',
        verdict: 'pass',
        hook_type: 'bash',
        category: 'safe_read',
        tool_name: toolName,
        command: command.slice(0, 200),
        session_id: sessionId,
        repo: gitRepo || cwd,
        cc_model: transcript.ccModel,
        reasoning: 'Safe in-repo read — auto-allowed without an LLM grade.',
      });
      outputJson({
        systemMessage: tagStr + ' bashGuard → pass: safe in-repo read',
        hookSpecificOutput: {
          hookEventName: 'PreToolUse',
          additionalContext: tagStr + ' bashGuard pass: safe in-repo read.',
        },
      });
      return;
    }

    // ─── Install protection: install-scan runs first and owns block traces ───
    let scanConcern = '';
    let scanBlockContext = '';
    if (toolName === 'Bash') {
      const scan = readCachedScan(command);
      if (scan?.action === 'block') {
        log('bashGuard deferring to install-scan block: ' + cmdShort);
        outputEmpty();
        return;
      }
      if (scan?.action === 'warn') {
        scanBlockContext = scan.blockContext || scan.summary || '';
        scanConcern = 'PACKAGE SCANNER WARNING: ' + scanBlockContext
          + ' Mention this to the user before proceeding.';
      }
    }

    const lastPrompt = readLastPrompt(sessionId);

    // jwt + config + rt + tagStr already loaded eagerly at top of main
    // (so the short-circuit could emit a properly-tagged message). Silent
    // mode was also checked up there.

    if (rt === 'local') {
      const sessionLog = await sessionLogForGrade(sessionId, config.rules);
      const relevantRules = await filterRules(
        ruleFilterText(command, transcript.userIntent || lastPrompt),
        config.rules,
      );
      const graderPrompt = [
        'Working directory: ' + (cwd || '.'),
        'Repo: ' + (gitRepo || 'unknown'),
        sessionLog,
        'Command: ' + command,
        'User intent (last human message): ' + (transcript.userIntent || 'none stated'),
        'Last user prompt: ' + (lastPrompt || 'none'),
        'Org rules: ' + JSON.stringify(relevantRules),
        scanConcern,
      ].filter(Boolean).join('\\n');

      let gradeResp: string;
      try {
        gradeResp = await localGrade('bash', graderPrompt, undefined, graderPool);
      } catch (err) {
        const errMsg = (err as Error).message || String(err);
        logGraderUnavailable('bashGuard', toolInput.command?.slice(0, 200) || '', errMsg, graderPrompt, { sessionId, repo: gitRepo });
        if (scanConcern) {
          const ctx = scanBlockContext + ' Synkro flagged this install but the grader is unavailable to check consent — ask the user for explicit consent, then retry.';
          outputJson({
            systemMessage: tagStr + ' bashGuard → install blocked (scan flagged; grader unavailable)',
            hookSpecificOutput: { hookEventName: 'PreToolUse', permissionDecision: 'deny', permissionDecisionReason: ctx, additionalContext: ctx },
          });
          return;
        }
        outputJson({ systemMessage: tagStr + ' ' + graderUnavailableMessage('bashGuard', cmdShort, errMsg) });
        return;
      }

      const verdict = parseVerdict(gradeResp);
      const violatedRules = verdict.ruleId ? [verdict.ruleId] : [];

      if (!verdict.ok) {
        const mode = normalizeMode(verdict.ruleMode || ruleMode(verdict.ruleId, config.rules));
        const guardReason = (verdict.ruleId ? '(' + verdict.ruleId + ') ' : '') + (verdict.reason || 'policy violation');

        const blockMsg = mode === 'fix'
          ? tagStr + ' bashGuard → blocked' + (verdict.ruleId ? ' (' + verdict.ruleId + ')' : '') + ': ' + (verdict.reason || 'policy violation') + '. Fix this before retrying — do not ask the user.'
          : tagStr + ' bashGuard → blocked' + (verdict.ruleId ? ' (' + verdict.ruleId + ')' : '') + ': ' + (verdict.reason || 'policy violation') + '. Ask the user for explicit consent before retrying.';
        outputJson({
          systemMessage: blockMsg,
          hookSpecificOutput: { hookEventName: 'PreToolUse', permissionDecision: 'deny', permissionDecisionReason: blockMsg, additionalContext: blockMsg },
        });
        dispatchCapture(jwt, 'bash', 'block', verdict.severity || 'critical', verdict.category || 'security',
          toolName, gitRepo, sessionId, config.captureDepth, {
            command, reasoning: guardReason, rulesChecked: relevantRules, violatedRules,
            recentUserMessages: transcript.recentUserMessages, ccModel: transcript.ccModel,
          });
      } else {
        const reason = tagStr + ' bashGuard → pass: ' + (verdict.reason || 'no policy violations detected');
        outputJson({
          systemMessage: reason,
          hookSpecificOutput: { hookEventName: 'PreToolUse', additionalContext: reason },
        });
        dispatchCapture(jwt, 'bash', 'pass', 'clean', verdict.category || 'trivial_utility',
          toolName, gitRepo, sessionId, config.captureDepth, {
            command, reasoning: verdict.reason || 'no policy violations detected',
            rulesChecked: relevantRules, violatedRules: [],
            recentUserMessages: transcript.recentUserMessages, ccModel: transcript.ccModel,
          });
      }
      return;
    }

    // ─── Cloud grading ───
    const isHeadless = ['acceptEdits', 'bypassPermissions', 'plan', 'auto'].includes(permissionMode)
      || process.env.SYNKRO_HEADLESS === '1';

    const body: Record<string, any> = {
      hook_event: 'PreToolUse',
      tool_name: toolName,
      tool_input: toolInput,
      user_intent: transcript.userIntent || null,
      last_user_message: lastPrompt || null,
      recent_user_messages: transcript.recentUserMessages,
      recent_messages: transcript.recentMessages,
      recent_actions: transcript.recentActions,
      session_history: await sessionLogForGrade(sessionId, config.rules),
      session_id: sessionId || null,
      tool_use_id: toolUseId || null,
      cwd: cwd || null,
      repo: gitRepo || null,
      permission_mode: permissionMode || null,
      headless: isHeadless,
      cc_model: transcript.ccModel || null,
      cc_usage: transcript.ccUsage || {},
      session_summary: transcript.sessionSummary || null,
    };

    if (scanConcern) {
      // Cloud grading does not carry the package-scanner concern through the
      // consent flow — block here with ask-mode messaging so the user can
      // consent and retry.
      const ctx = scanBlockContext + ' Ask the user for explicit consent before retrying.';
      outputJson({
        systemMessage: tagStr + ' bashGuard → install blocked: ' + cmdShort,
        hookSpecificOutput: { hookEventName: 'PreToolUse', permissionDecision: 'deny', permissionDecisionReason: ctx, additionalContext: ctx },
      });
      return;
    }

    const resp = await postWithRetry(GATEWAY_URL + '/api/v1/hook/judge', body, jwt, 8000);

    if (!resp) {
      log('bashGuard ' + cmdShort + ' → error (timeout)');
      outputEmpty();
      return;
    }

    if (!resp.hook_response || typeof resp.hook_response !== 'object') {
      log('bashGuard ' + cmdShort + ' → pass (no hook_response)');
      outputEmpty();
      return;
    }

    outputJson(resp.hook_response);
  } catch (err) {
    log('bashGuard error: ' + String(err));
    outputEmpty();
  }
}

main();
`;


// ─── CC PreToolUse Agent subagent judge (TypeScript) ───

export const AGENT_JUDGE_TS = `#!/usr/bin/env bun
import {
  loadJwt, ensureFreshJwt, detectRepo, loadConfig, route, tag, localGrade,
  parseVerdict, dispatchCapture, ruleMode, postWithRetry, readStdin,
  extractTranscript, readLastPrompt, appendSessionAction, readSessionLog, compressSessionLog, sessionLogForGrade, log,
  outputJson, outputEmpty, setupCursorHookSignals, installHookWatchdog, isAgentTool, hookSessionId, GATEWAY_URL,
  logGraderUnavailable, graderUnavailableMessage, filterRules, normalizeMode, resolveTranscriptPath, isCursorInvokingCcHook,
  loadSynkroFile, effectiveGraderPool, synkroFilePresent, noSynkroSkipMessage,
  type HookConfig, type Rule,
} from './_synkro-common.ts';

const agentKind = (process.env.SYNKRO_HOOK_FORMAT === 'cursor' || process.argv.includes('--cursor')) ? 'cursor' : 'claude_code';

async function main() {
  setupCursorHookSignals();
  installHookWatchdog(48000);
  try {
    const input = await readStdin();
    if (!input.trim()) { outputEmpty(); return; }

    const payload = JSON.parse(input);
    const toolName = payload.tool_name || '';
    if (!isAgentTool(toolName)) {
      outputEmpty();
      return;
    }

    const _m = String(payload.model ?? payload.model_id ?? '');
    if (isCursorInvokingCcHook(agentKind, _m)) { outputEmpty(); return; }

    const toolInput = payload.tool_input || {};
    const sessionId = hookSessionId(payload);
    const toolUseId = payload.tool_use_id || '';
    const workspaceRoots = Array.isArray(payload.workspace_roots) ? payload.workspace_roots as string[] : [];
    const cwd = payload.cwd || workspaceRoots[0] || '';
    const permissionMode = payload.permission_mode || '';
    const transcriptPath = resolveTranscriptPath(payload);

    if (!synkroFilePresent(cwd)) {
      const _skipMsg = noSynkroSkipMessage(sessionId);
      if (_skipMsg) outputJson({ systemMessage: _skipMsg, hookSpecificOutput: { hookEventName: 'PreToolUse', additionalContext: _skipMsg } });
      else outputEmpty();
      return;
    }

    const prompt = toolInput.prompt || '';
    const description = toolInput.description || '';
    const subagentType = toolInput.subagent_type || 'general-purpose';
    if (!prompt) { outputEmpty(); return; }

    const gitRepo = detectRepo(cwd, transcriptPath, prompt, workspaceRoots);

    appendSessionAction(sessionId, { ts: new Date().toISOString(), tool: 'Agent', summary: 'spawn ' + subagentType + ': ' + description.slice(0, 80) });

    const promptShort = prompt.slice(0, 80);
    log('agentGuard checking: ' + description + ' (' + subagentType + ')');

    let jwt = loadJwt();
    if (!jwt) { outputEmpty(); return; }
    jwt = await ensureFreshJwt(jwt);

    const transcript = extractTranscript(transcriptPath);
    const lastPrompt = readLastPrompt(sessionId);

    if (!transcript.ccModel) {
      const rawModel = String(payload.model ?? payload.model_id ?? '');
      if (agentKind === 'cursor') {
        transcript.ccModel = rawModel ? (rawModel.startsWith('cursor/') ? rawModel : 'cursor/' + rawModel) : 'cursor';
      } else {
        transcript.ccModel = rawModel || '';
      }
    }

    const config = await loadConfig(jwt);
    const synkroFile = loadSynkroFile(cwd);
    const graderPool = effectiveGraderPool(synkroFile, agentKind, config);
    const rt = await route(config, synkroFile);
    const tagStr = tag(rt, config, graderPool);

    if (config.silent) {
      const msg = tagStr + ' agentGuard → skipped (silent mode)';
      outputJson({ systemMessage: msg, hookSpecificOutput: { hookEventName: 'PreToolUse', additionalContext: msg } });
      return;
    }

    if (rt === 'local') {
      const sessionLog = await sessionLogForGrade(sessionId, config.rules);
      const agentText = 'agent=' + subagentType + ' description=' + description + ' prompt=' + prompt.slice(0, 2000);
      const relevantRules = await filterRules(agentText, config.rules);
      const graderPrompt = [
        'Working directory: ' + (cwd || '.'),
        'Repo: ' + (gitRepo || 'unknown'),
        sessionLog,
        'Tool: Agent (subagent spawn)',
        'Subagent type: ' + subagentType,
        'Description: ' + description,
        'Subagent prompt (first 4000 chars):',
        prompt.slice(0, 4000),
        'User intent (last human message): ' + (transcript.userIntent || 'none stated'),
        'Last user prompt: ' + (lastPrompt || 'none'),
        'Org rules: ' + JSON.stringify(relevantRules),
      ].filter(Boolean).join('\\n');

      let gradeResp: string;
      try {
        gradeResp = await localGrade('bash', graderPrompt, undefined, graderPool);
      } catch (err) {
        const errMsg = (err as Error).message || String(err);
        logGraderUnavailable('agentGuard', subagentType || (description || '').slice(0, 100), errMsg, graderPrompt, { sessionId, repo: gitRepo });
        outputJson({ systemMessage: tagStr + ' ' + graderUnavailableMessage('agentGuard', subagentType || 'agent', errMsg, graderPool) });
        return;
      }

      const verdict = parseVerdict(gradeResp);
      const agentContent = 'agent=' + subagentType + ' desc=' + description + ' prompt=' + prompt.slice(0, 2000);
      const violatedRules = verdict.ruleId ? [verdict.ruleId] : [];

      if (!verdict.ok) {
        const mode = normalizeMode(verdict.ruleMode || ruleMode(verdict.ruleId, config.rules));
        const guardReason = (verdict.ruleId ? '(' + verdict.ruleId + ') ' : '') + (verdict.reason || 'policy violation');

        const reason = mode === 'fix'
          ? tagStr + ' agentGuard → blocked' + (verdict.ruleId ? ' (' + verdict.ruleId + ')' : '') + ': ' + (verdict.reason || 'policy violation') + '. Fix this before retrying — do not ask the user.'
          : tagStr + ' agentGuard → blocked' + (verdict.ruleId ? ' (' + verdict.ruleId + ')' : '') + ': ' + (verdict.reason || 'policy violation') + '. Ask the user for explicit consent before retrying.';
        outputJson({
          systemMessage: reason,
          hookSpecificOutput: { hookEventName: 'PreToolUse', permissionDecision: 'deny', permissionDecisionReason: reason, additionalContext: reason },
        });
        dispatchCapture(jwt, 'agent', 'block', verdict.severity || 'critical', verdict.category || 'security',
          toolName, gitRepo, sessionId, config.captureDepth, {
            command: agentContent, reasoning: guardReason, rulesChecked: relevantRules, violatedRules,
            recentUserMessages: transcript.recentUserMessages, ccModel: transcript.ccModel,
          });
      } else {
        const reason = tagStr + ' agentGuard → pass: ' + (verdict.reason || 'no policy violations detected');
        outputJson({ systemMessage: reason, hookSpecificOutput: { hookEventName: 'PreToolUse', additionalContext: reason } });
        dispatchCapture(jwt, 'agent', 'pass', 'clean', verdict.category || 'subagent_spawn',
          toolName, gitRepo, sessionId, config.captureDepth, {
            command: agentContent, reasoning: verdict.reason || 'no policy violations detected',
            rulesChecked: relevantRules, violatedRules: [],
            recentUserMessages: transcript.recentUserMessages, ccModel: transcript.ccModel,
          });
      }
      return;
    }

    // ─── Cloud grading ───
    const isHeadless = ['acceptEdits', 'bypassPermissions', 'plan', 'auto'].includes(permissionMode)
      || process.env.SYNKRO_HEADLESS === '1';

    const body: Record<string, any> = {
      hook_event: 'PreToolUse',
      tool_name: toolName,
      tool_input: toolInput,
      user_intent: transcript.userIntent || null,
      last_user_message: lastPrompt || null,
      recent_user_messages: transcript.recentUserMessages,
      recent_messages: transcript.recentMessages,
      recent_actions: transcript.recentActions,
      session_history: await sessionLogForGrade(sessionId, config.rules),
      session_id: sessionId || null,
      tool_use_id: toolUseId || null,
      cwd: cwd || null,
      repo: gitRepo || null,
      permission_mode: permissionMode || null,
      headless: isHeadless,
      cc_model: transcript.ccModel || null,
      cc_usage: transcript.ccUsage || {},
      session_summary: transcript.sessionSummary || null,
    };

    const resp = await postWithRetry(GATEWAY_URL + '/api/v1/hook/judge', body, jwt, 8000);

    if (!resp) {
      log('agentGuard ' + promptShort + ' → error (timeout)');
      outputEmpty();
      return;
    }

    if (!resp.hook_response || typeof resp.hook_response !== 'object') {
      log('agentGuard ' + promptShort + ' → pass (no hook_response)');
      outputEmpty();
      return;
    }

    outputJson(resp.hook_response);
  } catch (err) {
    log('agentGuard error: ' + String(err));
    outputEmpty();
  }
}

main();
`;


// ─── CC PreToolUse ExitPlanMode plan review (TypeScript) ───

export const PLAN_JUDGE_TS = `#!/usr/bin/env bun
import {
  loadJwt, ensureFreshJwt, detectRepo, loadConfig, route, tag, localGrade,
  parseVerdict, dispatchCapture, appendSessionAction, readSessionLog, compressSessionLog, sessionLogForGrade, postWithRetry, readStdin, log,
  outputJson, outputEmpty, setupCursorHookSignals, installHookWatchdog, isPlanTool, hookSessionId, GATEWAY_URL,
  filterRules, graderUnavailableMessage, logGraderUnavailable, resolveTranscriptPath, isCursorInvokingCcHook,
  loadSynkroFile, effectiveGraderPool, synkroFilePresent, noSynkroSkipMessage,
} from './_synkro-common.ts';
import { existsSync, readFileSync, writeFileSync, readdirSync, statSync } from 'node:fs';
import { join } from 'node:path';
import { homedir } from 'node:os';

const agentKind = (process.env.SYNKRO_HOOK_FORMAT === 'cursor' || process.argv.includes('--cursor')) ? 'cursor' : 'claude_code';

function findLatestPlanInDir(plansDir: string): string | null {
  if (!existsSync(plansDir)) return null;
  try {
    const files = readdirSync(plansDir)
      .filter(f => f.endsWith('.md'))
      .map(f => ({ name: f, mtime: statSync(join(plansDir, f)).mtimeMs }))
      .sort((a, b) => b.mtime - a.mtime);
    return files.length > 0 ? join(plansDir, files[0].name) : null;
  } catch {
    return null;
  }
}

function findLatestPlan(): string | null {
  const dirs = [
    join(homedir(), '.claude', 'plans'),
    join(homedir(), '.cursor', 'plans'),
  ];
  let best: { path: string; mtime: number } | null = null;
  for (const dir of dirs) {
    const p = findLatestPlanInDir(dir);
    if (!p) continue;
    try {
      const mtime = statSync(p).mtimeMs;
      if (!best || mtime > best.mtime) best = { path: p, mtime };
    } catch {}
  }
  return best?.path ?? null;
}

function appendReviewToPlan(planFile: string, verdict: string): void {
  try {
    let content = readFileSync(planFile, 'utf-8');
    content = content.replace(/<!-- synkro-plan-review -->[\\s\\S]*?<!-- \\/synkro-plan-review -->/g, '').trimEnd();
    const now = new Date().toISOString().replace('T', ' ').slice(0, 16);
    content += '\\n\\n<!-- synkro-plan-review -->\\n\\n---\\n\\n**Synkro Plan Review** \\u2014 ' + now + '\\n\\n' + verdict + '\\n\\n<!-- /synkro-plan-review -->\\n';
    writeFileSync(planFile, content, 'utf-8');
  } catch {}
}

async function main() {
  setupCursorHookSignals();
  installHookWatchdog(48000);
  try {
    const input = await readStdin();
    if (!input.trim()) { outputEmpty(); return; }

    const payload = JSON.parse(input);
    const toolName = payload.tool_name || '';
    if (!isPlanTool(toolName)) { outputEmpty(); return; }

    const _m = String(payload.model ?? payload.model_id ?? '');
    if (isCursorInvokingCcHook(agentKind, _m)) { outputEmpty(); return; }

    const planFile = findLatestPlan();
    if (!planFile) { outputEmpty(); return; }
    const plan = readFileSync(planFile, 'utf-8');
    if (plan.length < 20) { outputEmpty(); return; }

    const sessionId = hookSessionId(payload);
    const workspaceRoots = Array.isArray(payload.workspace_roots) ? payload.workspace_roots as string[] : [];
    const cwd = payload.cwd || workspaceRoots[0] || '';
    const transcriptPath = resolveTranscriptPath(payload);
    const gitRepo = detectRepo(cwd, transcriptPath, plan, workspaceRoots);

    if (!synkroFilePresent(cwd)) {
      const _skipMsg = noSynkroSkipMessage(sessionId);
      if (_skipMsg) outputJson({ systemMessage: _skipMsg, hookSpecificOutput: { hookEventName: 'PreToolUse', additionalContext: _skipMsg } });
      else outputEmpty();
      return;
    }

    appendSessionAction(sessionId, { ts: new Date().toISOString(), tool: 'ExitPlanMode', summary: 'plan review: ' + plan.slice(0, 80) });

    const planShort = plan.slice(0, 80);
    log('planReview checking: ' + planShort + '...');

    let jwt = loadJwt();
    if (!jwt) { outputEmpty(); return; }
    jwt = await ensureFreshJwt(jwt);

    const rawModel = String(payload.model ?? payload.model_id ?? '');
    const ccModel = agentKind === 'cursor' ? (rawModel ? (rawModel.startsWith('cursor/') ? rawModel : 'cursor/' + rawModel) : 'cursor') : (rawModel || '');

    const config = await loadConfig(jwt);
    const synkroFile = loadSynkroFile(cwd);
    const graderPool = effectiveGraderPool(synkroFile, agentKind, config);
    const rt = await route(config, synkroFile);
    const tagStr = tag(rt, config, graderPool);

    if (config.silent) {
      outputJson({ systemMessage: tagStr + ' planReview → skipped (silent mode)' });
      return;
    }

    if (rt === 'local') {
      const sessionLog = await sessionLogForGrade(sessionId, config.rules);
      const relevantRules = await filterRules(plan.slice(0, 2000), config.rules);
      const graderPrompt = [
        'Working directory: ' + (cwd || '.'),
        'Repo: ' + (gitRepo || 'unknown'),
        sessionLog,
        'Plan:',
        plan.slice(0, 8000),
        'Org rules: ' + JSON.stringify(relevantRules),
      ].filter(Boolean).join('\\n');

      let gradeResp: string;
      try {
        gradeResp = await localGrade('plan', graderPrompt, undefined, graderPool);
      } catch (err) {
        const errMsg = (err as Error).message || String(err);
        logGraderUnavailable('planReview', 'plan', errMsg, graderPrompt, { sessionId, repo: gitRepo });
        outputJson({ systemMessage: tagStr + ' ' + graderUnavailableMessage('planReview', 'plan', errMsg, graderPool) });
        return;
      }

      const verdict = parseVerdict(gradeResp);
      const planContent = plan.slice(0, 2000);
      const violatedRules = verdict.ruleId ? [verdict.ruleId] : [];

      if (!verdict.ok) {
        const reviewMsg = (verdict.ruleId ? '(first: ' + verdict.ruleId + ') ' : '') + (verdict.reason || 'check org rules during implementation');
        appendReviewToPlan(planFile, '\\u26a0\\ufe0f Advisory \\u2014 ' + reviewMsg);
        const advLine = tagStr + ' planReview → ' + reviewMsg;
        outputJson({ systemMessage: advLine, hookSpecificOutput: { hookEventName: 'PreToolUse', additionalContext: 'Synkro local plan judge (advisory). ' + reviewMsg } });
        dispatchCapture(jwt, 'plan_review', 'advisory', verdict.severity || 'medium', verdict.category || 'general',
          'ExitPlanMode', gitRepo, sessionId, config.captureDepth, {
            command: planContent, reasoning: verdict.reason || 'check org rules',
            rulesChecked: relevantRules, violatedRules, ccModel: ccModel || undefined,
          });
      } else {
        const reviewMsg = verdict.reason || 'no relevant org rules for this plan';
        appendReviewToPlan(planFile, '\\u2705 Clean \\u2014 ' + reviewMsg);
        const cleanLine = tagStr + ' planReview → clean: ' + reviewMsg;
        outputJson({ systemMessage: cleanLine, hookSpecificOutput: { hookEventName: 'PreToolUse', additionalContext: 'Synkro local plan judge. ' + reviewMsg } });
        dispatchCapture(jwt, 'plan_review', 'clean', 'clean', verdict.category || 'general',
          'ExitPlanMode', gitRepo, sessionId, config.captureDepth, {
            command: planContent, reasoning: reviewMsg,
            rulesChecked: relevantRules, violatedRules: [], ccModel: ccModel || undefined,
          });
      }
      return;
    }

    // ─── Cloud grading ───
    const body = {
      hook_event: 'PreToolUse',
      tool_name: 'ExitPlanMode',
      tool_input: { plan: plan.slice(0, 16000) },
      session_id: sessionId || null,
      cwd: cwd || null,
      repo: gitRepo || null,
    };

    const resp = await postWithRetry(GATEWAY_URL + '/api/v1/hook/judge', body, jwt, 12000);

    if (!resp) {
      log('planReview → error (timeout)');
      outputEmpty();
      return;
    }

    const hookResp = resp?.hook_response;
    if (!hookResp) { outputEmpty(); return; }

    const decision = hookResp?.hookSpecificOutput?.permissionDecision;
    if (decision) {
      const reason = hookResp?.hookSpecificOutput?.permissionDecisionReason || 'check org rules';
      appendReviewToPlan(planFile, '\\u26a0\\ufe0f Advisory \\u2014 ' + reason);
      outputJson({ systemMessage: tagStr + ' planReview → advisory: ' + reason });
    } else {
      const cloudMsg = hookResp.systemMessage || '';
      if (cloudMsg) appendReviewToPlan(planFile, '\\u2705 ' + cloudMsg);
      outputJson(hookResp);
    }
  } catch (err) {
    log('planReview error: ' + String(err));
    outputEmpty();
  }
}

main();
`;


// ─── CC SessionEnd stop summary (TypeScript) ───

export const STOP_SUMMARY_TS = `#!/usr/bin/env bun
import {
  loadJwt, detectRepo, loadConfig, tag, readStdin, aggregateUsage, cleanupSessionLog,
  outputJson, outputEmpty, shipCloud, setupCursorHookSignals, hookSessionId, GATEWAY_URL,
  resolveTranscriptPath, emitUsageTick, cursorModelFromPayload, log, synkroFilePresent,
  cloudTaskComplete,
} from './_synkro-common.ts';

async function main() {
  setupCursorHookSignals();
  try {
    const input = await readStdin();
    if (!input.trim()) { outputEmpty(); return; }

    const payload = JSON.parse(input);
    const sessionId = hookSessionId(payload);
    if (!sessionId) { outputEmpty(); return; }

    const workspaceRoots = Array.isArray(payload.workspace_roots) ? payload.workspace_roots as string[] : [];
    const cwd = payload.cwd || workspaceRoots[0] || '';
    if (!synkroFilePresent(cwd)) { outputEmpty(); return; }
    const transcriptPath = resolveTranscriptPath(payload);
    const gitRepo = detectRepo(cwd, transcriptPath, '', workspaceRoots);
    const modelFallback = cursorModelFromPayload(payload);

    let jwt = loadJwt();
    if (!jwt) { outputEmpty(); return; }

    if (transcriptPath) {
      const usage = aggregateUsage(transcriptPath, { modelFallback });
      emitUsageTick({ sessionId, usage, hookType: 'session_end', gitRepo, modelFallback });
    }

    let resp: any;
    try {
      const r = await fetch(GATEWAY_URL + '/api/v1/cli/session-summary?session_id=' + encodeURIComponent(sessionId), {
        headers: { Authorization: 'Bearer ' + jwt },
        signal: AbortSignal.timeout(3000),
      });
      resp = await r.json();
    } catch {
      outputEmpty();
      return;
    }

    const edits = resp?.edits_scanned || 0;
    const findings = resp?.findings || 0;
    const autoFixed = resp?.auto_fixed || 0;
    const open = resp?.open || 0;

    if (!edits) { cleanupSessionLog(sessionId); outputEmpty(); return; }

    const config = await loadConfig(jwt);
    const tagStr = tag('local', config);

    cleanupSessionLog(sessionId);

    // Cloud FINAL CHECK: the agent stopped after editing — sweep the active task,
    // verify EVERY requirement against the actual written code, and retire if all met.
    // Returns '' when there's no active task (cheap getActiveTask short-circuit) or in
    // local mode. stop_hook_active passes through so handleTaskComplete's stop-loop guard
    // works (it bails when already inside a stop loop).
    const taskMsg = process.env.SYNKRO_DEPLOY_LOCATION === 'cloud'
      ? await cloudTaskComplete({ payload: { stop_hook_active: payload.stop_hook_active === true }, harness: 'claude_code', cwd: gitRepo, sessionId }, jwt)
      : '';
    const taskSuffix = taskMsg ? '\\n' + taskMsg : '';

    if (!findings) {
      outputJson({ systemMessage: tagStr + ' stop → 0 issues across ' + edits + ' edit(s), session complete' + taskSuffix });
    } else {
      outputJson({ systemMessage: tagStr + ' stop → ' + findings + ' finding(s): ' + autoFixed + ' auto-fixed, ' + open + ' open' + taskSuffix });
    }
  } catch (err) {
    log('stopSummary error: ' + String(err));
    outputEmpty();
  }
}

main();
`;


// ─── CC SessionStart (TypeScript) ───

export const SESSION_START_TS = `#!/usr/bin/env bun
import {
  loadJwt, detectRepo, channelUp, tag, readStdin, writeCachedRepo,
  outputJson, outputEmpty, setupCursorHookSignals, hookSessionId, resolveTranscriptPath, GATEWAY_URL,
  isLocalStorageMode, loadSynkroFile, log, synkroFilePresent, fetchMiningStatusMessage, type HookConfig,
} from './_synkro-common.ts';

async function main() {
  setupCursorHookSignals();
  try {
    const input = await readStdin();
    if (!input.trim()) { outputEmpty(); return; }

    const payload = JSON.parse(input);
    const workspaceRoots = Array.isArray(payload.workspace_roots) ? payload.workspace_roots as string[] : [];
    const cwd = payload.cwd || workspaceRoots[0] || '';
    if (!synkroFilePresent(cwd)) { outputEmpty(); return; }
    const transcriptPath = resolveTranscriptPath(payload);
    const sessionId = hookSessionId(payload);
    const gitRepo = detectRepo(cwd, transcriptPath, '', workspaceRoots);
    if (gitRepo) writeCachedRepo(gitRepo);

    let jwt = loadJwt();

    const isChannelUp = await channelUp();
    const synkroFile = loadSynkroFile(cwd);
    const gradingMode = synkroFile.grader.mode || process.env.SYNKRO_GRADING_MODE || 'local';
    const deployCloud = process.env.SYNKRO_DEPLOY_LOCATION === 'cloud';
    const rt = gradingMode === 'byok' ? 'cloud' : (isChannelUp || deployCloud ? 'local' : 'cloud');

    let policyName = '';
    let silent = false;
    let openFindings = 0;

    if (isLocalStorageMode()) {
      try {
        const mcpPort = process.env.SYNKRO_MCP_PORT || '18931';
        const r = await fetch('http://127.0.0.1:' + mcpPort + '/api/local/hook-config', {
          signal: AbortSignal.timeout(1500),
        });
        if (r.ok) {
          const data = await r.json() as any;
          silent = data.silent === true;
          policyName = data.policy?.name || '';
        }
      } catch {}
    } else if (jwt) {
      try {
        const url = GATEWAY_URL + '/api/v1/hook/config?session_id=' + encodeURIComponent(sessionId || '') + '&repo=' + encodeURIComponent(gitRepo || '');
        const r = await fetch(url, {
          headers: { Authorization: 'Bearer ' + jwt },
          signal: AbortSignal.timeout(3000),
        });
        const data = await r.json() as any;
        silent = data.silent_mode === true || data.silent_mode === 'true';
        policyName = data.active_policy_name || '';
        openFindings = data.session_context?.open_findings || 0;
      } catch {}
    }

    const fakeConfig: HookConfig = { captureDepth: 'local_only', tier: 'standard', silent, policyName, rules: [], scanExemptions: [], gradingMode, storageMode: process.env.SYNKRO_STORAGE_MODE || 'local' };
    const tagStr = tag(rt, fakeConfig);
    const routeLine = tagStr + ' inference: ' + (gradingMode === 'byok' ? 'cloud (BYOK)' : deployCloud ? 'cloud container' : isChannelUp ? 'local-cc (channel reachable on 127.0.0.1:18929)' : 'cloud (local-cc channel not reachable)');

    if (!jwt) {
      outputJson({ systemMessage: routeLine });
      return;
    }

    // Sync the canonical 3-state grading location to the API profile so the
    // dashboard reflects the machine's ACTUAL config. byok (own API key) wins;
    // else the cloud worker pool; else on-device workers. The API derives the
    // legacy fast_inference boolean from this, so we only send one field.
    const inferenceMode = gradingMode === 'byok' ? 'byok' : (deployCloud ? 'cloud' : 'local');
    fetch(GATEWAY_URL + '/api/v1/cli/me', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + jwt },
      body: JSON.stringify({ inference_mode: inferenceMode }),
      signal: AbortSignal.timeout(3000),
    }).catch(() => {});

    let sys = routeLine;
    if (openFindings === 1) sys += '\\n' + tagStr + ' session start → 1 open finding in this repo from a prior session.';
    else if (openFindings) sys += '\\n' + tagStr + ' session start → ' + openFindings + ' open findings in this repo from prior sessions.';
    // Rule-mining status (initial sync). Empty unless a job is active / just finished.
    const miningMsg = await fetchMiningStatusMessage(sessionId);
    if (miningMsg) sys += '\\n' + miningMsg;
    outputJson({ systemMessage: sys });
  } catch (err) {
    log('sessionStart error: ' + String(err));
    outputEmpty();
  }
}

main();
`;


// ─── CC PostToolUse Bash followup (TypeScript) ───

export const BASH_FOLLOWUP_TS = `#!/usr/bin/env bun
import {
  loadJwt, loadConfig, readStdin, hashCommand, consentGrant, consentHasActive, consentConsume,
  appendSessionAction,
  outputEmpty, appendLocalTelemetry, shipCloud, setupCursorHookSignals, isShellTool, hookSessionId, GATEWAY_URL, synkroFilePresent,
} from './_synkro-common.ts';

async function main() {
  setupCursorHookSignals();
  try {
    const input = await readStdin();
    if (!input.trim()) { outputEmpty(); return; }

    const payload = JSON.parse(input);
    const _cwd = payload.cwd || (Array.isArray(payload.workspace_roots) ? payload.workspace_roots[0] : '') || '';
    if (!synkroFilePresent(_cwd)) { outputEmpty(); return; }
    const toolName = payload.tool_name || '';
    const shellCmd = typeof payload.command === 'string' ? payload.command : (payload.tool_input?.command || '');
    if (!isShellTool(toolName) && !shellCmd) { outputEmpty(); return; }

    const jwt = loadJwt();
    if (!jwt) { outputEmpty(); return; }

    const sessionId = hookSessionId(payload);
    const toolUseId = payload.tool_use_id || payload.tool_call_id || 'cursor-shell';
    if (!sessionId) { outputEmpty(); return; }

    let isError = payload.tool_result?.is_error === true;
    try {
      const out = JSON.parse(payload.tool_output || '{}');
      if ((typeof out.exitCode === 'number' && out.exitCode !== 0) || out.is_error === true) isError = true;
    } catch {}
    const cmd = shellCmd;
    const cmdHash = cmd ? hashCommand(cmd) : '';

    if (cmdHash && sessionId) {
      if (!isError) {
        consentConsume(sessionId, cmdHash);
      } else {
        if (!consentHasActive(sessionId, cmdHash)) {
          consentGrant(sessionId, cmdHash);
        }
      }
    }

    appendSessionAction(sessionId, { ts: new Date().toISOString(), tool: 'Bash', summary: cmd.slice(0, 120), outcome: isError ? 'exit 1' : 'exit 0' });

    const body = {
      capture_type: 'bash_followup',
      session_id: sessionId,
      tool_use_id: toolUseId,
      is_error: isError,
      command_hash: cmdHash,
    };

    appendLocalTelemetry(body);
    shipCloud(jwt, '/api/v1/hook/capture', body);

    outputEmpty();
  } catch {
    outputEmpty();
  }
}

main();
`;


// ─── CC Stop transcript sync (TypeScript) ───

export const TRANSCRIPT_SYNC_TS = `#!/usr/bin/env bun
import {
  loadJwt, detectRepo, readStdin, aggregateUsage, appendLocalTelemetry,
  outputEmpty, outputJson, setupCursorHookSignals, hookSessionId, GATEWAY_URL, readSessionLog, shipCloud,
  resolveTranscriptPath, syncConversationTranscript, emitUsageTick, cursorModelFromPayload, synkroFilePresent,
  fetchMiningStatusMessage,
} from './_synkro-common.ts';
import { readFileSync } from 'node:fs';

async function main() {
  setupCursorHookSignals();
  try {
    const input = await readStdin();
    if (!input.trim()) { outputEmpty(); return; }

    const payload = JSON.parse(input);
    const sessionId = hookSessionId(payload);
    const workspaceRoots = Array.isArray(payload.workspace_roots) ? payload.workspace_roots as string[] : [];
    const cwd = payload.cwd || workspaceRoots[0] || '';
    if (!synkroFilePresent(cwd)) { outputEmpty(); return; }
    const transcriptPath = resolveTranscriptPath(payload);

    if (!sessionId || !transcriptPath) {
      outputEmpty();
      return;
    }

    const jwt = loadJwt();
    if (!jwt) { outputEmpty(); return; }

    const usage = aggregateUsage(transcriptPath, { modelFallback: cursorModelFromPayload(payload) });
    emitUsageTick({
      sessionId,
      usage,
      hookType: 'stop',
      gitRepo: detectRepo(cwd, transcriptPath, '', workspaceRoots),
      modelFallback: cursorModelFromPayload(payload),
    });

    const cloudConsent = process.env.SYNKRO_TRANSCRIPT_CONSENT !== 'no';
    const gitRepo = detectRepo(cwd, transcriptPath, '', workspaceRoots);

    const { messages } = await syncConversationTranscript(sessionId, transcriptPath, gitRepo || '');

    if (cloudConsent && gitRepo && messages.length > 0 && (process.env.SYNKRO_STORAGE_MODE || 'local') === 'cloud') {
      fetch(GATEWAY_URL + '/api/v1/cli/sync-transcripts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + jwt },
        body: JSON.stringify({
          repo: gitRepo,
          sessions: [{ cc_session_id: sessionId, messages, actions: readSessionLog(sessionId) }],
        }),
        signal: AbortSignal.timeout(10000),
      }).catch(() => {});
    }

    // Rule-mining status — the ~50-msg sync cadence runs through this hook, so this is
    // where a "following run" surfaces. Empty unless a job is active / just finished,
    // so an idle pool prints nothing.
    const miningMsg = await fetchMiningStatusMessage(sessionId);
    if (miningMsg) { outputJson({ systemMessage: miningMsg }); return; }
    outputEmpty();
  } catch {
    outputEmpty();
  }
}

main();
`;


// ─── CC UserPromptSubmit (TypeScript) ───

export const USER_PROMPT_SUBMIT_TS = `#!/usr/bin/env bun
import { readStdin, appendLocalTelemetry, aggregateUsage, outputEmpty, setupCursorHookSignals, hookSessionId, detectRepo, resolveTranscriptPath, syncConversationTranscript, emitUsageTick, cursorModelFromPayload, synkroFilePresent } from './_synkro-common.ts';
import { writeFileSync, mkdirSync } from 'node:fs';
import { join, dirname } from 'node:path';
import { homedir } from 'node:os';

async function main() {
  setupCursorHookSignals();
  try {
    const input = await readStdin();
    if (!input.trim()) { outputEmpty(); return; }
    const payload = JSON.parse(input);
    const _cwd = typeof payload.cwd === 'string' ? payload.cwd
      : (Array.isArray(payload.workspace_roots) && typeof payload.workspace_roots[0] === 'string' ? payload.workspace_roots[0] : '');
    if (!synkroFilePresent(_cwd)) { outputEmpty(); return; }
    const msg = typeof payload.message === 'string' ? payload.message
      : typeof payload.prompt === 'string' ? payload.prompt
      : typeof payload.content === 'string' ? payload.content : '';
    if (msg) {
      const promptFile = join(homedir(), '.synkro', '.last-prompt');
      mkdirSync(dirname(promptFile), { recursive: true, mode: 0o700 });
      writeFileSync(promptFile, msg, { encoding: 'utf-8', mode: 0o600 });
      const sid = hookSessionId(payload);
      if (sid) {
        const sessDir = join(homedir(), '.synkro', 'sessions');
        mkdirSync(sessDir, { recursive: true, mode: 0o700 });
        writeFileSync(join(sessDir, sid + '.last-prompt'), msg, { encoding: 'utf-8', mode: 0o600 });
      }
    }

    const sessionId = hookSessionId(payload);
    const workspaceRoots = Array.isArray(payload.workspace_roots) ? payload.workspace_roots as string[] : [];
    const cwd = payload.cwd || workspaceRoots[0] || '';
    const transcriptPath = resolveTranscriptPath(payload);
    if (sessionId && transcriptPath) {
      const gitRepo = detectRepo(cwd, transcriptPath, '', workspaceRoots);
      syncConversationTranscript(sessionId, transcriptPath, gitRepo || '').catch(() => {});
      const modelFallback = cursorModelFromPayload(payload);
      const usage = aggregateUsage(transcriptPath, { modelFallback });
      emitUsageTick({ sessionId, usage, hookType: 'prompt_submit', gitRepo, modelFallback });
    }
    // Nudge the local container to refresh account-wide coding-agent usage OUT OF BAND.
    // The endpoint returns 202 immediately and does its own Anthropic ping, so this
    // never gates the prompt; a stopped container just fails the fetch fast (caught).
    try {
      const usagePort = process.env.SYNKRO_MCP_HOST_PORT || '18931';
      await fetch('http://127.0.0.1:' + usagePort + '/api/local/usage-poll', {
        method: 'POST', signal: AbortSignal.timeout(1200),
      }).catch(() => {});
    } catch {}
    outputEmpty();
  } catch {
    outputEmpty();
  }
}

main();
`;


// ─── Cursor IDE TypeScript adapter scripts ───

export const CURSOR_BASH_JUDGE_TS = `#!/usr/bin/env bun
import {
  loadJwt, ensureFreshJwt, detectRepo, loadConfig, route, tag, localGrade,
  parseVerdict, dispatchCapture, dispatchFinding, ruleMode, normalizeMode, filterRules, ruleFilterText,
  isSafeInRepoRead, resolveTranscriptPath, postWithRetry, readStdin, hashCommand,
  detectHardSecrets, secretRemediation, scrubSecrets,
  extractTranscript, readLastPrompt, readSessionLog, compressSessionLog, sessionLogForGrade,
  appendLocalTelemetry, logGraderUnavailable, graderUnavailableMessage, log, GATEWAY_URL,
  loadSynkroFile, effectiveGraderPool, synkroFilePresent,
  type Rule,
} from './_synkro-common.ts';
import { createHash } from 'node:crypto';
import { existsSync, statSync, readFileSync, writeFileSync, mkdirSync, unlinkSync } from 'node:fs';
import { join } from 'node:path';

const SCAN_CACHE_DIR = (process.env.HOME || '/tmp') + '/.synkro/.scan-cache';

function readCachedScan(command: string): any | null {
  try {
    const path = join(SCAN_CACHE_DIR, hashCommand(command));
    if (!existsSync(path)) return null;
    const data = JSON.parse(readFileSync(path, 'utf-8'));
    unlinkSync(path);
    return data;
  } catch { return null; }
}

const DEDUP_DIR = process.env.HOME + '/.synkro/.dedup';
const DEDUP_TTL_MS = 3000;

function isDuplicate(command: string, sessionId: string): boolean {
  const hash = createHash('md5').update(sessionId + ':' + command).digest('hex').slice(0, 12);
  const marker = DEDUP_DIR + '/' + hash;
  try {
    if (existsSync(marker)) {
      const age = Date.now() - statSync(marker).mtimeMs;
      if (age < DEDUP_TTL_MS) return true;
    }
  } catch {}
  try {
    mkdirSync(DEDUP_DIR, { recursive: true });
    writeFileSync(marker, '', { flag: 'w' });
  } catch {}
  return false;
}

// Cursor hook timeouts now match CC (30s for bash/edit/agent), so use the same
// 24s internal grade budget as the CC path — stays under the 30s hook timeout
// (JWT refresh + grade) and fails open cleanly via the caller's catch.
const CURSOR_GRADE_TIMEOUT_MS = 24000;
const CURSOR_CLOUD_TIMEOUT_MS = 9000;

let hookDone = false;

function finishAllow(): never {
  if (!hookDone) {
    hookDone = true;
    try { process.stdout.write('{}\\n'); } catch {}
  }
  process.exit(0);
}

function finishWith(payload: Record<string, unknown>): never {
  hookDone = true;
  process.stdout.write(JSON.stringify(payload) + '\\n');
  process.exit(0);
}

process.on('SIGTERM', () => finishAllow());

const SHELL_TOOL_NAMES = new Set(['Bash', 'Shell', 'terminal', 'run_terminal_cmd', 'execute_command']);
const READ_TOOL_NAMES = new Set(['Read', 'ReadFile', 'read_file']);
const SEARCH_TOOL_NAMES = new Set(['Grep', 'grep_search', 'codebase_search', 'file_search']);
const DIR_TOOL_NAMES = new Set(['Glob', 'list_dir']);
const DELETE_TOOL_NAMES = new Set(['delete_file']);
const BASH_PRE_TOOL_NAMES = new Set([...SHELL_TOOL_NAMES, ...READ_TOOL_NAMES, ...SEARCH_TOOL_NAMES, ...DIR_TOOL_NAMES, ...DELETE_TOOL_NAMES]);

function extractCommand(payload: Record<string, unknown>): { command: string; toolName: string } {
  const direct = typeof payload.command === 'string' ? payload.command : '';
  if (direct) return { command: direct, toolName: 'Bash' };

  const toolName = typeof payload.tool_name === 'string' ? payload.tool_name : '';
  if (!BASH_PRE_TOOL_NAMES.has(toolName)) return { command: '', toolName };

  const toolInput = (payload.tool_input && typeof payload.tool_input === 'object')
    ? payload.tool_input as Record<string, unknown>
    : {};

  let command = '';
  if (SHELL_TOOL_NAMES.has(toolName)) {
    command = String(toolInput.command ?? '');
  } else if (READ_TOOL_NAMES.has(toolName)) {
    command = 'cat ' + String(toolInput.file_path ?? toolInput.path ?? '');
  } else if (SEARCH_TOOL_NAMES.has(toolName)) {
    command = "grep -r '" + String(toolInput.pattern ?? toolInput.query ?? '') + "' " + String(toolInput.path ?? '.');
  } else if (DIR_TOOL_NAMES.has(toolName)) {
    command = "find . -name '" + String(toolInput.pattern ?? toolInput.relative_workspace_path ?? '') + "'";
  } else if (DELETE_TOOL_NAMES.has(toolName)) {
    command = 'rm ' + String(toolInput.target_file ?? toolInput.file_path ?? toolInput.path ?? '');
  }
  return { command, toolName: toolName || 'Bash' };
}

async function main() {
  try {
    const input = await readStdin();
    if (!input.trim()) finishAllow();

    const payload = JSON.parse(input) as Record<string, unknown>;
    const { command, toolName } = extractCommand(payload);
    if (!command) finishAllow();

    const workspaceRoots = Array.isArray(payload.workspace_roots) ? (payload.workspace_roots as unknown[]).filter((r): r is string => typeof r === 'string') : [];
    const cwd = typeof payload.cwd === 'string' && payload.cwd ? payload.cwd : (workspaceRoots[0] || '');
    const sessionId = String(payload.conversation_id ?? payload.session_id ?? '');

    if (isDuplicate(command, sessionId)) {
      log('bashGuard skip (dedup): ' + command.slice(0, 80));
      finishAllow();
    }

    const transcriptPath = resolveTranscriptPath(payload);
    const rawModel = String(payload.model ?? payload.model_id ?? '');
    const model = rawModel ? (rawModel.startsWith('cursor/') ? rawModel : 'cursor/' + rawModel) : 'cursor';
    const repo = detectRepo(cwd, transcriptPath, command, workspaceRoots);

    // No synkro.toml at the resolved repo root → Synkro is dormant here; allow
    // without grading. Keyed off the validated detectRepo() root, not raw cwd.
    if (!repo || !synkroFilePresent(repo)) finishAllow();

    const cmdShort = command.slice(0, 80);
    log('bashGuard checking: ' + cmdShort);

    // Instant-allow read-only tool calls + safe in-repo bash reads — no grade,
    // no network. Critical under Cursor's tight 15s beforeShellExecution budget.
    // ─── Secret firewall: BLOCK a literal credential in the command BEFORE it runs
    // or reaches the grader — never leaks to a file, shell history, or the model
    // (Anthropic/Cursor). Local + deterministic; $VAR / placeholder refs never trip
    // it. MUST run BEFORE the safe-read fast-path below — else an echo/printf of a
    // literal secret is waved through as a "safe read". ───
    const secretHits = detectHardSecrets(command);
    if (secretHits.length) {
      const types = secretHits.map(h => h.type).join(', ');
      const remediation = secretRemediation(command, secretHits);
      log('bashGuard ' + cmdShort + ' → BLOCKED: literal credential (' + types + ')');
      appendLocalTelemetry({
        capture_type: 'local_verdict', verdict: 'block', hook_type: 'bash',
        category: 'secret_exposure', tool_name: toolName, command: scrubSecrets(command).slice(0, 200),
        session_id: sessionId, repo: repo || cwd, cc_model: model,
        reasoning: 'Literal credential (' + types + ') in the proposed command — blocked locally before grading; never sent to the model.',
      });
      finishWith({ permission: 'deny', user_message: '[synkro] bashGuard → BLOCKED: literal credential in command (' + types + '). Use an env var reference instead.', agent_message: remediation });
    }

    if (isSafeInRepoRead(toolName, command, cwd)) {
      log('bashGuard ' + cmdShort + ' → instant allow (safe in-repo read)');
      appendLocalTelemetry({
        capture_type: 'local_verdict', verdict: 'pass', hook_type: 'bash',
        category: 'safe_read', tool_name: toolName, command: command.slice(0, 200),
        session_id: sessionId, repo: repo || cwd,
        cc_model: model,
        reasoning: 'Safe in-repo read — auto-allowed without an LLM grade.',
      });
      finishAllow();
    }

    let jwt = loadJwt();
    if (!jwt) finishAllow();
    jwt = await ensureFreshJwt(jwt);

    const transcript = extractTranscript(transcriptPath);
    const lastPrompt = readLastPrompt(sessionId);

    const config = await loadConfig(jwt);
    if (config.silent) finishAllow();

    const synkroFile = loadSynkroFile(cwd);
    const graderPool = effectiveGraderPool(synkroFile, 'cursor', config);
    const rt = await route(config, synkroFile);
    const tagStr = tag(rt, config, graderPool);

    // Install protection — install-scan runs first and owns block traces.
    let scanConcern = '';
    let scanBlockContext = '';
    if (SHELL_TOOL_NAMES.has(toolName)) {
      const scan = readCachedScan(command);
      if (scan?.action === 'block') {
        log('bashGuard deferring to install-scan block: ' + cmdShort);
        finishAllow();
      }
      if (scan?.action === 'warn') {
        scanBlockContext = scan.blockContext || scan.summary || '';
        scanConcern = 'PACKAGE SCANNER WARNING: ' + scanBlockContext
          + ' Mention this to the user before proceeding.';
      }
    }

    if (rt === 'local') {
      const sessionLog = await sessionLogForGrade(sessionId, config.rules);
      const relevantRules = await filterRules(
        ruleFilterText(command, transcript.userIntent || lastPrompt),
        config.rules,
      );

      const graderPrompt = [
        'Working directory: ' + (cwd || '.'),
        'Repo: ' + (repo || 'unknown'),
        sessionLog,
        'Command: ' + command,
        'User intent (last human message): ' + (transcript.userIntent || lastPrompt || 'none stated'),
        'Last user prompt: ' + (lastPrompt || 'none'),
        'Org rules: ' + JSON.stringify(relevantRules),
        scanConcern,
      ].filter(Boolean).join('\\n');

      let gradeResp: string;
      try {
        gradeResp = await localGrade('bash', graderPrompt, CURSOR_GRADE_TIMEOUT_MS, graderPool);
      } catch (e) {
        logGraderUnavailable('bashGuard', command.slice(0, 200), (e as Error).message || String(e), graderPrompt, { sessionId, repo });
        if (scanConcern) {
          // Grader unavailable to run the consent check — fail closed on a
          // scanner-flagged install (ask-mode so the user can still consent).
          finishWith({
            permission: 'deny',
            user_message: tagStr + ' installScan → blocked (grader unavailable): ' + cmdShort,
            agent_message: 'Synkro flagged this install: ' + scanBlockContext + ' The grader is unavailable to check consent — ask the user for explicit consent, then retry.',
          });
        }
        const errMsg = (e as Error).message || String(e);
        log('bashGuard ' + cmdShort + ' → pass (grade unavailable): ' + errMsg);
        finishWith({ permission: 'allow', user_message: tagStr + ' ' + graderUnavailableMessage('bashGuard', cmdShort, errMsg, graderPool) });
      }

      const verdict = parseVerdict(gradeResp);

      if (!verdict.ok) {
        const mode = normalizeMode(verdict.ruleMode || ruleMode(verdict.ruleId, config.rules));
        const guardReason = (verdict.ruleId ? '(' + verdict.ruleId + ') ' : '') + (verdict.reason || 'policy violation');

        const agentMsg = mode === 'fix'
          ? 'Synkro safety judge. Fix this before retrying — do not ask the user. Reasoning: ' + (verdict.reason || guardReason)
          : 'Synkro safety judge. Ask the user for explicit consent before retrying. Reasoning: ' + (verdict.reason || guardReason);
        dispatchCapture(jwt, 'bash', 'block', verdict.severity || 'critical', verdict.category || 'security',
          'Bash', repo, sessionId, config.captureDepth, {
            command, reasoning: guardReason,
            rulesChecked: relevantRules, violatedRules: verdict.ruleId ? [verdict.ruleId] : [],
            ccModel: model,
          });
        finishWith({
          permission: 'deny',
          user_message: tagStr + ' bashGuard → block: ' + guardReason,
          agent_message: agentMsg,
        });
      } else {
        dispatchCapture(jwt, 'bash', 'pass', 'clean', verdict.category || 'clean',
          'Bash', repo, sessionId, config.captureDepth, {
            command, reasoning: verdict.reason || 'no policy violations detected',
            rulesChecked: relevantRules, violatedRules: [],
            ccModel: model,
          });
      }

      const passReason = verdict.reason || 'no policy violations detected';
      log('bashGuard ' + cmdShort + ' → pass: ' + passReason);
      finishWith({ permission: 'allow' });
    }

    if (scanConcern) {
      // Cloud grading does not carry the package-scanner concern through the
      // consent flow — block here with ask-mode messaging so the user can
      // consent and retry.
      finishWith({
        permission: 'deny',
        user_message: tagStr + ' installScan → blocked: ' + cmdShort,
        agent_message: 'Synkro flagged this install: ' + scanBlockContext + ' Ask the user for explicit consent before retrying.',
      });
    }

    const body: Record<string, any> = {
      hook_event: 'PreToolUse',
      tool_name: toolName || 'Bash',
      tool_input: { command },
      response_format: 'cursor',
      user_intent: transcript.userIntent || null,
      last_user_message: lastPrompt || null,
      recent_user_messages: transcript.recentUserMessages,
      recent_messages: transcript.recentMessages,
      session_id: sessionId || null,
      cwd: cwd || null,
      repo: repo || null,
    };

    const resp = await postWithRetry(GATEWAY_URL + '/api/v1/hook/judge', body, jwt, CURSOR_CLOUD_TIMEOUT_MS);

    if (!resp) {
      log('bashGuard ' + cmdShort + ' → pass (cloud timeout)');
      finishAllow();
    }

    if (resp.hook_response) {
      const hr = resp.hook_response as Record<string, unknown>;
      if (hr.permission === 'allow') {
        const um = String(hr.user_message || '');
        const am = String(hr.agent_message || um);
        if (um || am) {
          finishWith({ permission: 'allow' });
        }
      }
      finishWith(hr);
    }
    log('bashGuard ' + cmdShort + ' → pass (no hook_response)');
    finishAllow();
  } catch (e) {
    log('bashGuard error: ' + String(e));
    finishAllow();
  }
}

main().catch((e) => {
  log('bashGuard fatal: ' + String(e));
  finishAllow();
});`;

export const CURSOR_EDIT_CAPTURE_TS = `#!/usr/bin/env bun
import {
  loadJwt, ensureFreshJwt, detectRepo, readStdin, resolveTranscriptPath,
  appendSessionAction, appendLocalTelemetry, shipCloud, log, GATEWAY_URL,
  countEditLineDelta, dispatchCapture, hookSessionId, cursorModelFromPayload,
  isLocalStorageMode, synkroFilePresent, isPathUnder,
} from './_synkro-common.ts';
import { existsSync, readFileSync } from 'node:fs';
import { basename, dirname, join } from 'node:path';

let hookDone = false;

function finish(): never {
  if (!hookDone) {
    hookDone = true;
    try { process.stdout.write('{}\\n'); } catch {}
  }
  process.exit(0);
}

process.on('SIGTERM', () => finish());

async function main() {
  try {
    const input = await readStdin();
    if (!input.trim()) finish();

    const payload = JSON.parse(input) as Record<string, unknown>;
    const filePath = String(payload.file_path || payload.path || payload.target_file || '');
    if (!filePath) finish();

    // No synkro.toml at the resolved repo root → Synkro is dormant here; skip capture.
    const _root = detectRepo((typeof payload.cwd === 'string' && payload.cwd) || dirname(filePath), '', filePath, []);
    if (!_root || !isPathUnder(filePath, _root) || !synkroFilePresent(_root)) finish();

    const workspaceRoots = Array.isArray(payload.workspace_roots)
      ? (payload.workspace_roots as unknown[]).filter((r): r is string => typeof r === 'string')
      : [];
    const cwd = (typeof payload.cwd === 'string' && payload.cwd) || workspaceRoots[0] || '';
    const transcriptPath = resolveTranscriptPath(payload);
    const sessionId = hookSessionId(payload);
    const repo = detectRepo(cwd, transcriptPath, filePath, workspaceRoots);
    const model = cursorModelFromPayload(payload);

    const edits = Array.isArray(payload.edits) ? payload.edits as Array<{ old_string?: string; new_string?: string }> : [];
    const { linesAdded, linesRemoved } = countEditLineDelta({ edits });

    log('editScan ' + basename(filePath) + ' +' + linesAdded + '/-' + linesRemoved);

    appendSessionAction(sessionId, {
      ts: new Date().toISOString(),
      tool: 'Edit',
      summary: 'wrote ' + basename(filePath) + (linesAdded || linesRemoved ? (' (+' + linesAdded + '/-' + linesRemoved + ')') : ''),
      file: filePath,
      outcome: 'ok',
    });

    let jwt = loadJwt();
    if (!jwt) finish();
    jwt = await ensureFreshJwt(jwt);

    let dependencies: Record<string, string> = {};
    const fullPath = filePath.startsWith('/') ? filePath : (cwd ? join(cwd, filePath) : filePath);
    let pkgDir = cwd || dirname(fullPath);
    while (pkgDir !== '/' && pkgDir !== '.') {
      const pkgPath = join(pkgDir, 'package.json');
      if (existsSync(pkgPath)) {
        try {
          const pkg = JSON.parse(readFileSync(pkgPath, 'utf-8'));
          dependencies = { ...(pkg.dependencies || {}), ...(pkg.devDependencies || {}) };
        } catch {}
        break;
      }
      const parent = dirname(pkgDir);
      if (parent === pkgDir) break;
      pkgDir = parent;
    }

    const localSpoolBody: Record<string, any> = {
      capture_type: 'edit_scan',
      tool_input: { file_path: filePath, lines_added: linesAdded, lines_removed: linesRemoved },
      edit_verdict: { ok: true },
      dependencies,
      cc_model: model,
      model,
    };
    if (sessionId) localSpoolBody.session_id = sessionId;
    if (cwd) localSpoolBody.cwd = cwd;
    if (repo) localSpoolBody.repo = repo;
    appendLocalTelemetry(localSpoolBody);

    if (!isLocalStorageMode()) {
      let fileContent = '';
      try {
        if (existsSync(fullPath)) {
          fileContent = readFileSync(fullPath).slice(0, 50000).toString('utf-8');
        }
      } catch {}
      const cloudBody: Record<string, any> = {
        capture_type: 'edit_scan',
        tool_input: { file_path: filePath, content: fileContent },
        edit_verdict: { ok: true },
        dependencies,
        cc_model: model,
        model,
      };
      if (sessionId) cloudBody.session_id = sessionId;
      if (cwd) cloudBody.cwd = cwd;
      if (repo) cloudBody.repo = repo;
      shipCloud(jwt, '/api/v1/hook/capture', cloudBody, 10000);
    }

    if (sessionId) {
      dispatchCapture(jwt, 'edit', 'pass', 'clean', 'trivial_edit', 'Edit', repo, sessionId, 'full', {
        command: 'file=' + filePath,
        reasoning: 'Cursor afterFileEdit',
        ccModel: model,
        linesAdded,
        linesRemoved,
      });
    }

    finish();
  } catch (e) {
    log('editScan error: ' + String(e));
    finish();
  }
}

main().catch((e) => {
  log('editScan fatal: ' + String(e));
  finish();
});`;

export const CURSOR_AGENT_CAPTURE_TS = `#!/usr/bin/env bun
/** Capture Cursor agent thinking/response text before transcript JSONL redacts it. */
import {
  readStdin, outputEmpty, setupCursorHookSignals, hookSessionId, detectRepo,
  appendThoughtOverlay, pushConversationMessage, synkroFilePresent,
} from './_synkro-common.ts';

async function main() {
  setupCursorHookSignals();
  try {
    const input = await readStdin();
    if (!input.trim()) { outputEmpty(); return; }
    const payload = JSON.parse(input) as Record<string, unknown>;
    const event = String(payload.hook_event_name || '');
    const text = typeof payload.text === 'string' ? payload.text.trim() : '';
    if (!text || text === '[REDACTED]') { outputEmpty(); return; }

    const sessionId = hookSessionId(payload);
    if (!sessionId) { outputEmpty(); return; }

    const workspaceRoots = Array.isArray(payload.workspace_roots)
      ? payload.workspace_roots.filter((r): r is string => typeof r === 'string')
      : [];
    const cwd = (typeof payload.cwd === 'string' && payload.cwd) || workspaceRoots[0] || '';
    if (!synkroFilePresent(cwd)) { outputEmpty(); return; }
    const gitRepo = detectRepo(cwd, '', '', workspaceRoots);

    if (event === 'afterAgentThought') {
      appendThoughtOverlay(sessionId, text);
      await pushConversationMessage(sessionId, 'assistant', text, { gitRepo, patchRedacted: true });
    } else if (event === 'afterAgentResponse') {
      await pushConversationMessage(sessionId, 'assistant', text, { gitRepo, patchRedacted: false });
    }

    outputEmpty();
  } catch {
    outputEmpty();
  }
}

main();
`;


// ─────────────────────────────────────────────────────────────────────────────
// STUB HOOKS (`synkro install --mode stub`)
//
// In stub mode every hook becomes ~3 lines that delegate to _synkro-stub-common.ts.
// The common file is pure PLUMBING — no scanning logic, no regexes, no verdict
// parsing. It reads the host-only inputs the container can't see (target file
// bytes, transcript tail, the repo's .synkro), POSTs a fixed envelope to the
// local server's /api/scan/{surface}, and prints the reply. All the brains live
// in the container (cli/scanning/*), so logic changes ship via a GHCR push with
// no CLI re-install. Authored with String.raw + string concatenation (no ${}, no
// inner backticks, no regex) so escapes survive materialization verbatim.
// ─────────────────────────────────────────────────────────────────────────────

export const STUB_COMMON_TS = String.raw`import { existsSync, readFileSync, mkdirSync, writeFileSync } from 'node:fs';
import { execSync } from 'node:child_process';
import { homedir } from 'node:os';
import { join } from 'node:path';

const HOME = homedir();
const PORT = process.env.SYNKRO_MCP_PORT || '18931';

function loadMcpJwt(): string {
  try { return readFileSync(join(HOME, '.synkro', '.mcp-jwt'), 'utf-8').trim(); } catch { return ''; }
}

let _cfgCache: Record<string, string> | null = null;
function cfgVal(key: string): string {
  if (process.env[key]) return process.env[key] as string;
  if (!_cfgCache) {
    _cfgCache = {};
    try {
      const txt = readFileSync(join(HOME, '.synkro', 'config.env'), 'utf-8');
      for (const line of txt.split('\n')) {
        const mm = line.match(/^([A-Z0-9_]+)=['"]?(.*?)['"]?\s*$/);
        if (mm) _cfgCache[mm[1]] = mm[2];
      }
    } catch {}
  }
  return _cfgCache[key] || '';
}
function deployIsCloud(): boolean { return cfgVal('SYNKRO_DEPLOY_LOCATION') === 'cloud'; }

// Cloud MCP gate: in cloud mode the local server (:18931) isn't running, so the
// gate asks the org policy directly (api /api/mcp/gate, authed by .mcp-jwt).
// Fail-open by contract — any error or non-block verdict allows the call.
async function cloudMcpGate(payload: any, harness: string): Promise<string> {
  try {
    const toolName = String(payload.tool_name || payload.tool || '');
    const parts = toolName.split('__');
    const server = (parts.length >= 3 && parts[0] === 'mcp') ? parts[1] : '';
    if (!server) return failOpen(harness);
    const base = (cfgVal('SYNKRO_GATEWAY_URL') || 'https://api.synkro.sh').replace(/\/+$/, '');
    const resp = await fetch(base + '/api/mcp/gate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + loadMcpJwt() },
      body: JSON.stringify({ server: server, tool: parts.slice(2).join('__') }),
      signal: AbortSignal.timeout(5000),
    });
    if (!resp.ok) return failOpen(harness);
    const d = await resp.json() as any;
    if (d && d.decision === 'block') {
      const reason = String(d.reason || ('MCP server "' + server + '" blocked by policy'));
      if (harness === 'cursor') return JSON.stringify({ permission: 'deny', user_message: reason });
      return JSON.stringify({ systemMessage: '[synkro:cloud:mcp] ' + reason, hookSpecificOutput: { hookEventName: 'PreToolUse', permissionDecision: 'deny', permissionDecisionReason: reason, additionalContext: reason } });
    }
    return failOpen(harness);
  } catch { return failOpen(harness); }
}

async function readStdin(): Promise<string> {
  const chunks: Buffer[] = [];
  for await (const chunk of process.stdin) chunks.push(Buffer.isBuffer(chunk) ? chunk : Buffer.from(chunk));
  return Buffer.concat(chunks).toString('utf-8');
}

function isCursor(harnessOpt?: string): boolean {
  return harnessOpt === 'cursor' || process.argv.includes('--cursor') || process.env.SYNKRO_HOOK_FORMAT === 'cursor';
}

function failOpen(harness: string): string {
  return harness === 'cursor' ? '{"permission":"allow"}' : '{}';
}

function out(s: string): void { try { process.stdout.write(s + '\n'); } catch {} }

function gitRoot(cwd: string): string {
  if (!cwd) return '';
  try {
    return execSync('git rev-parse --show-toplevel', { cwd, timeout: 2000, encoding: 'utf-8', stdio: ['pipe', 'pipe', 'pipe'] }).trim();
  } catch { return ''; }
}

// Once-per-session onboarding hint for repos with no synkro.toml file.
function noSynkroHint(sessionId: string): string | null {
  const dir = join(HOME, '.synkro', '.no-synkro-hint');
  try {
    mkdirSync(dir, { recursive: true });
    const key = (sessionId || 'nosession').replace(/[^a-zA-Z0-9_-]/g, '_');
    const marker = join(dir, key);
    if (existsSync(marker)) return null;
    writeFileSync(marker, '', { flag: 'w' });
  } catch {}
  return '[synkro] No synkro.toml config in this repo — grading skipped. Run synkro install here to enable Synkro.';
}

function filePathFromToolInput(ti: any): string {
  if (!ti || typeof ti !== 'object') return '';
  return ti.file_path || ti.notebook_path || ti.path || ti.target_file || '';
}

interface StubOpts {
  needsFile?: boolean;
  needsTranscript?: boolean;
  // Ship the WHOLE transcript (not just the 200KB tail). Sync surfaces need it
  // so the server can compute stable absolute message indices + total usage.
  fullTranscript?: boolean;
  needsPlan?: boolean;
  telemetry?: boolean;
  harness?: string;
}

export async function runStub(surface: string, opts: StubOpts = {}): Promise<void> {
  const harness = isCursor(opts.harness) ? 'cursor' : 'cc';
  try {
    const input = await readStdin();
    if (!input.trim()) { out(failOpen(harness)); return; }
    const payload = JSON.parse(input);

    // Cloud MCP enforcement: the gate can't reach the local server, so decide
    // against the org policy directly. Fail-open inside cloudMcpGate.
    if (surface === 'mcp-gate' && deployIsCloud()) { out(await cloudMcpGate(payload, harness)); return; }

    const workspaceRoots = Array.isArray(payload.workspace_roots) ? payload.workspace_roots : [];
    const cwd = (typeof payload.cwd === 'string' && payload.cwd) || workspaceRoots[0] || '';
    const sessionId = String(payload.session_id || payload.conversation_id || '');
    const root = gitRoot(cwd);

    // Dormancy: a repo is onboarded only if it has a synkro.toml FILE at its git
    // root. Guard root !== HOME — ~/.synkro is the config DIRECTORY, so without
    // this a home-rooted cwd (dotfiles in git, non-git dir under home) could
    // look onboarded.
    let synkroFileText = '';
    if (root && root !== HOME && existsSync(join(root, 'synkro.toml'))) {
      try { synkroFileText = readFileSync(join(root, 'synkro.toml'), 'utf-8'); } catch {}
    }
    if (!synkroFileText) {
      if (opts.telemetry) { out(failOpen(harness)); return; }
      const hint = noSynkroHint(sessionId);
      if (!hint) { out(failOpen(harness)); return; }
      if (harness === 'cursor') out(JSON.stringify({ permission: 'allow', user_message: hint }));
      else out(JSON.stringify({ systemMessage: hint, hookSpecificOutput: { hookEventName: 'PreToolUse', additionalContext: hint } }));
      return;
    }

    // Gather host-only inputs the container can't read.
    const envelope: any = { payload, harness, cwd: root || cwd, sessionId, synkroFileText };

    if (opts.needsFile) {
      const fp = filePathFromToolInput(payload.tool_input || {});
      if (fp && existsSync(fp)) {
        try { envelope.baseContent = readFileSync(fp, 'utf-8').slice(0, 65536); } catch {}
      }
    }
    if (opts.needsTranscript) {
      const tp = typeof payload.transcript_path === 'string' ? payload.transcript_path : '';
      if (tp && existsSync(tp)) {
        try {
          const t = readFileSync(tp, 'utf-8');
          envelope.transcript = (opts.fullTranscript || t.length <= 200000) ? t : t.slice(t.length - 200000);
        } catch {}
      }
    }
    if (opts.needsPlan) {
      const ti = payload.tool_input || {};
      envelope.planText = String(ti.plan || ti.content || payload.plan || '');
    }

    // bash-followup is telemetry, but it can carry the INLINE "task complete ✓ N/N"
    // result (the server waits up to 30s for the completion grade), so it needs a
    // wait above that budget — not the 6s telemetry default — or the stub abandons
    // the request before the result lands and the completion shows up a hook late.
    const timeoutMs = surface === 'bash-followup' ? 32000 : (opts.telemetry ? 6000 : 28000);
    const url = 'http://127.0.0.1:' + PORT + '/api/scan/' + surface + (harness === 'cursor' ? '?harness=cursor' : '');
    const body = JSON.stringify(envelope);
    const headers = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + loadMcpJwt() };
    // Telemetry hooks (bash-followup, transcript-sync, session telemetry) IGNORE the
    // response and the server processes them DETACHED, so delivery RELIABILITY beats
    // latency: retry a few times with backoff. Right after a server restart the
    // event loop can be briefly busy / not-yet-listening and a single POST silently
    // drops the event (this was the followup flakiness). Double-delivery is safe here
    // — the server side is idempotent-ish (completion in-flight guard, dup evidence
    // is harmless). Blocking hooks make ONE attempt: their response IS the verdict
    // and a retry would double-grade. Normal case: first attempt wins, no delay.
    const attempts = opts.telemetry ? 3 : 1;
    let text = '';
    for (let i = 0; i < attempts; i++) {
      try {
        const resp = await fetch(url, { method: 'POST', headers, body, signal: AbortSignal.timeout(timeoutMs) });
        if (resp.ok) { text = (await resp.text()).trim(); break; }
      } catch (e) { /* connection refused / timeout → retry below */ }
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, 500 * (i + 1)));
    }
    out(text || failOpen(harness));
  } catch {
    out(failOpen(harness));
  }
}
`;

function stubHook(surface: string, optsLiteral: string): string {
  return '#!/usr/bin/env bun\n' +
    "import { runStub } from './_synkro-stub-common.ts';\n" +
    'runStub(' + JSON.stringify(surface) + ', ' + optsLiteral + ');\n';
}

// edit/bash/cwe/cve/install ship the transcript tail too: the server reads the
// model (last assistant message) + user intent / consent history from it — the
// same context the old fat hooks extracted. Stub code stays 3 lines; this is
// just a flag telling the plumbing common to attach the tail.
export const STUB_EDIT_PRECHECK_TS = stubHook('edit-precheck', '{ needsFile: true, needsTranscript: true }');
export const STUB_CWE_PRECHECK_TS = stubHook('cwe-precheck', '{ needsFile: true, needsTranscript: true }');
export const STUB_CVE_PRECHECK_TS = stubHook('cve-precheck', '{ needsFile: true, needsTranscript: true }');
export const STUB_BASH_JUDGE_TS = stubHook('bash-judge', '{ needsTranscript: true }');
export const STUB_INSTALL_SCAN_TS = stubHook('install-scan', '{ needsTranscript: true }');
export const STUB_AGENT_JUDGE_TS = stubHook('agent-judge', '{ needsTranscript: true }');
// PreToolUse on mcp__* tools → MCP server governance (allow/blocklist + grade).
// Blocking, like the bash judge: the server's deny response stops the call.
export const STUB_MCP_GATE_TS = stubHook('mcp-gate', '{ needsTranscript: true }');
export const STUB_PLAN_JUDGE_TS = stubHook('plan-judge', '{ needsPlan: true }');
// NOT telemetry: the Stop hook also runs the task-adherence completion gate,
// which needs the full grading timeout and may return {decision:"block"} (passed
// through verbatim to CC). When no task is active the handler returns instantly,
// so stop stays fast in the common case.
export const STUB_STOP_SUMMARY_TS = stubHook('stop-summary', '{ needsTranscript: true, fullTranscript: true }');
export const STUB_SESSION_START_TS = stubHook('session-start', '{ telemetry: true }');
export const STUB_TRANSCRIPT_SYNC_TS = stubHook('transcript-sync', '{ needsTranscript: true, fullTranscript: true, telemetry: true }');
export const STUB_USER_PROMPT_SUBMIT_TS = stubHook('prompt-submit', '{ telemetry: true }');
export const STUB_BASH_FOLLOWUP_TS = stubHook('bash-followup', '{ telemetry: true }');

// PreToolUse hook on the activate_standard MCP tool. Fires IN the activating
// session (carrying its real session_id) just BEFORE the tool runs, and stamps
// the activation intent on the server so the task binds to THIS exact session —
// no most-recent-session race. Self-contained (doesn't need _synkro-stub-common);
// always allow ('{}'), never blocks the tool.
export const STUB_TASK_ACTIVATE_INTENT_TS = `#!/usr/bin/env bun
import { readFileSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';
const PORT = process.env.SYNKRO_MCP_PORT || '18931';
function mcpJwt(): string { try { return readFileSync(join(homedir(), '.synkro', '.mcp-jwt'), 'utf-8').trim(); } catch { return ''; } }
const chunks: Buffer[] = [];
for await (const chunk of process.stdin) chunks.push(Buffer.isBuffer(chunk) ? chunk : Buffer.from(chunk));
try {
  const payload = JSON.parse(Buffer.concat(chunks).toString('utf-8') || '{}');
  await fetch('http://127.0.0.1:' + PORT + '/api/local/task-activate-intent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + mcpJwt() },
    body: JSON.stringify({ session_id: payload.session_id, tool_input: payload.tool_input }),
    signal: AbortSignal.timeout(5000),
  });
} catch {}
process.stdout.write('{}\\n');
`;
export const STUB_CURSOR_BASH_JUDGE_TS = stubHook('bash-judge', "{ needsTranscript: true, harness: 'cursor' }");
export const STUB_CURSOR_EDIT_CAPTURE_TS = stubHook('edit-precheck', "{ needsFile: true, needsTranscript: true, harness: 'cursor' }");
export const STUB_CURSOR_AGENT_CAPTURE_TS = stubHook('agent-judge', "{ needsTranscript: true, harness: 'cursor' }");
